import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { showToast } from 'Core/redux/toast/slice';
import { TextEditor } from 'Core/components/editor';
import { format, parse } from 'date-fns';
import { MOLD_MAINTENANCE } from '../../core/constants/moldConstants';
import { SimpleFileUpload, DocumentViewerModal } from '../../core/components';
import { moldDocuments } from '../../core/prop-types/moldDocuments';
import { PartService } from 'Service/api/part-service';
import { MoldService } from 'Service/api/mold-service';
import { mapMaintenanceRecordSubmittedByFromApi } from './map-maintenance-api-fields';
import { FaPencilAlt, FaEye } from 'react-icons/fa';
import { Eye } from 'react-feather';
import SegmentWebcamFileUpload from '../../core/components/webcamfileupload/segment-webcam-file-upload';
import { Modal, Button } from 'react-bootstrap';

const normalizePartsForSelect = parts => {
  if (!Array.isArray(parts)) {
    return [];
  }

  return parts
    .filter(part => !!part)
    .map(part => {
      const partId =
        part.partId ??
        part.PartId ??
        part.partID ??
        part.id ??
        part.value ??
        null;
      const partNumber =
        part.partNumber ??
        part.PartNumber ??
        part.label ??
        part.partname ??
        part.PartName ??
        '';

      return {
        value: partId,
        label: partNumber,
        partId,
        partNumber,
      };
    });
};

const initData = {
  id: 0,
  toolId: '',
  moldInfoId: 0,
  moldNumber: '',
  moldDescription: '',
  partID: 0,
  materialType: '',
  cavity: '',
  totalCyclesSinceLastMaint: '0',
  totalCyclesToDate: '',
  lastMaintenanceDate: '',
  maintenanceIntervalCycles: '0',
  currentMaintenanceDate: '',
  currentMaintenanceHours: '0',
  totalMaintenanceHours: '0',
  originalTotalMaintenanceHours: '0', // Store original total from API
  cleaning: false,
  spray: false,
  comments1: '',
  inspection: false,
  comments2: '',
  lubrication: false,
  comments3: '',
  componentReplacement: false,
  componentsReplaced: false,
  comments4: '',
  repair: false,
  detailsofRepair: '',
  functionalTest: false,
  comments5: '',
  maintenance_Report_Document: '',
  inspection_Checklist_Document: '',
  functionalTest_Results_Document: '',
  moldPicture_Before_Maintenance: '',
  moldPicture_After_Maintenance: '',
  maintenanceTypePictures: '',
  repairPictures: '',
  additionalPicPath1: null,
  additionalPicPath2: null,
  additionalPicPath3: null,
  additionalPicPath4: null,
  additionalPicPath: null,
  
  // File upload fields for maintenance records
  moldPicture_Before_Maintenance_File: null,
  moldPicture_After_Maintenance_File: null,
  additionalPicPath1_File: null,
  additionalPicPath2_File: null,
  additionalPicPath3_File: null,
  additionalPicPath4_File: null,
  additionalPicPath_File: null,
  
  // New fields from API response
  customerId: null,
  location: null,
  notes: null,
  moldWidth: null,
  moldLength: null,
  moldHeight: null,
  maintenanceID: null,
  additionalComments: '',
  maintenance_PerformedBy: '',
  quality_Control_ApprovedBy: '',
  engineering_ApprovedBy: '',
  submittedBy: '',
  maintenance_PerformedBy_Date: '',
  quality_Control_ApprovedBy_Date: '',
  engineering_ApprovedBy_Date: '',
  version: null,
  maintenanceType: '',
  submittedByDate: '',
  uploadedImages: [],
  imageSegments: [],
  activities: [],
  activityIssues: [],

  additionalLabel: 'Additional Pictures',
  maintenanceFrequency: '', // No default selection
  maintenanceChecklist: null, // Store checklist data
  maintenanceIssues: [], // Store issues found
  maintenanceTypes: [], // Store multiple maintenance types
  parts: [], // Parts list for multi-select
};

const defaultFields = {
  cleaning: false,
  spray: false,
  inspection: false,
  lubrication: false,
  componentReplacement: false,
  componentsReplaced: false,
  repair: false,
  functionalTest: false,
  totalCyclesSinceLastMaint: '0',
  maintenanceIntervalCycles: '0',
};

const EditableLabel = ({ defaultText, onChange, isDisabled }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(defaultText || 'Additional Pictures');
  const [isHovered, setIsHovered] = useState(false);

  const handleBlur = () => {
    setIsEditing(false);
    if (onChange) {
      onChange(text);
    }
  };

  return (
    <div className="d-flex align-items-center">
      {isEditing ? (
        <input
          type="text"
          className="form-control"
          value={text}
          onChange={e => setText(e.target.value)}
          onBlur={handleBlur}
          autoFocus
        />
      ) : (
        <label className="mb-0">
          {text || 'Additional Pictures'}
          {!isDisabled && (
            <span
              style={{
                marginLeft: '6px',
                fontSize: '12px',
                color: '#888',
                cursor: 'pointer',
              }}
              onClick={() => setIsEditing(true)}
            >
              (Edit)
            </span>
          )}
        </label>
      )}
    </div>
  );
};

const MoldMaintenanceAddEditForm = ({ data, onChanges, onFileUpload, id, isDisabled, onMaintenanceSubmit, onImageSegmentUploaded, filterDataForAPI, dataToSave }) => {
  const dispatch = useDispatch();
  
  const normalizeRecordName = (name = '') => name.trim().toLowerCase();
  const MAINTENANCE_RECORD_NAMES = [
    'Mold Picture Before Maintenance',
    'Mold Picture After Maintenance',
    'Preventive Maintenance Pictures',
    'Breakdown Maintenance Pictures',
    'Repair Pictures',
    'Mold Modification Pictures'
  ];
  const OTHER_MAINTENANCE_RECORD_NAMES = [
    'Action Images',
    'Sub Action Images',
    'Capture Future Actions Images'
  ];
  const MAINTENANCE_RECORD_SET = new Set(MAINTENANCE_RECORD_NAMES.map(normalizeRecordName));
  const OTHER_MAINTENANCE_RECORD_SET = new Set(OTHER_MAINTENANCE_RECORD_NAMES.map(normalizeRecordName));
  const RECORD_FIELD_PAIRS = [
    ['Mold Picture Before Maintenance', 'MoldPicture_Before_Maintenance'],
    ['Mold Picture After Maintenance', 'MoldPicture_After_Maintenance'],
    ['Preventive Maintenance Pictures', 'AdditionalPicPath1'],
    ['Breakdown Maintenance Pictures', 'AdditionalPicPath2'],
    ['Repair Pictures', 'AdditionalPicPath3'],
    ['Mold Modification Pictures', 'AdditionalPicPath4'],
    ['Additional Pictures', 'AdditionalPicPath'],
    ['Extra Images', 'AdditionalPicPath'],
    ['Action Images', 'ActionImages'],
    ['Sub Action Images', 'SubActionImages'],
    ['Capture Future Actions Images', 'FutureActionsImages']
  ];

  // Function to process image segments from API response and map them to file upload components
  const processImageSegments = (imageSegments) => {
    if (!imageSegments || !Array.isArray(imageSegments)) {
      return {};
    }
    
    const mappedFiles = {};
    let additionalPicturesLabel = 'Additional Pictures';
    
    const uniqueRecords = [...new Set(imageSegments.map(segment => segment.record))];
    const unknownRecords = uniqueRecords.filter(record => !getFieldNameForRecord(record));
    if (unknownRecords.length > 0) {
      const unknownRecordWithImages = unknownRecords.find(record =>
        imageSegments.some(segment => segment.record === record)
      );
      if (unknownRecordWithImages) {
        additionalPicturesLabel = unknownRecordWithImages;
      }
    }
    
    if (uniqueRecords.some(record => normalizeRecordName(record) === normalizeRecordName('Extra Images')) &&
      imageSegments.some(segment => segment.record === 'Extra Images')) {
      additionalPicturesLabel = 'Extra Images';
    }
    
    imageSegments.forEach(segment => {
      const {
        record,
        imageUrl,
        segment: segmentName,
        id,
        category,
        actionImageId,
        subActionImageId
      } = segment;
      let fieldName = getFieldNameForRecord(record);
      const excludeFromAdditional = shouldExcludeFromAdditional(record, category);
      
      if (fieldName) {
        if (fieldName === 'AdditionalPicPath' && excludeFromAdditional) {
          return;
        }
      } else {
        if (excludeFromAdditional) {
          return;
        }
        fieldName = 'AdditionalPicPath';
      }
      
      if (!mappedFiles[fieldName]) {
        mappedFiles[fieldName] = [];
      }
      
      const fileObject = {
        id,
        name: `${segmentName}_${id}.jpg`,
        path: imageUrl,
        fileName: `${segmentName}_${id}.jpg`,
        filePath: imageUrl,
        record,
        category,
        actionImageId,
        subActionImageId,
        segment: segmentName,
        slotName: segmentName
      };
      mappedFiles[fieldName].push(fileObject);
    });
    
    if (mappedFiles['AdditionalPicPath'] && mappedFiles['AdditionalPicPath'].length > 0) {
      mappedFiles['additionalPicturesLabel'] = additionalPicturesLabel;
    }
    
    const subActionImages = imageSegments.filter(segment => normalizeRecordName(segment.record) === normalizeRecordName('Sub Action Images'));
    if (subActionImages.length > 0) {
      console.log('Processing Sub Action Images:', subActionImages);
      subActionImages.forEach(imageSegment => {
        if (imageSegment.actionImageId && imageSegment.subActionImageId) {
          const uniqueId = `subissue_${imageSegment.actionImageId}_${imageSegment.subActionImageId}`;
          setSubActionActivityIds(prev => ({
            ...prev,
            [uniqueId]: {
              activityId: imageSegment.actionImageId,
              subActivityId: imageSegment.subActionImageId,
              actionImageId: imageSegment.actionImageId,
              subActionImageId: imageSegment.subActionImageId,
              title: imageSegment.segment || '',
              comments: ''
            }
          }));
          console.log('Stored Sub Action Image IDs:', {
            uniqueId,
            actionImageId: imageSegment.actionImageId,
            subActionImageId: imageSegment.subActionImageId,
            segment: imageSegment.segment
          });
        }
      });
      mappedFiles.SubActionImages = subActionImages.map(segment => ({
        id: segment.id,
        name: segment.segment || 'Image',
        path: segment.imageUrl,
        isLocal: false,
        segment: segment.segment,
        title: segment.title || '',
        description: segment.description || '',
        actionImageId: segment.actionImageId,
        subActionImageId: segment.subActionImageId,
        record: segment.record,
        category: segment.category,
        slotName: segment.segment
      }));
    }
    
    return mappedFiles;
  };

  const buildDocumentFromSegment = (segment) => ({
    id: segment.id,
    name: segment.title || segment.segment || segment.record || `Image_${segment.id || ''}`,
    path: segment.imageUrl,
    fileName: segment.fileName || `${segment.segment || segment.record || 'Image'}_${segment.id || ''}`,
    filePath: segment.imageUrl,
    record: segment.record,
    category: segment.category,
    actionImageId: segment.actionImageId,
    subActionImageId: segment.subActionImageId,
    segment: segment.segment || segment.slotName,
    slotName: segment.slotName || segment.segment,
    isLocal: false
  });

  const getActionImagesForActivityIssue = (activityIssueId) => {
    if (!activityIssueId || activityIssueId <= 0) {
      return [];
    }

    const processedActionImages = (processedImageSegments?.ActionImages || []).filter(img => img.actionImageId === activityIssueId);
    if (processedActionImages.length > 0) {
      return processedActionImages;
    }

    if (Array.isArray(formData?.imageSegments)) {
      return formData.imageSegments
        .filter(segment => normalizeRecordName(segment.record) === normalizeRecordName('Action Images') && segment.actionImageId === activityIssueId)
        .map(buildDocumentFromSegment);
    }

    return [];
  };

  // Function to determine which maintenance buttons should be enabled based on maintenanceType
  const getMaintenanceButtonStates = (maintenanceType) => {
    const buttonStates = {
      cleaning: false,
      spray: false,
      inspection: false,
      lubrication: false,
      componentReplacement: false,
      componentsReplaced: false,
      repair: false,
      functionalTest: false
    };

    switch (maintenanceType) {
      case 'Preventive Maintenance':
        buttonStates.cleaning = true;
        buttonStates.spray = true;
        buttonStates.inspection = true;
        buttonStates.lubrication = true;
        buttonStates.componentReplacement = true;
        buttonStates.componentsReplaced = true;
        buttonStates.functionalTest = true;
        break;
      case 'Breakdown Maintenance':
        buttonStates.cleaning = true;
        buttonStates.inspection = true;
        buttonStates.repair = true;
        buttonStates.componentReplacement = true;
        buttonStates.componentsReplaced = true;
        buttonStates.functionalTest = true;
        break;
      case 'Mold Modificaion':
        buttonStates.cleaning = true;
        buttonStates.inspection = true;
        buttonStates.repair = true;
        buttonStates.componentReplacement = true;
        buttonStates.componentsReplaced = true;
        buttonStates.functionalTest = true;
        break;
      default:
        // For new records or unknown types, enable all buttons
        Object.keys(buttonStates).forEach(key => {
          buttonStates[key] = true;
        });
        break;
    }

    return buttonStates;
  };
  const [moldReceivingData, setMoldReceivingData] = useState(
    data?.moldReceivingData ? data.moldReceivingData : {},
  );

  // Set lastMaintenanceDate when moldReceivingData changes
  useEffect(() => {
    if (moldReceivingData?.lastMaintenanceDate) {
      // If there's a previous maintenance date from API, use it and make field read-only
      const convertedDate = convertISODateToFormDate(moldReceivingData.lastMaintenanceDate);
      setFormData(prevData => ({
        ...prevData,
        lastMaintenanceDate: convertedDate,
      }));
    } else if (!formData.lastMaintenanceDate) {
      // If no previous maintenance and field is empty, set to current date
      const today = format(new Date(), 'yyyy-MM-dd');
      setFormData(prevData => ({
        ...prevData,
        lastMaintenanceDate: today,
      }));
    }
  }, [moldReceivingData?.lastMaintenanceDate]);

  // Helper function to convert ISO date to YYYY-MM-DD format
  const convertISODateToFormDate = (isoDate) => {
    if (!isoDate) return '';
    try {
      // Handle both ISO format and already formatted dates
      if (isoDate.includes('T')) {
        return isoDate.split('T')[0]; // Extract YYYY-MM-DD from ISO format
      }
      return isoDate; // Already in correct format
    } catch (error) {
      return '';
    }
  };

  /** ISO / API value → yyyy-MM-ddTHH:mm:ss for datetime-local (step 1) */
  const convertISODateToFormDateTime = (isoDate) => {
    if (isoDate == null || isoDate === '') return '';
    const s = String(isoDate).trim();
    if (!s) return '';
    try {
      const d = new Date(s);
      if (!Number.isNaN(d.getTime())) {
        const pad = n => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      }
      if (s.length >= 19 && s[4] === '-' && s[7] === '-' && s[10] === 'T') {
        return s.slice(0, 19);
      }
      if (s.length >= 16 && s[4] === '-' && s[7] === '-' && s[10] === 'T') {
        return `${s.slice(0, 16)}:00`;
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
        return `${s}T00:00:00`;
      }
      return s;
    } catch {
      return '';
    }
  };

  const [formData, setFormData] = useState(() => {
    try {
      let initialFormData = data?.moldMaintenanceData ? { ...defaultFields, ...data?.moldMaintenanceData } : { ...initData };
      initialFormData = mapMaintenanceRecordSubmittedByFromApi(initialFormData);

      // Normalize parts from API response
      const normalizedParts = normalizePartsForSelect(
        initialFormData.parts || initialFormData.moldParts || initialFormData.selectedParts
      ) || [];
      initialFormData.parts = normalizedParts;
      
      // Set primary partID from parts if not already set
      if (!initialFormData.partID && normalizedParts.length > 0) {
        const firstWithId = normalizedParts.find(option => option.value);
        initialFormData.partID = firstWithId ? firstWithId.value : 0;
      }
      
      // Convert date fields from ISO format to form format
      if (initialFormData.lastMaintenanceDate) {
        initialFormData.lastMaintenanceDate = convertISODateToFormDate(initialFormData.lastMaintenanceDate);
      }
      if (initialFormData.currentMaintenanceDate) {
        initialFormData.currentMaintenanceDate = convertISODateToFormDate(initialFormData.currentMaintenanceDate);
      }
      if (initialFormData.maintenance_PerformedBy_Date) {
        initialFormData.maintenance_PerformedBy_Date = convertISODateToFormDateTime(
          initialFormData.maintenance_PerformedBy_Date
        );
      }
      if (initialFormData.quality_Control_ApprovedBy_Date) {
        initialFormData.quality_Control_ApprovedBy_Date = convertISODateToFormDateTime(
          initialFormData.quality_Control_ApprovedBy_Date
        );
      }
      if (initialFormData.engineering_ApprovedBy_Date) {
        initialFormData.engineering_ApprovedBy_Date = convertISODateToFormDateTime(
          initialFormData.engineering_ApprovedBy_Date
        );
      }
      if (initialFormData.submittedByDate) {
        initialFormData.submittedByDate = convertISODateToFormDateTime(initialFormData.submittedByDate);
      }
      
      // Ensure id is set to 0 for new records if not present
      if (initialFormData.id === undefined || initialFormData.id === null) {
        initialFormData.id = 0;
      }
      // Auto-populate current maintenance date with today's date if not already set
      if (!initialFormData.currentMaintenanceDate) {
        const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
        initialFormData.currentMaintenanceDate = today;
      }
      
      return initialFormData;
    } catch (error) {
      const defaultData = { ...initData };
      // Auto-populate current maintenance date with today's date
      const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
      defaultData.currentMaintenanceDate = today;
      return defaultData;
    }
  });
  
  const [partsList, setPartsList] = useState([]);
  const [processedImageSegments, setProcessedImageSegments] = useState({});
  const [showPartsModal, setShowPartsModal] = useState(false);

  // Modal state for maintenance checklist
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [selectedMaintenanceType, setSelectedMaintenanceType] = useState('');
  const [checklistResponses, setChecklistResponses] = useState({});
  const [shouldOpenCaptureModal, setShouldOpenCaptureModal] = useState(false);

  // Modal state for issues found
  const [showIssuesModal, setShowIssuesModal] = useState(false);
  const [issuesList, setIssuesList] = useState([{ 
    id: 1, 
    title: '', 
    comments: '', 
    subIssues: [],
    newFutureAction: '',
    futureActionsList: [],
    actionImages: [] // Add action images state
  }]);
  const [futureActionsData, setFutureActionsData] = useState([]);
  const [loadingFutureActions, setLoadingFutureActions] = useState(false);
  const [showFutureActionConfirm, setShowFutureActionConfirm] = useState(false);
  const [pendingFutureAction, setPendingFutureAction] = useState(null);
  const [savedActivityIssueId, setSavedActivityIssueId] = useState(null);
  const [issueActivityIds, setIssueActivityIds] = useState({}); // Track activityIssueId for each issue
  const [savedIssuesTracker, setSavedIssuesTracker] = useState({}); // Track which issues have been saved to avoid duplicate API calls
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [documentViewerUrl, setDocumentViewerUrl] = useState('');
  const [documentViewerName, setDocumentViewerName] = useState('');
  const [categories, setCategories] = useState([
    { categoryId: 1, category: 'Safety Issue' },
    { categoryId: 2, category: 'Quality Issue' },
    { categoryId: 3, category: 'Performance Issue' },
    { categoryId: 4, category: 'Maintenance Issue' },
    { categoryId: 5, category: 'Equipment Issue' },
    { categoryId: 6, category: 'Process Issue' },
    { categoryId: 7, category: 'Documentation Issue' },
    { categoryId: 8, category: 'Other' }
  ]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  
  // Function to get all categories
  const getAllCategories = async () => {
    try {
      setCategoriesLoading(true);
      // Using MoldService pattern for consistent API calls
      const response = await MoldService.getAllCategories();
      
      if (response && response.status === 200 && response.response) {
        setCategories(response.response);
      } else {
        // Keep the default categories that are already set
      }
    } catch (error) {
      // Keep the default categories that are already set
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Load categories when component mounts
  useEffect(() => {
    getAllCategories();
  }, []);

  // Debug: Log categories state changes
  useEffect(() => {
  }, [categories]);
  
  // Modal state for add issues
  const [showAddIssuesModal, setShowAddIssuesModal] = useState(false);
  const [newIssue, setNewIssue] = useState({ title: '', comments: '', actionImages: [] });
  const [parentIssueId, setParentIssueId] = useState(null); // Track which issue is adding sub-issues
  const isSavingNewIssueRef = useRef(false); // Prevent double execution of handleSaveNewIssue
  
  // State for editing sub-issues
  const [editingSubIssue, setEditingSubIssue] = useState(null); // Track which sub-issue is being edited
  const [editingSubIssueData, setEditingSubIssueData] = useState({ title: '', comments: '' }); // Store editing data
  
  // State for accordion/collapsible sub-issues
  const [expandedSubIssues, setExpandedSubIssues] = useState({}); // Track which sub-issues sections are expanded
  
  
  // State to track saved issues
  const [savedIssues, setSavedIssues] = useState(null); // Store activityIssueId and maintenanceId
  
  // State to track maintenance ID for API calls
  const [maintenanceId, setMaintenanceId] = useState(0);
  
  // State to track activities and activityIssues from API response
  const [loadedActivities, setLoadedActivities] = useState(null);
  const [loadedActivityIssues, setLoadedActivityIssues] = useState(null);


  // State to track if we're viewing saved records (read-only mode)
  const [isViewingSavedRecord, setIsViewingSavedRecord] = useState(false);

  // State for confirmation dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmCallback, setConfirmCallback] = useState(null);
  
  // State for save images confirmation
  const [showSaveImagesConfirm, setShowSaveImagesConfirm] = useState(false);
  const [pendingImages, setPendingImages] = useState(null);
  
  // State for Close Maintenance Record functionality
  const [isPageDisabled, setIsPageDisabled] = useState(false);

  // State to track if saveOrUpdateMoldMaintenanceActivity has been called
  const [maintenanceActivityCalled, setMaintenanceActivityCalled] = useState(false);
  const [savedMaintenanceActivityData, setSavedMaintenanceActivityData] = useState(null);
  
  // State to track activity IDs for each sub-action
  const [subActionActivityIds, setSubActionActivityIds] = useState({});
  
  // Function to get activity IDs for a specific sub-action
  const getSubActionActivityIds = (subIssueId) => {
    console.log('=== getSubActionActivityIds CALLED ===');
    console.log('subIssueId:', subIssueId);
    console.log('subActionActivityIds keys:', Object.keys(subActionActivityIds));
    console.log('subActionActivityIds:', subActionActivityIds);
    
    // Find the sub-issue by ID (either uniqueId or regular id)
    const subIssue = issuesList.flatMap(issue => issue.subIssues || []).find(sub => 
      sub.id === subIssueId || sub.uniqueId === subIssueId
    );
    
    console.log('Found subIssue:', subIssue);
    
    // Priority 1: Try to find by uniqueId in subActionActivityIds (this is where we store the IDs from API)
    if (subIssue && subIssue.uniqueId) {
      const specificIds = subActionActivityIds[subIssue.uniqueId];
      console.log('Looking for uniqueId:', subIssue.uniqueId, 'Found:', specificIds);
      if (specificIds && specificIds.actionImageId) {
        console.log('Found IDs by uniqueId:', specificIds);
        return specificIds;
      }
    }
    
    // Priority 2: Try to find by direct ID lookup in subActionActivityIds
    const directLookup = subActionActivityIds[subIssueId];
    console.log('Looking for direct subIssueId:', subIssueId, 'Found:', directLookup);
    if (directLookup && directLookup.actionImageId) {
      console.log('Found IDs by direct subIssueId lookup:', directLookup);
      return directLookup;
    }
    
    // Priority 3: If we found the sub-issue and it has the IDs stored directly in the object
    if (subIssue && (subIssue.actionImageId !== undefined || subIssue.subActionImageId !== undefined)) {
      const directIds = {
        activityId: subIssue.activityId,
        subActivityId: subIssue.subActivityId,
        actionImageId: subIssue.actionImageId,
        subActionImageId: subIssue.subActionImageId,
        title: subIssue.title || '',
        comments: subIssue.comments || ''
      };
      console.log('Using direct IDs from sub-issue object:', directIds);
      return directIds;
    }
    
    // Final fallback
    if (savedMaintenanceActivityData && savedMaintenanceActivityData.actionImageId) {
      console.log('Using final fallback savedMaintenanceActivityData:', savedMaintenanceActivityData);
      return savedMaintenanceActivityData;
    }
    
    console.log('No IDs found for subIssueId:', subIssueId, 'returning default object with 0s');
    return {
      activityId: 0,
      subActivityId: 0,
      actionImageId: 0,
      subActionImageId: 0,
      title: '',
      comments: ''
    };
  };

  // Function to get the latest activity IDs (for Add Actions modal)
  const getLatestActivityIds = () => {
    // Get the most recent activity IDs from subActionActivityIds
    const latestKeys = Object.keys(subActionActivityIds);
    if (latestKeys.length > 0) {
      const latestKey = latestKeys[latestKeys.length - 1];
      const latestIds = subActionActivityIds[latestKey];
      return latestIds;
    }
    
    // Fallback to global savedMaintenanceActivityData
    return savedMaintenanceActivityData;
  };
  
  // State to track saved maintenance records (for green button display)
  const [savedMaintenanceRecords, setSavedMaintenanceRecords] = useState({
    weekly: null, // Will store the maintenance activity ID
    monthly: null // Will store the maintenance activity ID
  });

  // State for multiple maintenance types selection
  const [selectedMaintenanceTypes, setSelectedMaintenanceTypes] = useState(() => {
    // Initialize from existing data if available
    if (data?.moldMaintenanceData?.maintenanceTypes && Array.isArray(data.moldMaintenanceData.maintenanceTypes)) {
      return data.moldMaintenanceData.maintenanceTypes;
    }
    // Fallback: if maintenanceType is a string, split it by comma
    if (data?.moldMaintenanceData?.maintenanceType && typeof data.moldMaintenanceData.maintenanceType === 'string') {
      return data.moldMaintenanceData.maintenanceType.split(',').map(type => type.trim()).filter(Boolean);
    }
    return [];
  });


  // Maintenance checklist data
  const maintenanceChecklists = {
    Weekly: [
      'Perform visual inspection of the mold.',
      'Inspect water circulation and verify mold temperature.',
      'Lubricate ejector pins.',
      'Lubricate core slides (in and out).',
      'Lubricate oil core guide pins.',
      'Inspect for hydraulic oil leaks.',
      'Check for abnormal noises and ensure smooth mold operation.',
      'Check lubrication of clamp and injection units.',
      'Review mold tip condition.',
      'Perform setup inspection.'
    ],
    Monthly: [
      'Check for worn edges (tolerance: 0.004 mm on edges).',
      'Inspect for gaps between mating parts.',
      'Check air vents on slides.',
      'Inspect cavities for deposits or buildup.',
      'Inspect sprue bushing.',
      'Inspect ejector pin holes.',
      'Inspect water lines.',
      'Inspect hydraulic lines.',
      'Inspect limit switches.',
      'Perform complete cleaning.'
    ]
  };

  // Initialize maintenanceId from existing data
  useEffect(() => {
    if (data?.moldMaintenanceData?.id && data.moldMaintenanceData.id !== 0) {
      setMaintenanceId(data.moldMaintenanceData.id);
    } else if (formData?.id && formData.id !== 0) {
      setMaintenanceId(formData.id);
    }
  }, [data?.moldMaintenanceData?.id, formData?.id]);


  // Debug: Monitor moldInfoID changes
  useEffect(() => {
  }, [formData?.moldInfoID]);

  // Get button states based on maintenance type
  const maintenanceButtonStates = getMaintenanceButtonStates(formData?.maintenanceType);

  useEffect(() => {
    if (moldReceivingData?.moldInfoID) {
      setFormData(prevData => ({
        ...prevData,
        moldInfoID: moldReceivingData.moldInfoID,
      }));
    }
  }, [moldReceivingData]); // Removed formData from dependency array to avoid infinite loops

  // Update button states when maintenance type changes
  useEffect(() => {
    
    // Reset disabled checkboxes when maintenance type changes
    if (formData?.maintenanceType) {
      const buttonStates = getMaintenanceButtonStates(formData.maintenanceType);
      const updatedData = { ...formData };
      
      // Uncheck disabled options
      if (!buttonStates.cleaning) updatedData.cleaning = false;
      if (!buttonStates.spray) updatedData.spray = false;
      if (!buttonStates.inspection) updatedData.inspection = false;
      if (!buttonStates.lubrication) updatedData.lubrication = false;
      if (!buttonStates.componentReplacement) updatedData.componentReplacement = false;
      if (!buttonStates.componentsReplaced) updatedData.componentsReplaced = false;
      if (!buttonStates.repair) updatedData.repair = false;
      if (!buttonStates.functionalTest) updatedData.functionalTest = false;
      
      // Only update if there are changes
      const hasChanges = Object.keys(buttonStates).some(key => {
        if (!buttonStates[key] && formData[key]) return true;
        return false;
      });
      
      if (hasChanges) {
        setFormData(updatedData);
        onChanges(updatedData);
      }
    }
  }, [formData?.maintenanceType]);

  useEffect(() => {
    // console.log('Form useEffect: data?.moldMaintenanceData?.id =', data?.moldMaintenanceData?.id);
    // console.log('Form useEffect: formData.id =', formData.id);
    // console.log('Form useEffect: data?.moldMaintenanceData?.imageSegments =', data?.moldMaintenanceData?.imageSegments);
    if (
      data?.moldMaintenanceData?.id !== undefined &&
      formData.id !== data.moldMaintenanceData.id
    ) {
      // console.log('Form useEffect: Updating formData.id from', formData.id, 'to', data.moldMaintenanceData.id);
      setFormData(prevData => ({
        ...prevData,
        id: data.moldMaintenanceData.id,
      }));
      
      // Process image segments if available
      if (data?.moldMaintenanceData?.imageSegments) {
        // console.log('Processing image segments:', data.moldMaintenanceData.imageSegments);
        const mappedFiles = processImageSegments(data.moldMaintenanceData.imageSegments);
        setProcessedImageSegments(mappedFiles);
        // console.log('Mapped files:', mappedFiles);
        
        // Update additional label if there's a custom label from image segments
        if (mappedFiles.additionalPicturesLabel && mappedFiles.additionalPicturesLabel !== 'Additional Pictures') {
          setFormData(prevData => ({
            ...prevData,
            additionalLabel: mappedFiles.additionalPicturesLabel
          }));
        }
      }
    }
  }, [data?.moldMaintenanceData?.id]); // Re-run only if moldMaintenanceData.id changes

  // Handle direct data (not nested in moldMaintenanceData)
  useEffect(() => {
    // console.log('Direct imageSegments useEffect triggered');
    // console.log('data?.imageSegments =', data?.imageSegments);
    // console.log('data?.moldMaintenanceData =', data?.moldMaintenanceData);
    if (data?.imageSegments && !data?.moldMaintenanceData) {
      // console.log('Processing direct image segments:', data.imageSegments);
      const mappedFiles = processImageSegments(data.imageSegments);
      setProcessedImageSegments(mappedFiles);
      // console.log('Mapped files from direct data:', mappedFiles);
      
      // Update additional label if there's a custom label from image segments
      if (mappedFiles.additionalPicturesLabel && mappedFiles.additionalPicturesLabel !== 'Additional Pictures') {
        setFormData(prevData => ({
          ...prevData,
          additionalLabel: mappedFiles.additionalPicturesLabel
        }));
      }
    }
  }, [data?.imageSegments]);


  // Debug useEffect to see when data changes
  useEffect(() => {
    // console.log('Data object changed:', data);
    // console.log('data keys:', Object.keys(data || {}));
    if (data?.moldMaintenanceData?.imageSegments) {
      // console.log('Found imageSegments in moldMaintenanceData, processing...');
      const mappedFiles = processImageSegments(data.moldMaintenanceData.imageSegments);
      setProcessedImageSegments(mappedFiles);
      // console.log('Mapped files from moldMaintenanceData:', mappedFiles);
      
      // Update additional label if there's a custom label from image segments
      if (mappedFiles.additionalPicturesLabel && mappedFiles.additionalPicturesLabel !== 'Additional Pictures') {
        setFormData(prevData => ({
          ...prevData,
          additionalLabel: mappedFiles.additionalPicturesLabel
        }));
      }
    } else if (data?.imageSegments) {
      // console.log('Found imageSegments in data root, processing...');
      const mappedFiles = processImageSegments(data.imageSegments);
      setProcessedImageSegments(mappedFiles);
      // console.log('Mapped files from data root:', mappedFiles);
      
      // Update additional label if there's a custom label from image segments
      if (mappedFiles.additionalPicturesLabel && mappedFiles.additionalPicturesLabel !== 'Additional Pictures') {
        setFormData(prevData => ({
          ...prevData,
          additionalLabel: mappedFiles.additionalPicturesLabel
        }));
      }
    }
  }, [data]);

  useEffect(() => {
    if (!Array.isArray(issuesList) || issuesList.length === 0) {
      return;
    }

    let hasChanges = false;
    const updatedIssues = issuesList.map(issue => {
      const activityIssueIdValue = issue.activityIssueId || issue.ActivityIssueId || 0;
      if (!activityIssueIdValue) {
        return issue;
      }

      const latestImages = getActionImagesForActivityIssue(activityIssueIdValue);
      const currentIds = (issue.actionImages || []).map(img => img.id).join(',');
      const latestIds = latestImages.map(img => img.id).join(',');

      if (currentIds === latestIds) {
        return issue;
      }

      hasChanges = true;
      return {
        ...issue,
        actionImages: latestImages
      };
    });

    if (hasChanges) {
      setIssuesList(updatedIssues);
    }
  }, [processedImageSegments?.ActionImages, formData?.imageSegments, issuesList]);

  // Update selectedMaintenanceTypes when data changes
  useEffect(() => {
    // console.log('selectedMaintenanceTypes useEffect triggered');
    // console.log('data?.moldMaintenanceData?.maintenanceTypes:', data?.moldMaintenanceData?.maintenanceTypes);
    // console.log('data?.moldMaintenanceData?.maintenanceType:', data?.moldMaintenanceData?.maintenanceType);
    
    if (data?.moldMaintenanceData?.maintenanceTypes && Array.isArray(data.moldMaintenanceData.maintenanceTypes)) {
      // console.log('Setting selectedMaintenanceTypes from maintenanceTypes array:', data.moldMaintenanceData.maintenanceTypes);
      setSelectedMaintenanceTypes(data.moldMaintenanceData.maintenanceTypes);
    } else if (data?.moldMaintenanceData?.maintenanceType && typeof data.moldMaintenanceData.maintenanceType === 'string') {
      const types = data.moldMaintenanceData.maintenanceType.split(',').map(type => type.trim()).filter(Boolean);
      // console.log('Setting selectedMaintenanceTypes from maintenanceType string:', types);
      setSelectedMaintenanceTypes(types);
    } else {
      // console.log('No maintenance types found in data, keeping current selection');
    }
  }, [data?.moldMaintenanceData?.maintenanceTypes, data?.moldMaintenanceData?.maintenanceType, data?.moldMaintenanceData]);

  // Handle activities and activityIssues from API response
  useEffect(() => {
    
    // Reset savedMaintenanceRecords when data changes
    setSavedMaintenanceRecords({
      weekly: null,
      monthly: null
    });
    
    if (data?.moldMaintenanceData?.activities && Array.isArray(data.moldMaintenanceData.activities) && data.moldMaintenanceData.activities.length > 0) {
      // Find the appropriate activity for loading (prefer the first one with data, or first one if all are empty)
      let activityToLoad = data.moldMaintenanceData.activities[0];
      
      // Look for an activity that has actual data
      for (const activity of data.moldMaintenanceData.activities) {
        const hasAnyData = Object.values(activity).some(value => value !== null && value !== undefined);
        if (hasAnyData) {
          activityToLoad = activity;
          break;
        }
      }
      
      setLoadedActivities(activityToLoad);
      
      // Update savedMaintenanceRecords to show green buttons based on all activities
      let weeklyActivityId = null;
      let monthlyActivityId = null;
      
      // Check each activity to determine if it contains weekly or monthly data
      console.log('Processing activities for maintenance frequency detection:', data.moldMaintenanceData.activities);
      
      data.moldMaintenanceData.activities.forEach((activity, index) => {
        console.log(`Processing activity ${index + 1}:`, activity);
        
        if (activity.id) {
          // Check if this activity has Weekly maintenance data
          const hasWeeklyData = activity.performVisualInspection !== null || 
                               activity.inspectWaterCirculation !== null || 
                               activity.lubricateEjectorPins !== null || 
                               activity.lubricateCoreSlides !== null || 
                               activity.lubricateOilCoreGuidePins !== null || 
                               activity.inspectHydraulicOilLeaks !== null || 
                               activity.checkAbnormalNoises !== null || 
                               activity.checkClampInjectionLubrication !== null || 
                               activity.reviewMoldTipCondition !== null || 
                               activity.performSetupInspection !== null;
          
          // Check if this activity has Monthly maintenance data
          const hasMonthlyData = activity.checkWornEdges !== null || 
                                activity.inspectMatingPartGaps !== null || 
                                activity.checkAirVentsOnSlides !== null || 
                                activity.inspectCavitiesForDeposits !== null || 
                                activity.inspectSprueBushing !== null || 
                                activity.inspectEjectorPinHoles !== null || 
                                activity.inspectWaterLines !== null || 
                                activity.inspectHydraulicLines !== null || 
                                activity.inspectLimitSwitches !== null || 
                                activity.performCompleteCleaning !== null;
          
          console.log(`Activity ${index + 1} (ID: ${activity.id}) - Weekly data: ${hasWeeklyData}, Monthly data: ${hasMonthlyData}`);
          
          // Store the activity ID for the appropriate frequency type
          if (hasWeeklyData && !weeklyActivityId) {
            weeklyActivityId = activity.id;
            console.log(`Set weekly activity ID to: ${activity.id}`);
          }
          if (hasMonthlyData && !monthlyActivityId) {
            monthlyActivityId = activity.id;
            console.log(`Set monthly activity ID to: ${activity.id}`);
          }
        }
      });
      
      const finalSavedRecords = {
        weekly: weeklyActivityId,
        monthly: monthlyActivityId
      };
      
      console.log('Final savedMaintenanceRecords state:', finalSavedRecords);
      setSavedMaintenanceRecords(finalSavedRecords);
    }
    
    if (data?.moldMaintenanceData?.activityIssues && Array.isArray(data.moldMaintenanceData.activityIssues) && data.moldMaintenanceData.activityIssues.length > 0) {
      setLoadedActivityIssues(data.moldMaintenanceData.activityIssues);
      
      // Update savedIssues to show green button
      const firstIssue = data.moldMaintenanceData.activityIssues[0];
      if (firstIssue.activityIssueId && firstIssue.maintenanceId) {
        setSavedIssues({
          activityIssueId: firstIssue.activityIssueId,
          maintenanceId: firstIssue.maintenanceId
        });
      }
      
      // Load future actions for each issue that has an activityIssueId
      const moldInfoId = data?.moldMaintenanceData?.moldInfoID;
      if (moldInfoId) {
        // Store activityIssueId for each issue and load their future actions
        data.moldMaintenanceData.activityIssues.forEach((issue, index) => {
          const activityIssueId = issue.activityIssueId;
            
          if (activityIssueId && activityIssueId > 0) {
            // Store the activityIssueId for this issue
            setIssueActivityIds(prev => ({
              ...prev,
              [index + 1]: activityIssueId
            }));
            
            // Load future actions for this specific issue - pass activityIssueId directly
            loadFutureActionsForIssue(moldInfoId, index + 1, activityIssueId);
          }
        });
      }
    }
  }, [data?.moldMaintenanceData?.activities, data?.moldMaintenanceData?.activityIssues]);

  // Handle date field updates when data changes
  useEffect(() => {
    if (data?.moldMaintenanceData) {
      
      setFormData(prevFormData => {
        const updatedFormData = { ...prevFormData };
        let hasChanges = false;
        
        // Convert and update date fields
        const dateOnlyFields = ['lastMaintenanceDate', 'currentMaintenanceDate'];
        dateOnlyFields.forEach(fieldName => {
          const apiValue = data.moldMaintenanceData[fieldName];
          if (apiValue && apiValue !== updatedFormData[fieldName]) {
            const convertedDate = convertISODateToFormDate(apiValue);
            if (convertedDate !== updatedFormData[fieldName]) {
              updatedFormData[fieldName] = convertedDate;
              hasChanges = true;
            }
          }
        });

        const dateTimeFields = [
          'maintenance_PerformedBy_Date',
          'quality_Control_ApprovedBy_Date',
          'engineering_ApprovedBy_Date',
          'submittedByDate',
        ];
        dateTimeFields.forEach(fieldName => {
          const apiValue = data.moldMaintenanceData[fieldName];
          if (apiValue && apiValue !== updatedFormData[fieldName]) {
            const converted = convertISODateToFormDateTime(apiValue);
            if (converted !== updatedFormData[fieldName]) {
              updatedFormData[fieldName] = converted;
              hasChanges = true;
            }
          }
        });
        
        // Also update other fields that might have changed
        const otherFields = [
          'totalCyclesSinceLastMaint',
          'maintenanceIntervalCycles',
          'currentMaintenanceHours',
          'totalMaintenanceHours',
          'maintenance_PerformedBy',
          'quality_Control_ApprovedBy',
          'engineering_ApprovedBy',
          'submittedBy',
          'additionalComments'
        ];
        
        otherFields.forEach(fieldName => {
          const apiValue =
            fieldName === 'submittedBy'
              ? mapMaintenanceRecordSubmittedByFromApi(data.moldMaintenanceData).submittedBy
              : data.moldMaintenanceData[fieldName];
          if (apiValue !== undefined && apiValue !== updatedFormData[fieldName]) {
            updatedFormData[fieldName] = apiValue;
            hasChanges = true;
          }
        });
        
        // For existing maintenance records, reset current maintenance hours to 0
        // but keep total maintenance hours from API response for display
        if (data.moldMaintenanceData.id && data.moldMaintenanceData.id > 0) {
          if (updatedFormData.currentMaintenanceHours !== '0') {
            updatedFormData.currentMaintenanceHours = '0';
            hasChanges = true;
          }
          
          // Keep total maintenance hours from API response for display
          if (data.moldMaintenanceData.totalMaintenanceHours) {
            updatedFormData.totalMaintenanceHours = data.moldMaintenanceData.totalMaintenanceHours.toString();
            updatedFormData.originalTotalMaintenanceHours = data.moldMaintenanceData.totalMaintenanceHours.toString();
            hasChanges = true;
          }
        }
        
        if (hasChanges) {
        }
        
        return hasChanges ? updatedFormData : prevFormData;
      });
    }
  }, [data?.moldMaintenanceData]);

  // Update parent component when selectedMaintenanceTypes changes
  useEffect(() => {
    if (selectedMaintenanceTypes.length > 0) {
      updateFormField('maintenanceTypes', selectedMaintenanceTypes);
    } else {
      // Also update when maintenance types are cleared
      updateFormField('maintenanceTypes', []);
    }
  }, [selectedMaintenanceTypes]);

  useEffect(() => {
    const fetchParts = async () => {
      try {
        const response = await PartService.getAllPartsList();
        if (response?.response) {
          setPartsList(response.response);
        }
      } catch (error) {
      }
    };

    fetchParts();
  }, []);

  const updateFormField = (fieldName, value) => {
    setFormData(prevData => {
      let safeValue = value;
      
      // Validate and cap currentMaintenanceHours to prevent database overflow
      if (fieldName === 'currentMaintenanceHours') {
        const currentHours = parseFloat(value) || 0;
        // Cap at 10,000 to be very safe with database limits
        safeValue = Math.min(Math.max(currentHours, 0), 10000).toString();
      }
      
      const updatedData = {
        ...prevData,
        [fieldName]: safeValue,
      };
      
      // Handle maintenance hours calculation
      if (fieldName === 'currentMaintenanceHours') {
        const currentHours = parseFloat(safeValue) || 0;
        
        // Don't change total maintenance hours in the UI
        // Keep it at 0 initially, the actual calculation will be done when saving
      }
      
      // Also update the parent component with the updated data
      onChanges(updatedData);
      return updatedData;
    });
  };

  const handleFileUpload = eOrFiles => {
    // If called from WebcamFileUpload, eOrFiles will be an array of files
    if (Array.isArray(eOrFiles)) {
      const paths = eOrFiles.map(f => f.path).join(',');
      let fieldName = eOrFiles[0]?.id;
      // Map known image fields to PascalCase for API
      const fieldMap = {
        MOLD_PICTURE_BEFORE_MAINTENANCE: 'MoldPicture_Before_Maintenance',
        MOLD_PICTURE_AFTER_MAINTENANCE: 'MoldPicture_After_Maintenance',
        // Map specific image types to their corresponding fields
        MAINTENANCE_TYPE_PICTURES: 'AdditionalPicPath1', // Preventive Maintenance Pictures -> additionalPicPath1
        REPAIR_PICTURES: 'AdditionalPicPath3', // Repair Pictures -> additionalPicPath3
        ADDITIONAL_PICTURES: 'AdditionalPicPath', // Custom Additional Pictures field
        ADDITIONAL_PICTURE_ONE: 'AdditionalPicPath1', // Preventive Maintenance Pictures -> additionalPicPath1
        ADDITIONAL_PICTURE_TWO: 'AdditionalPicPath2', // Breakdown Maintenance Pictures -> additionalPicPath2
        ADDITIONAL_PICTURE_THREE: 'AdditionalPicPath3', // Repair Pictures -> additionalPicPath3
      };
      if (fieldMap[fieldName]) {
        fieldName = fieldMap[fieldName];
      }
      if (fieldName) {
        updateFormField(fieldName, paths);
      }
      return;
    }
    // Otherwise, handle as a regular file input event for document uploads
    const e = eOrFiles;
    if (e && e.target && e.target.files) {
      const file = e.target.files[0];
      if (file) {
        onFileUpload(e.target.id, file); // Pass data to parent
      }
    }
  };

  // Function to handle when maintenance record is saved and update the form's ID
  const handleMaintenanceSaved = (newMaintenanceId, newMoldInfoID) => {
    
    if (newMaintenanceId && newMaintenanceId > 0) {
      setFormData(prevData => {
        const updatedData = {
          ...prevData,
          id: newMaintenanceId,
          // Update moldInfoID if provided, otherwise keep existing
          moldInfoID: newMoldInfoID || prevData.moldInfoID
        };
        // Also update the parent component with the updated data
        onChanges(updatedData);
        return updatedData;
      });
    } else {
    }
  };

  // Function to handle when activity issue is saved and update the issue data
  const handleActivityIssueSaved = (activityIssueId, issueId) => {
    console.log('=== handleActivityIssueSaved CALLED ===');
    console.log('handleActivityIssueSaved called with:', { activityIssueId, issueId });
    console.log('Current issuesList before update:', issuesList);
    
    if (activityIssueId && activityIssueId > 0) {
      setIssuesList(prevIssues => {
        const updatedIssues = prevIssues.map(issue => 
          issue.id === issueId 
            ? { ...issue, activityIssueId: activityIssueId }
            : issue
        );
        console.log('Updated issuesList with activityIssueId:', updatedIssues);
        console.log('Issue that was updated:', updatedIssues.find(issue => issue.id === issueId));
        return updatedIssues;
      });
      
      // Also update the issueActivityIds state for consistency
      setIssueActivityIds(prev => ({
        ...prev,
        [issueId]: activityIssueId
      }));
      
      console.log('State update completed. The issue should now have activityIssueId:', activityIssueId);
    } else {
      console.log('No valid activityIssueId provided:', activityIssueId);
    }
  };

  // Function to update maintenanceId for image segments after successful submit
  const updateImageSegmentsMaintenanceId = async (newMaintenanceId) => {
    const buttonLabels = [
      'Mold Picture Before Maintenance',
      'Mold Picture After Maintenance',
      'Preventive Maintenance Pictures',
      'Breakdown Maintenance Pictures',
      'Repair Pictures',
      'Mold Modification Pictures',
      formData.additionalLabel || 'Additional Pictures'
    ];

    for (const buttonLabel of buttonLabels) {
      try {
        await MoldService.updateImageSegmentsMaintenanceId(
          formData.moldInfoID,
          buttonLabel,
          newMaintenanceId
        );
      } catch (error) {
      }
    }
  };

  // Function to handle image segment uploads
  const handleImageSegmentUploaded = (segment, record) => {
    const targetFieldFromMap = getFieldNameForRecord(record);
    const excludeFromAdditional = shouldExcludeFromAdditional(record, segment?.category);
    let targetFieldName = targetFieldFromMap;
    
    if (!targetFieldName) {
      if (excludeFromAdditional) {
        return;
      }
      targetFieldName = 'AdditionalPicPath';
    } else if (targetFieldName === 'AdditionalPicPath' && excludeFromAdditional) {
      return;
    }
    
    setProcessedImageSegments(prevProcessed => {
      const updated = { ...prevProcessed };
      const existingFiles = updated[targetFieldName] ? [...updated[targetFieldName]] : [];
      const fileObject = {
        id: segment.id,
        name: `${segment.segment || 'Image'}_${segment.id || Date.now()}.jpg`,
        path: segment.imageUrl,
        fileName: `${segment.segment || 'Image'}_${segment.id || Date.now()}.jpg`,
        filePath: segment.imageUrl,
        record,
        category: segment.category,
        actionImageId: segment.actionImageId,
        subActionImageId: segment.subActionImageId,
        segment: segment.segment,
        slotName: segment.segment
      };
      const existingIndex = existingFiles.findIndex(img => img.id === segment.id);
      if (existingIndex === -1) {
        existingFiles.push(fileObject);
      } else {
        existingFiles[existingIndex] = fileObject;
      }
      updated[targetFieldName] = existingFiles;
      if (targetFieldName === 'AdditionalPicPath') {
        updated.additionalPicturesLabel = record || updated.additionalPicturesLabel || 'Additional Pictures';
      }
      return updated;
    });
    
    setFormData(prevData => {
      const currentSegments = Array.isArray(prevData.imageSegments) ? prevData.imageSegments : [];
      const existingIndex = currentSegments.findIndex(existing => existing.id === segment.id);
      const updatedSegment = {
        ...segment,
        record
      };
      const nextSegments = existingIndex === -1
        ? [...currentSegments, updatedSegment]
        : currentSegments.map(existing => existing.id === segment.id ? updatedSegment : existing);
      return {
        ...prevData,
        imageSegments: nextSegments
      };
    });
    
    if (onImageSegmentUploaded) {
      onImageSegmentUploaded(segment, record);
    }
  };

  function normalizeImageKey(key) {
    return key.replace(/_/g, '').toLowerCase();
  }

  const RECORD_FIELD_MAP = new Map(
    RECORD_FIELD_PAIRS.map(([label, field]) => [normalizeRecordName(label), field])
  );
  const getFieldNameForRecord = (record) => RECORD_FIELD_MAP.get(normalizeRecordName(record));
  const shouldExcludeFromAdditional = (record, category = '') => {
    const normalizedRecord = normalizeRecordName(record);
    if (MAINTENANCE_RECORD_SET.has(normalizedRecord)) {
      return true;
    }
    if (OTHER_MAINTENANCE_RECORD_SET.has(normalizedRecord)) {
      return true;
    }
    // Handle null/undefined category
    const categoryStr = category || '';
    return categoryStr.trim().toLowerCase().includes('other-maintenance-records');
  };

  function convertDocumentsForWebCamFileUpload(fieldName) {
    if (fieldName === 'AdditionalPicPath') {
      return getAdditionalDocuments();
    }

    if (processedImageSegments[fieldName] && processedImageSegments[fieldName].length > 0) {
      return processedImageSegments[fieldName];
    }
    
    const urls = (formData[fieldName] || '').split(',').filter(Boolean);
    const uploaded = (formData.uploadedImages || []).filter(
      img => normalizeImageKey(img.imageName || '') === normalizeImageKey(fieldName),
    );
    const urlDocs = urls.map((url, idx) => ({
      name: `${fieldName}_${idx + 1}`,
      path: url,
    }));
    const uploadedDocs = uploaded.map(img => ({
      name: img.imageName,
      path: img.imagePath,
    }));
    const result = [...urlDocs, ...uploadedDocs];
    if (fieldName === 'AdditionalPicPath' && Array.isArray(formData?.imageSegments)) {
      return result.filter(doc => {
        if (!doc.id) {
          return true;
        }
        const matchingSegment = formData.imageSegments.find(segment => segment.id === doc.id);
        if (!matchingSegment) {
          return true;
        }
        return !shouldExcludeFromAdditional(matchingSegment.record, matchingSegment.category);
      });
    }
    return result;
  }

  // Function to check if there are images for a specific field
  const hasImagesForField = (fieldName) => {
    if (fieldName === 'AdditionalPicPath') {
      return getAdditionalDocuments().length > 0;
    }
    const documents = convertDocumentsForWebCamFileUpload(fieldName);
    const hasImages = documents && documents.length > 0;
    return hasImages;
  };

  // Function to check if there are image segments for a specific record
  const hasImageSegmentsForRecord = (recordName) => {
    // First check processedImageSegments (from getAllMoldMaintenanceByToolID)
    if (recordName === 'Action Images' && processedImageSegments.SubActionImages && processedImageSegments.SubActionImages.length > 0) {
      console.log('Found SubActionImages in processedImageSegments:', processedImageSegments.SubActionImages.length);
      return true;
    }
    
    // Fallback to formData.imageSegments
    if (!formData?.imageSegments || !Array.isArray(formData.imageSegments)) {
      return false;
    }
    
    const matchingSegments = formData.imageSegments.filter(segment => segment.record === recordName);
    const hasSegments = matchingSegments.length > 0;
    
    console.log(`hasImageSegmentsForRecord(${recordName}):`, hasSegments, 'matching segments:', matchingSegments.length);
    return hasSegments;
  };

  // Function to check if there are image segments for any unknown/custom record
  const hasImageSegmentsForUnknownRecords = () => {
    if (!formData?.imageSegments || !Array.isArray(formData.imageSegments)) {
      return false;
    }
    
    const knownRecords = [
      'Mold Picture Before Maintenance',
      'Mold Picture After Maintenance', 
      'Preventive Maintenance Pictures',
      'Breakdown Maintenance Pictures',
      'Repair Pictures',
      'Mold Modification Pictures',
      'Additional Pictures',
      'Extra Images',
      'Action Images',
      'Sub Action Images',
      'Capture Future Actions Images'
    ];
    
    const unknownSegments = formData.imageSegments.filter(segment => {
      return !knownRecords.includes(segment.record);
    });
    
    return unknownSegments.length > 0;
  };

  const getDocumentPath = documentPath => {
    let fileName = '';
    if (documentPath != undefined) {
      const encodedFileName = documentPath?.split('/').pop();
      const decoded = decodeURIComponent(encodedFileName);
      const lastUnderscore = decoded.lastIndexOf('_');
      const extensionIndex = decoded.lastIndexOf('.');

      if (lastUnderscore !== -1 && extensionIndex !== -1 && lastUnderscore < extensionIndex) {
        fileName = decoded?.slice(0, lastUnderscore) + decoded?.slice(extensionIndex);
      } else {
        fileName = decoded;
      }
    }

    return documentPath ? { fileUrl: documentPath, fileName: fileName } : '';
  };

  // Function to get document path from either formData or dataToSave
  const getDocumentPathFromData = (fieldName) => {
    // First check dataToSave (from parent component) for the document path
    const dataToSavePath = dataToSave?.[fieldName];
    if (dataToSavePath) {
      return getDocumentPath(dataToSavePath);
    }
    
    // Fallback to formData
    const formDataPath = formData?.[fieldName];
    return getDocumentPath(formDataPath);
  };

  // Modal handler functions
  const handleMaintenanceTypeClick = (type) => {
    setSelectedMaintenanceType(type);
    setShowMaintenanceModal(true);
    setIsViewingSavedRecord(false); // Set to create mode (not view mode)
    // Initialize checklist responses for this type
    const initialResponses = {};
    maintenanceChecklists[type].forEach((item, index) => {
      initialResponses[`${type}_${index}`] = '';
    });
    setChecklistResponses(initialResponses);
  };

  const handleChecklistResponse = (itemKey, response) => {
    setChecklistResponses(prev => ({
      ...prev,
      [itemKey]: response
    }));
  };

  // Function to map API response to checklist format
  const mapApiResponseToChecklist = (apiResponse) => {
    const mappedResponses = {};
    
    // Mapping between API field names and checklist items
    const fieldMapping = {
      // Weekly checklist items
      'performVisualInspection': { type: 'Weekly', index: 0 },
      'inspectWaterCirculation': { type: 'Weekly', index: 1 },
      'lubricateEjectorPins': { type: 'Weekly', index: 2 },
      'lubricateCoreSlides': { type: 'Weekly', index: 3 },
      'lubricateOilCoreGuidePins': { type: 'Weekly', index: 4 },
      'inspectHydraulicOilLeaks': { type: 'Weekly', index: 5 },
      'checkAbnormalNoises': { type: 'Weekly', index: 6 },
      'checkClampInjectionLubrication': { type: 'Weekly', index: 7 },
      'reviewMoldTipCondition': { type: 'Weekly', index: 8 },
      'performSetupInspection': { type: 'Weekly', index: 9 },
      
      // Monthly checklist items
      'checkWornEdges': { type: 'Monthly', index: 0 },
      'inspectMatingPartGaps': { type: 'Monthly', index: 1 },
      'checkAirVentsOnSlides': { type: 'Monthly', index: 2 },
      'inspectCavitiesForDeposits': { type: 'Monthly', index: 3 },
      'inspectSprueBushing': { type: 'Monthly', index: 4 },
      'inspectEjectorPinHoles': { type: 'Monthly', index: 5 },
      'inspectWaterLines': { type: 'Monthly', index: 6 },
      'inspectHydraulicLines': { type: 'Monthly', index: 7 },
      'inspectLimitSwitches': { type: 'Monthly', index: 8 },
      'performCompleteCleaning': { type: 'Monthly', index: 9 }
    };
    
    // Map each field from the API response to checklist format
    Object.keys(apiResponse).forEach(key => {
      const value = apiResponse[key];
      const mapping = fieldMapping[key];
      
      if (mapping) {
        const checklistKey = `${mapping.type}_${mapping.index}`;
        if (value === true) {
          mappedResponses[checklistKey] = 'Yes';
        } else if (value === false) {
          mappedResponses[checklistKey] = 'No';
        } else {
          mappedResponses[checklistKey] = ''; // null or undefined
        }
      }
    });
    
    return mappedResponses;
  };

  // Function to map checklist responses to API format
  const mapChecklistToAPI = (checklistResponses, maintenanceType) => {
    // Get existing activity ID if available
    let existingActivityId = 0;
    if (loadedActivities && loadedActivities.id) {
      existingActivityId = loadedActivities.id;
    } else if (savedMaintenanceActivityData && (savedMaintenanceActivityData.Id || savedMaintenanceActivityData.id)) {
      existingActivityId = savedMaintenanceActivityData.Id || savedMaintenanceActivityData.id;
    }
    
    const apiData = {
      Id: existingActivityId, // Use existing ID if available, otherwise 0 for new record
      MoldInfoID: formData.moldInfoID || data?.moldInfoID || data?.moldMaintenanceData?.moldInfoID,
      MaintenanceId: maintenanceId || 0,
    };
    

    // Helper function to map response to API format
    const mapResponse = (response) => {
      if (response === 'Yes') return true;
      if (response === 'No') return false;
      return null; // For empty or unselected responses
    };

    if (maintenanceType === 'Weekly') {
      // Map Weekly checklist items to API fields (using camelCase field names)
      apiData.performVisualInspection = mapResponse(checklistResponses['Weekly_0']);
      apiData.inspectWaterCirculation = mapResponse(checklistResponses['Weekly_1']);
      apiData.lubricateEjectorPins = mapResponse(checklistResponses['Weekly_2']);
      apiData.lubricateCoreSlides = mapResponse(checklistResponses['Weekly_3']);
      apiData.lubricateOilCoreGuidePins = mapResponse(checklistResponses['Weekly_4']);
      apiData.inspectHydraulicOilLeaks = mapResponse(checklistResponses['Weekly_5']);
      apiData.checkAbnormalNoises = mapResponse(checklistResponses['Weekly_6']);
      apiData.checkClampInjectionLubrication = mapResponse(checklistResponses['Weekly_7']);
      apiData.reviewMoldTipCondition = mapResponse(checklistResponses['Weekly_8']);
      apiData.performSetupInspection = mapResponse(checklistResponses['Weekly_9']);
    } else if (maintenanceType === 'Monthly') {
      // Map Monthly checklist items to API fields (using camelCase field names)
      apiData.checkWornEdges = mapResponse(checklistResponses['Monthly_0']);
      apiData.inspectMatingPartGaps = mapResponse(checklistResponses['Monthly_1']);
      apiData.checkAirVentsOnSlides = mapResponse(checklistResponses['Monthly_2']);
      apiData.inspectCavitiesForDeposits = mapResponse(checklistResponses['Monthly_3']);
      apiData.inspectSprueBushing = mapResponse(checklistResponses['Monthly_4']);
      apiData.inspectEjectorPinHoles = mapResponse(checklistResponses['Monthly_5']);
      apiData.inspectWaterLines = mapResponse(checklistResponses['Monthly_6']);
      apiData.inspectHydraulicLines = mapResponse(checklistResponses['Monthly_7']);
      apiData.inspectLimitSwitches = mapResponse(checklistResponses['Monthly_8']);
      apiData.performCompleteCleaning = mapResponse(checklistResponses['Monthly_9']);
    }

    return apiData;
  };

  // const handleSaveChecklist = async () => {
  //   try {
  //     // Use the same data structure as image capture modals
  //     const modalData = getFormDataForModals();
      
  //     // console.log('handleSaveChecklist - modalData:', modalData);
  //     // console.log('handleSaveChecklist - modalData.ToolID:', modalData.ToolID);
  //     // console.log('handleSaveChecklist - modalData.MoldInfoID:', modalData.MoldInfoID);
      
  //     // Use the same API call logic as the parent component
  //     let maintenanceResponse;
  //     const currentId = maintenanceId || formData.id || 0;
      
  //     // Ensure the maintenance type is properly set and include all form data
  //     // Map form data field names to match what filterDataForAPI expects
  //     const updatedModalData = {
  //       ...formData, // Include all form data
  //       ...modalData, // Include modal-specific data (ToolID, MoldInfoID, etc.)
  //       // Ensure field names match what filterDataForAPI expects
  //       toolID: formData.toolId || modalData.toolID,
  //       moldInfoID: formData.moldInfoId || modalData.moldInfoID,
  //       maintenanceTypes: selectedMaintenanceTypes && selectedMaintenanceTypes.length > 0 
  //         ? selectedMaintenanceTypes 
  //         : (selectedMaintenanceType ? [selectedMaintenanceType] : [])
  //     };
      
      
  //     if (currentId == 0) {
  //       // For new records, use the direct API call with filterDataForAPI
  //       const filteredData = filterDataForAPI ? filterDataForAPI(updatedModalData) : updatedModalData;
        
  //       // Ensure critical fields are always included
  //       const finalData = {
  //         ...filteredData,
  //         ToolID: filteredData.ToolID || updatedModalData.toolID || formData.toolId,
  //         MoldInfoID: filteredData.MoldInfoID || updatedModalData.moldInfoID || formData.moldInfoId
  //       };
        
  //       maintenanceResponse = await MoldService.saveUpdateMaintenance({ moldMaintenance: finalData });
  //     } else {
  //       // For existing records, we need to call the parent's onMaintenanceSubmit
  //       // For now, let's use the direct API call approach
  //       const filteredData = filterDataForAPI ? filterDataForAPI(updatedModalData) : updatedModalData;
        
  //       // Ensure critical fields are always included
  //       const finalData = {
  //         ...filteredData,
  //         ToolID: filteredData.ToolID || updatedModalData.toolID || formData.toolId,
  //         MoldInfoID: filteredData.MoldInfoID || updatedModalData.moldInfoID || formData.moldInfoId
  //       };
        
  //       maintenanceResponse = await MoldService.saveUpdateMaintenance({ moldMaintenance: finalData });
  //     }

  //     // Extract the maintenance ID from response
  //     let newMaintenanceId = maintenanceId;
  //     if (maintenanceResponse && (maintenanceResponse.ID || maintenanceResponse.id || maintenanceResponse.maintenanceID)) {
  //       newMaintenanceId = maintenanceResponse.ID || maintenanceResponse.id || maintenanceResponse.maintenanceID;
  //       setMaintenanceId(newMaintenanceId);
  //     }

  //     // Now call saveOrUpdateMoldMaintenanceActivity API
  //     const activityData = mapChecklistToAPI(checklistResponses, selectedMaintenanceType);
  //     activityData.MaintenanceId = newMaintenanceId;

  //     // console.log('Calling saveOrUpdateMoldMaintenanceActivity with data:', activityData);
  //     const activityResponse = await MoldService.saveUpdateMaintenanceActivity(activityData);
  //     // console.log('Activity response:', activityResponse);

  //     // Show success toast
  //     dispatch(showToast({ 
  //       message: `${selectedMaintenanceType} Maintenance Checklist saved successfully!`, 
  //       type: 'success' 
  //     }));

  //   // Save the checklist responses to form data
  //   const checklistData = {
  //     maintenanceType: selectedMaintenanceType,
  //     checklistResponses: checklistResponses,
  //     completedDate: new Date().toISOString()
  //   };
    
  //   // Update form data with checklist information and maintenance frequency
  //   updateFormField('maintenanceChecklist', checklistData);
  //   updateFormField('maintenanceFrequency', selectedMaintenanceType);
    
  //   // Close modal
  //   setShowMaintenanceModal(false);
  //   setSelectedMaintenanceType('');
  //   setChecklistResponses({});

  //   } catch (error) {
  //     console.error('Error saving maintenance checklist:', error);
  //     dispatch(showToast({ 
  //       message: 'Error saving maintenance checklist. Please try again.', 
  //       type: 'danger' 
  //     }));
  //   }
  // };
   const handleSaveChecklist = async () => {
     // Only show confirmation dialog for new records (Save), not for updates
     if (isViewingSavedRecord) {
       // For updates, call directly without confirmation
       await performSaveChecklist();
     } else {
       // Check if maintenance already exists (maintenanceId > 0 or formData.id > 0)
       const existingMaintenanceId = formData.id || formData.moldMaintenanceData?.id || data?.moldMaintenanceData?.id || maintenanceId || 0;
       
       if (existingMaintenanceId > 0) {
         // Maintenance already exists, skip confirmation and call activity API directly
         console.log('Maintenance already exists with ID:', existingMaintenanceId);
         await performSaveChecklist();
       } else {
         // For new records, show confirmation dialog
     showConfirmModal(async () => {
       await performSaveChecklist();
     });
       }
     }
   };

   const performSaveChecklist = async () => {
     try {
      // Check if we're updating an existing record or creating a new one
      if (isViewingSavedRecord) {
        // Update existing record - only call saveOrUpdateMoldMaintenanceActivity API
        
        // Map the current checklist responses to API fields
        const activityData = mapChecklistToAPI(checklistResponses, selectedMaintenanceType);
        
        // Use the correct Id from loadedActivities (from getMoldMaintenanceActivityByIdAsync)
        if (loadedActivities && loadedActivities.id) {
          activityData.Id = loadedActivities.id;
        } else {
          dispatch(showToast({ 
            message: `No activity ID found for update operation`, 
            type: 'danger' 
          }));
          return;
        }
        
        // Get MaintenanceId from the current form data
        const maintenanceId = formData.id || formData.moldMaintenanceData?.id || data?.moldMaintenanceData?.id || maintenanceId || 0;
        activityData.MaintenanceId = maintenanceId;
        
        // Get MoldInfoID from the current form data
        const moldInfoID = formData.moldInfoId || formData.moldInfoID || 
                          data?.moldMaintenanceData?.moldInfoID || 
                          data?.moldInfoID || 
                          dataToSave?.moldInfoID;
        activityData.MoldInfoID = moldInfoID;
        
        // Call only the activity API for updates
        const activityResponse = await MoldService.saveUpdateMaintenanceActivity(activityData);
        
        dispatch(showToast({ 
          message: `${selectedMaintenanceType} Maintenance Record updated successfully!`, 
          type: 'success' 
        }));
         
         // Close modal
         setShowMaintenanceModal(false);
         setSelectedMaintenanceType('');
         setChecklistResponses({});
         setIsViewingSavedRecord(false);
         return;
      }
      
      // Check if maintenance already exists (for Save button when maintenanceId > 0)
      const existingMaintenanceId = formData.id || formData.moldMaintenanceData?.id || data?.moldMaintenanceData?.id || maintenanceId || 0;
      
      if (existingMaintenanceId > 0) {
        // Maintenance already exists, only call saveOrUpdateMoldMaintenanceActivity API
        
        // Map the current checklist responses to API fields
        const activityData = mapChecklistToAPI(checklistResponses, selectedMaintenanceType);
        activityData.MaintenanceId = existingMaintenanceId;
        
        // Get MoldInfoID from the current form data
        const moldInfoID = formData.moldInfoId || formData.moldInfoID || 
                          data?.moldMaintenanceData?.moldInfoID || 
                          data?.moldInfoID || 
                          dataToSave?.moldInfoID;
        activityData.MoldInfoID = moldInfoID;
        
        // Call only the activity API (maintenance already exists)
        const activityResponse = await MoldService.saveUpdateMaintenanceActivity(activityData);
        
        // Update savedMaintenanceRecords to show green button
        const activityId = activityResponse?.Id || activityResponse?.id || activityResponse?.response?.Id || activityResponse?.response?.id;
        if (activityId) {
          setSavedMaintenanceRecords(prev => ({
            ...prev,
            [selectedMaintenanceType.toLowerCase()]: activityId
          }));
        }
         
         dispatch(showToast({ 
          message: `${selectedMaintenanceType} Maintenance Checklist saved successfully!`, 
           type: 'success' 
         }));
         
         // Close modal
         setShowMaintenanceModal(false);
         setSelectedMaintenanceType('');
         setChecklistResponses({});
         return;
      }
       
       // Create new record - use existing logic
       // Use the exact same pattern as Save selected images button
       // First, update the form data with the maintenance type
       // The maintenanceType should come from the selected maintenance type checkboxes, not from Weekly/Monthly frequency
       const maintenanceTypes = selectedMaintenanceTypes && selectedMaintenanceTypes.length > 0 
         ? selectedMaintenanceTypes 
         : (formData.maintenanceTypes && formData.maintenanceTypes.length > 0 ? formData.maintenanceTypes : []);
       
       // Check if maintenance types are selected (mandatory field)
       if (!maintenanceTypes || maintenanceTypes.length === 0) {
         dispatch(showToast({ 
           message: 'Please select at least one Maintenance Type before saving the checklist', 
           type: 'danger' 
         }));
         return;
       }
       
       // Update form data with the maintenance type
       updateFormField('maintenanceTypes', maintenanceTypes);
       updateFormField('maintenanceFrequency', selectedMaintenanceType);
       
       // Use the same data structure as image capture modals (exact same pattern)
       const modalData = getFormDataForModals();
       
       // console.log('handleSaveChecklist - modalData:', modalData);
       // console.log('handleSaveChecklist - modalData.ToolID:', modalData.ToolID);
       // console.log('handleSaveChecklist - modalData.MoldInfoID:', modalData.MoldInfoID);
       
       // Use the same API call logic as the webcam modal (exact same pattern)
       let maintenanceResponse;
       const currentId = maintenanceId || formData.id || 0;
       
       // Use the exact same pattern as the webcam modal - call API directly with filteredData
       maintenanceResponse = await MoldService.saveUpdateMaintenance(modalData);
       
       if (maintenanceResponse && (maintenanceResponse.ID || maintenanceResponse.id || maintenanceResponse.maintenanceID || maintenanceResponse.response?.maintenanceID || maintenanceResponse.response?.id)) {
         const finalMaintenanceId = maintenanceResponse.ID || maintenanceResponse.id || maintenanceResponse.maintenanceID || maintenanceResponse.response?.maintenanceID || maintenanceResponse.response?.id;
         // console.log('Final maintenance ID:', finalMaintenanceId);
         
         // Update the form data with the new maintenance ID
         if (currentId == 0 && finalMaintenanceId) {
           setFormData(prevData => ({
             ...prevData,
             id: finalMaintenanceId
           }));
           // Also update the parent component
           onChanges({
             ...formData,
             id: finalMaintenanceId
           });
         }
         
         // After successful maintenance save, call the activity API
         let activityResponse;
         
         // Always map the current checklist responses to API fields
         const activityData = mapChecklistToAPI(checklistResponses, selectedMaintenanceType);
         activityData.MaintenanceId = finalMaintenanceId;
         
         // Get MoldInfoID from the current form data
         const moldInfoID = formData.moldInfoId || formData.moldInfoID || 
                           data?.moldMaintenanceData?.moldInfoID || 
                           data?.moldInfoID || 
                           dataToSave?.moldInfoID;
         activityData.MoldInfoID = moldInfoID;
         
         if (maintenanceActivityCalled && savedMaintenanceActivityData) {
           // If already called before, merge current checklist data with previously saved data
           activityData.MaintenanceId = savedMaintenanceActivityData.MaintenanceId || savedMaintenanceActivityData.maintenanceId || finalMaintenanceId;
           activityData.MoldInfoID = savedMaintenanceActivityData.MoldInfoID || savedMaintenanceActivityData.moldInfoID || moldInfoID;
           
           // For subsequent calls, we need to include the ID from the first response to update the existing record
           const savedId = savedMaintenanceActivityData.Id || savedMaintenanceActivityData.id || 
                          savedMaintenanceActivityData.response?.Id || savedMaintenanceActivityData.response?.id;
           if (savedId) {
             activityData.Id = savedId;
           } else {
           }
           
           // Merge current checklist data with previously saved data to preserve all fields
           const mergedActivityData = {
             ...savedMaintenanceActivityData, // Include all previously saved data
             ...activityData, // Override with current checklist data
             // Ensure essential fields are preserved
             MaintenanceId: activityData.MaintenanceId,
             MoldInfoID: activityData.MoldInfoID,
             Id: savedId // Use the extracted saved ID
           };
           
           activityResponse = await MoldService.saveUpdateMaintenanceActivity(mergedActivityData);
         } else {
           // First time calling, make the API call
           activityResponse = await MoldService.saveUpdateMaintenanceActivity(activityData);
           
           // Save the response data for future use - ensure we capture the ID properly
           setMaintenanceActivityCalled(true);
           setSavedMaintenanceActivityData(activityResponse);
           
         }
   
         // Show success toast
         dispatch(showToast({ 
           message: `${selectedMaintenanceType} Maintenance Checklist saved successfully!`, 
           type: 'success' 
         }));
   
    // Save the checklist responses to form data
    const checklistData = {
      maintenanceType: selectedMaintenanceType,
      checklistResponses: checklistResponses,
      completedDate: new Date().toISOString()
    };
    
    updateFormField('maintenanceChecklist', checklistData);
         
         // Mark this maintenance type as saved (for showing eye icon button)
         // Store the maintenance activity ID for later retrieval
         const activityId = activityResponse?.Id || activityResponse?.id || activityResponse?.response?.Id || activityResponse?.response?.id;
         setSavedMaintenanceRecords(prev => ({
           ...prev,
           [selectedMaintenanceType.toLowerCase()]: activityId
         }));
    
    // Close modal
    setShowMaintenanceModal(false);
    setSelectedMaintenanceType('');
    setChecklistResponses({});
       }
   
     } catch (error) {
       // console.error('Error details:', {
       //   message: error.message,
       //   stack: error.stack,
       //   response: error.response
       // });
       
       dispatch(showToast({ 
         message: `Error saving maintenance checklist: ${error.message}`, 
         type: 'danger' 
       }));
     }
   };
  const handleCancelChecklist = () => {
    // Close modal without saving
    setShowMaintenanceModal(false);
    setSelectedMaintenanceType('');
    setChecklistResponses({});
    setIsViewingSavedRecord(false);
  };

  // Custom confirmation dialog function
  const showConfirmModal = (callback) => {
    setConfirmCallback(() => callback);
    setShowConfirmDialog(true);
  };

  const handleConfirmYes = () => {
    if (confirmCallback) {
      confirmCallback();
    }
    setShowConfirmDialog(false);
    setConfirmCallback(null);
  };

  const handleConfirmNo = () => {
    setShowConfirmDialog(false);
    setConfirmCallback(null);
  };

  // Function to handle viewing saved maintenance records
  const handleViewSavedMaintenance = async (maintenanceType) => {
    try {
      // Set view mode to true (read-only)
      setIsViewingSavedRecord(true);
      
      // Always make a fresh API call to get the latest data
      const activityId = savedMaintenanceRecords[maintenanceType.toLowerCase()];
      
      if (!activityId) {
        dispatch(showToast({ 
          message: `No saved ${maintenanceType} maintenance record found`, 
          type: 'warning' 
        }));
        return;
      }
      
      console.log(`Loading saved ${maintenanceType} maintenance record with ID:`, activityId);
      
      // Call API to get saved maintenance record (always make fresh call)
      const response = await MoldService.getMoldMaintenanceActivityById(activityId);
      console.log(`API response for ${maintenanceType} maintenance record:`, response);
      
      if (response && response.status === 200 && response.response) {
        const savedData = response.response;
        
        // Map the saved data to checklist responses
        const mappedResponses = {};
        
        // Helper function to map API response to UI format
        const mapApiResponse = (value) => {
          if (value === true) return 'Yes';
          if (value === false) return 'No';
          return ''; // For null or undefined values
        };

        // Map Weekly checklist items (using camelCase field names from API response)
        if (maintenanceType === 'Weekly') {
          mappedResponses['Weekly_0'] = mapApiResponse(savedData.performVisualInspection);
          mappedResponses['Weekly_1'] = mapApiResponse(savedData.inspectWaterCirculation);
          mappedResponses['Weekly_2'] = mapApiResponse(savedData.lubricateEjectorPins);
          mappedResponses['Weekly_3'] = mapApiResponse(savedData.lubricateCoreSlides);
          mappedResponses['Weekly_4'] = mapApiResponse(savedData.lubricateOilCoreGuidePins);
          mappedResponses['Weekly_5'] = mapApiResponse(savedData.inspectHydraulicOilLeaks);
          mappedResponses['Weekly_6'] = mapApiResponse(savedData.checkAbnormalNoises);
          mappedResponses['Weekly_7'] = mapApiResponse(savedData.checkClampInjectionLubrication);
          mappedResponses['Weekly_8'] = mapApiResponse(savedData.reviewMoldTipCondition);
          mappedResponses['Weekly_9'] = mapApiResponse(savedData.performSetupInspection);
        }
        // Map Monthly checklist items (using camelCase field names from API response)
        else if (maintenanceType === 'Monthly') {
          mappedResponses['Monthly_0'] = mapApiResponse(savedData.checkWornEdges);
          mappedResponses['Monthly_1'] = mapApiResponse(savedData.inspectMatingPartGaps);
          mappedResponses['Monthly_2'] = mapApiResponse(savedData.checkAirVentsOnSlides);
          mappedResponses['Monthly_3'] = mapApiResponse(savedData.inspectCavitiesForDeposits);
          mappedResponses['Monthly_4'] = mapApiResponse(savedData.inspectSprueBushing);
          mappedResponses['Monthly_5'] = mapApiResponse(savedData.inspectEjectorPinHoles);
          mappedResponses['Monthly_6'] = mapApiResponse(savedData.inspectWaterLines);
          mappedResponses['Monthly_7'] = mapApiResponse(savedData.inspectHydraulicLines);
          mappedResponses['Monthly_8'] = mapApiResponse(savedData.inspectLimitSwitches);
          mappedResponses['Monthly_9'] = mapApiResponse(savedData.performCompleteCleaning);
        }
        
        console.log(`Mapped responses for ${maintenanceType}:`, mappedResponses);
        
        // Update loadedActivities with fresh data for correct ID usage
        setLoadedActivities(savedData);
        
        // Set the checklist responses
        setChecklistResponses(mappedResponses);
        
        // Open the modal with the maintenance type
        setSelectedMaintenanceType(maintenanceType);
        setShowMaintenanceModal(true);
        
        dispatch(showToast({ 
          message: `${maintenanceType} maintenance record loaded successfully`, 
          type: 'success' 
        }));
      } else {
        dispatch(showToast({ 
          message: `Failed to load saved ${maintenanceType} maintenance record`, 
          type: 'danger' 
        }));
      }
      
    } catch (error) {
      console.error(`Error loading saved ${maintenanceType} maintenance record:`, error);
      dispatch(showToast({ 
        message: `Error loading saved maintenance record: ${error.message}`, 
        type: 'danger' 
      }));
    }
  };

  // Issues modal handler functions
  const handleIssuesFoundClick = async () => {
    setShowIssuesModal(true);
    // Reset to initial state with one empty issue
    setIssuesList([{ 
      id: 1, 
      title: '', 
      comments: '', 
      subIssues: [],
      newFutureAction: '',
      futureActionsList: []
    }]);
    // Load categories when modal opens
    getAllCategories();
    
    // Check if we have existing activityIssues data from history page
    let activityIssueId = 0;
    if (loadedActivityIssues && Array.isArray(loadedActivityIssues) && loadedActivityIssues.length > 0) {
      // Use the first activityIssueId from loaded data
      const firstIssue = loadedActivityIssues[0];
      activityIssueId = firstIssue.activityIssueId || firstIssue.ActivityIssueId || 0;
      console.log('Found existing activityIssueId from loaded data:', activityIssueId);
      
      // Store the activityIssueId for this issue
      if (activityIssueId > 0) {
        setIssueActivityIds(prev => ({
          ...prev,
          1: activityIssueId // Store for the first issue (id: 1)
        }));
      }
      
      // Populate the issues list with existing data from loadedActivityIssues
      const mappedIssues = loadedActivityIssues.map((issue, index) => {
        const activityIssueIdValue = issue.activityIssueId || issue.ActivityIssueId || issue.activityId || issue.ActivityId || 0;
        return {
          id: index + 1,
          title: issue.title || issue.Title || '',
          comments: issue.comments || issue.Comments || '',
          subIssues: [],
          newFutureAction: '',
          futureActionsList: [],
          actionImages: getActionImagesForActivityIssue(activityIssueIdValue),
          activityIssueId: activityIssueIdValue
        };
      });
      
      // Update the issues list with loaded data
      setIssuesList(mappedIssues);
      
      // Store activityIssueId for each issue
      const newIssueActivityIds = {};
      loadedActivityIssues.forEach((issue, index) => {
        const issueActivityId = issue.activityIssueId || issue.ActivityIssueId || 0;
        if (issueActivityId > 0) {
          newIssueActivityIds[index + 1] = issueActivityId;
        }
      });
      setIssueActivityIds(prev => ({ ...prev, ...newIssueActivityIds }));
    } else if (savedIssues && savedIssues.activityIssueId && savedIssues.maintenanceId && formData?.id) {
      // If no loadedActivityIssues but we have savedIssues, fetch from API
      try {
        const response = await MoldService.getMoldMaintenanceActivityIssue(
          savedIssues.activityIssueId, 
          savedIssues.maintenanceId
        );
        
        if (response.status === 200 && response.response) {
          // Handle different response structures
          let issuesData = response.response;
          
          // If response is an array, use it directly
          // If response is an object with issues array, extract it
          if (!Array.isArray(issuesData)) {
            if (issuesData.issues && Array.isArray(issuesData.issues)) {
              issuesData = issuesData.issues;
            } else if (issuesData.data && Array.isArray(issuesData.data)) {
              issuesData = issuesData.data;
            } else {
              // If it's a single issue object, wrap it in an array
              issuesData = [issuesData];
            }
          }
          
          // Map the API response to the issuesList format
          const loadedIssues = issuesData.map((issue, index) => {
            const activityIssueIdValue = issue.activityIssueId || issue.ActivityIssueId || issue.activityId || issue.ActivityId || 0;
            
            // Process and deduplicate subIssues
            const subIssuesRaw = (issue.subActivityIssues || issue.SubActivityIssues || []).map((subIssue, subIndex) => {
              const actualActivityId = issue.activityIssueId || issue.ActivityIssueId || issue.activityId || issue.ActivityId;
              const actualSubActivityId = subIssue.subActivityIssueId || subIssue.subActivityId || subIssue.SubActivityId || subIssue.SubActivityIssueId || subIssue.id;
              const uniqueId = `subissue_${actualActivityId || issue.activityIssueId || issue.id || index}_${actualSubActivityId || subIssue.id || subIndex}`;
              
              if (actualActivityId || actualSubActivityId) {
                setSubActionActivityIds(prev => ({
                  ...prev,
                  [uniqueId]: {
                    activityId: actualActivityId,
                    subActivityId: actualSubActivityId,
                    actionImageId: actualActivityId,
                    subActionImageId: actualSubActivityId,
                    title: subIssue.title || subIssue.Title || '',
                    comments: subIssue.comments || subIssue.Comments || ''
                  }
                }));
              }
              
              return {
                id: subIndex + 1,
                uniqueId: uniqueId,
                title: subIssue.title || subIssue.Title || '',
                comments: subIssue.comments || subIssue.Comments || '',
                activityId: actualActivityId,
                subActivityId: actualSubActivityId,
                actionImageId: actualActivityId,
                subActionImageId: actualSubActivityId
              };
            });
            
            // Deduplicate subIssues based on subActivityId, or by title+comments if no ID
            const seenSubActivityIds = new Set();
            const seenContentKeys = new Set(); // Track title+comments combinations for sub-issues without IDs
            const deduplicatedSubIssues = subIssuesRaw.filter(subIssue => {
              // If sub-issue has a valid subActivityId, deduplicate by ID
              if (subIssue.subActivityId && subIssue.subActivityId !== 0) {
                if (seenSubActivityIds.has(subIssue.subActivityId)) {
                  return false; // Skip duplicate
                }
                seenSubActivityIds.add(subIssue.subActivityId);
                return true; // Keep first occurrence
              }
              
              // If no valid subActivityId, deduplicate by title + comments combination
              const contentKey = `${subIssue.title || ''}_${subIssue.comments || ''}`;
              if (seenContentKeys.has(contentKey)) {
                return false; // Skip duplicate
              }
              seenContentKeys.add(contentKey);
              return true; // Keep first occurrence
            });
            
            return {
              id: index + 1,
              title: issue.title || issue.Title || '',
              comments: issue.comments || issue.Comments || '',
              futureActions: issue.futureAction || issue.FutureAction || '',
              categoryId: issue.categoryId || issue.CategoryId || null,
              subIssues: deduplicatedSubIssues,
              actionImages: getActionImagesForActivityIssue(activityIssueIdValue),
              activityIssueId: activityIssueIdValue
            };
          });
          
          setIssuesList(loadedIssues);
          
          // Store activityIssueId for each issue and load future actions
          const moldInfoId = formData?.moldInfoID || formData?.moldMaintenanceData?.moldInfoID;
          if (moldInfoId) {
            issuesData.forEach((issue, index) => {
              const issueActivityIssueId = issue.activityIssueId || issue.ActivityIssueId || 0;
              
              if (issueActivityIssueId > 0) {
                setIssueActivityIds(prev => ({
                  ...prev,
                  [index + 1]: issueActivityIssueId
                }));
                
                // Load future actions for each issue
                loadFutureActionsForIssue(moldInfoId, index + 1, issueActivityIssueId);
              }
            });
          }
          
          // Update loadedActivityIssues so it's available for next time
          setLoadedActivityIssues(issuesData);
          
          // Update activityIssueId for future actions loading
          if (issuesData.length > 0) {
            activityIssueId = issuesData[0].activityIssueId || issuesData[0].ActivityIssueId || 0;
          }
        }
      } catch (error) {
        console.error('Error loading saved issues:', error);
        // Don't show error toast, just continue with empty state
      }
    }
    
    // Load existing future actions with the activityIssueId (0 if not found)
    const moldInfoId = formData?.moldInfoID || formData?.moldMaintenanceData?.moldInfoID;
    if (moldInfoId) {
      if (activityIssueId > 0) {
        // Load future actions for the first issue with the existing activityIssueId
        loadFutureActionsForIssue(moldInfoId, 1);
      } else {
        // No existing activityIssueId, load with 0
        loadFutureActions(moldInfoId, 0);
      }
    }
  };

  const handleAddIssue = () => {
    const newId = Math.max(...issuesList.map(issue => issue.id)) + 1;
    setIssuesList([...issuesList, { 
      id: newId, 
      title: '', 
      comments: '', 
      subIssues: [],
      newFutureAction: '',
      futureActionsList: []
    }]);
  };

  const handleIssueChange = (id, field, value) => {
    setIssuesList(prevIssues => 
      prevIssues.map(issue => 
      issue.id === id ? { ...issue, [field]: value } : issue
      )
    );
  };

  // Handle action images for issues
  const handleIssueActionImages = (issueId, images) => {
    console.log('handleIssueActionImages called for issue:', issueId, 'with images:', images);
    setIssuesList(issuesList.map(issue => 
      issue.id === issueId ? { ...issue, actionImages: images } : issue
    ));
  };

  // Handle capture image button click - show confirmation first
  const handleCaptureImageClick = () => {
    setShowSaveImagesConfirm(true);
  };

  // Handle save images confirmation
  const handleSaveImagesClick = (images) => {
    setPendingImages(images);
    setShowSaveImagesConfirm(true);
  };

  const handleSaveImagesConfirmYes = async () => {
    setShowSaveImagesConfirm(false);
    
    try {
      // Get the current issue data from the "Add Actions" modal
      const currentIssue = newIssue;
      
      // For Actions, we need to map the action type to a category
      // Since "Repair", "Replace", "No Action Taken" are not in the categories array,
      // we'll use a default category or map them appropriately
      let categoryId = null;
      
      // Map Actions dropdown values to categories
      if (currentIssue.title === 'Repair' || currentIssue.title === 'Replace') {
        // These are maintenance-related actions, use "Maintenance Issue" category
        const maintenanceCategory = categories.find(cat => cat.category === 'Maintenance Issue');
        categoryId = maintenanceCategory ? maintenanceCategory.categoryId : 4; // Default to Maintenance Issue
      } else if (currentIssue.title === 'No Action Taken') {
        // This could be any category, use "Other" as default
        const otherCategory = categories.find(cat => cat.category === 'Other');
        categoryId = otherCategory ? otherCategory.categoryId : 8; // Default to Other
      } else {
        // Fallback: try to find in categories array or use default
        const selectedCategory = categories.find(cat => cat.category === currentIssue.title);
        categoryId = selectedCategory ? selectedCategory.categoryId : 8; // Default to Other
      }
      
      // Use the existing uniqueId from the sub-issue if it exists, otherwise generate a new one
      // First try to find by parentIssueId to narrow down the search
      const parentIssue = parentIssueId ? issuesList.find(issue => issue.id === parentIssueId) : null;
      const subIssue = parentIssue?.subIssues?.find(
        sub => sub.title === currentIssue.title && sub.comments === currentIssue.comments
      ) || issuesList
        .flatMap(issue => issue.subIssues || [])
        .find(sub => sub.title === currentIssue.title && sub.comments === currentIssue.comments);
      
      const uniqueSubActionId =
        subIssue?.uniqueId || `subaction_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Check if this issue already has an activityIssueId
      let existingActivityIssueId = 0;
      
      // Only use activityIssueId if this specific issue has one stored
      // Don't use savedIssues.activityIssueId as it belongs to a different issue
      if (parentIssueId && issueActivityIds[parentIssueId]) {
        existingActivityIssueId = issueActivityIds[parentIssueId];
      }
      else {
        // New issue - use 0 for insertion
        existingActivityIssueId = 0;
      }
      
      const storedIdsForSubIssue = subActionActivityIds[uniqueSubActionId];
      // Prioritize subActivityId from the found subIssue (most recent/accurate)
      // Then check storedIds, then fallback to 0
      const existingSubActivityIssueId =
        (subIssue?.subActivityId && subIssue.subActivityId !== 0) ? subIssue.subActivityId :
        (subIssue?.subActivityIssueId && subIssue.subActivityIssueId !== 0) ? subIssue.subActivityIssueId :
        (storedIdsForSubIssue?.subActivityId && storedIdsForSubIssue.subActivityId !== 0) ? storedIdsForSubIssue.subActivityId :
        0;

      // Prepare the API payload with all required fields
      const issueData = {
        activityIssueId: existingActivityIssueId, // Use existing ID if available, otherwise 0 for new issue
        moldInfoID: formData?.moldInfoID,
        maintenanceId: formData?.id || 0,
        title: currentIssue.title, // Selected dropdown value
        categoryId: categoryId ? parseInt(categoryId) : null, // Convert to number
        futureAction: currentIssue.futureActions || '', // Future Action text field
        comments: currentIssue.comments,
        activity: currentIssue.title, // Add the required activity field
        subActivityIssues: [
          {
            subActivityIssueId: existingSubActivityIssueId,
            activityIssueId: existingActivityIssueId, // Use existing activityIssueId if available
            maintenanceId: formData?.id || 0, // Current maintenance ID
            title: currentIssue.title, // Actions dropdown value
            comments: currentIssue.comments || '', // Comments
            futureAction: currentIssue.futureActions || '', // Future Actions
            uniqueId: uniqueSubActionId // Add unique identifier
          }
        ]
      };
      
      // Call saveOrUpdateMoldMaintenanceActivityIssue API
      const response = await MoldService.saveOrUpdateMoldMaintenanceActivityIssue(issueData);
      
      if (response && response.status === 200 && response.response) {
        const responseData = response.response;
        
        // Get the activity IDs from the response
        const activityId = responseData.activityIssueId;
        const subActivityId = responseData.subActivityIssues && responseData.subActivityIssues.length > 0 
          ? responseData.subActivityIssues[0].subActivityIssueId 
          : 0;
        
        // Store the IDs for this specific sub-action using the unique ID
        setSubActionActivityIds(prev => {
          const newEntry = {
            activityId,
            subActivityId,
            actionImageId: activityId,
            subActionImageId: subActivityId,
            title: currentIssue.title,
            comments: currentIssue.comments
          };
          const deterministicKey =
            activityId && subActivityId ? `subissue_${activityId}_${subActivityId}` : null;
          const updated = {
            ...prev,
            [uniqueSubActionId]: newEntry,
            ...(deterministicKey ? { [deterministicKey]: newEntry } : {})
          };
          return updated;
        });
        
        // Update the issuesList with the latest sub-issue info so we can reuse IDs next time
        setIssuesList(prevIssues => {
          return prevIssues.map(issue => {
            if (issue.id !== parentIssueId) {
              return issue;
            }

            const subIssues = issue.subIssues || [];
            
            // Try multiple matching strategies to find the existing sub-issue
            // This is critical to prevent duplicates when Actions are added first, then images are saved
            let matchIndex = -1;
            
            // First, try to match by uniqueId (most reliable if it exists)
            if (uniqueSubActionId) {
              matchIndex = subIssues.findIndex(sub => sub.uniqueId === uniqueSubActionId);
            }
            
            // If not found, try to match by title + comments (for sub-issues added via "Add Actions" button)
            // This is important because when Actions are added first, they might not have subActivityId yet
            if (matchIndex === -1) {
              matchIndex = subIssues.findIndex(
                sub => sub.title === currentIssue.title && sub.comments === currentIssue.comments
              );
            }
            
            // If not found, try to match by subActivityId (if we have it from API response)
            // This is for cases where the sub-issue was already saved to the database
            if (matchIndex === -1 && subActivityId && subActivityId !== 0) {
              matchIndex = subIssues.findIndex(sub => sub.subActivityId === subActivityId);
            }

            const newSubIssue = {
              id:
                matchIndex >= 0
                  ? subIssues[matchIndex].id
                  : subIssues.length > 0
                    ? Math.max(...subIssues.map(s => s.id || 0)) + 1
                    : 1,
              title: currentIssue.title,
              comments: currentIssue.comments,
              uniqueId: uniqueSubActionId,
              activityId,
              subActivityId: subActivityId,
              actionImageId: activityId,
              subActionImageId: subActivityId
            };

            let updatedSubIssues;
            if (matchIndex >= 0) {
              // Update existing sub-issue instead of adding a new one
              updatedSubIssues = [...subIssues];
              updatedSubIssues[matchIndex] = { ...updatedSubIssues[matchIndex], ...newSubIssue };
            } else {
              // Only add if we're absolutely sure it doesn't exist
              // Double-check for duplicates before adding - this is critical to prevent duplicates
              // when Actions are added first, then images are saved
              const duplicateBySubActivityId = subActivityId && subActivityId !== 0 && 
                subIssues.some(sub => sub.subActivityId === subActivityId);
              
              const duplicateByUniqueId = uniqueSubActionId && 
                subIssues.some(sub => sub.uniqueId === uniqueSubActionId);
              
              const duplicateByContent = subIssues.some(
                sub => sub.title === currentIssue.title && sub.comments === currentIssue.comments
              );
              
              const duplicateExists = duplicateBySubActivityId || duplicateByUniqueId || duplicateByContent;
              
              if (duplicateExists) {
                // Don't add - keep existing list
                updatedSubIssues = subIssues;
              } else {
                updatedSubIssues = [...subIssues, newSubIssue];
              }
            }

            return {
              ...issue,
              activityIssueId: activityId,
              subIssues: updatedSubIssues
            };
          });
        });
        
        dispatch(showToast({ 
          message: 'Issue saved successfully', 
          type: 'success' 
        }));
        
        // Update savedIssues with the correct activityIssueId from API response
        setSavedIssues({
          activityIssueId: activityId,
          maintenanceId: formData?.id || 0
        });
        
        // Also store the activityIssueId for this specific parent issue
        if (parentIssueId) {
          setIssueActivityIds(prev => ({
            ...prev,
            [parentIssueId]: activityId
          }));
        }
        
        // Update the issuesList with the activityIssueId so the SegmentWebcamFileUpload component can access it
        // Now open the capture modal
        setShouldOpenCaptureModal(true);
        
      } else {
        dispatch(showToast({ 
          message: 'Failed to save issue', 
          type: 'danger' 
        }));
      }
    } catch (error) {
      dispatch(showToast({ 
        message: 'Error saving issue: ' + error.message, 
        type: 'danger' 
      }));
    }
    
    setPendingImages(null);
  };

  const handleSaveImagesConfirmNo = () => {
    setShowSaveImagesConfirm(false);
    setPendingImages(null);
  };

  // Handle Close Maintenance Record - Direct disable without modal
  const handleCloseMaintenanceRecord = (e) => {
    console.log('=== CLOSE MAINTENANCE RECORD ===');
    
    // Prevent any default behavior and stop propagation
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Disable the entire page directly
    setIsPageDisabled(true);
    
    // Show success message
    dispatch(showToast({
      message: 'Maintenance record closed successfully. Page has been disabled.',
      type: 'success'
    }));
  };



  // New handler functions for future actions
  const handleCaptureImage = (issueId) => {
    // Handle capture image functionality
    console.log('Capture image for issue:', issueId);
    // You can implement the image capture logic here
  };

  const handleAddFutureAction = async (issueId) => {
    const issue = issuesList.find(issue => issue.id === issueId);
    if (!issue || !issue.newFutureAction?.trim()) {
      dispatch(showToast({ 
        message: 'Please enter a future action', 
        type: 'warning' 
      }));
      return;
    }

    // Store the future action text before clearing the field
    const futureActionText = issue.newFutureAction.trim();
    
    console.log('Clearing future action field for issue:', issueId, 'current value:', issue.newFutureAction);
    
    // Clear the input field immediately after clicking Add button using handleIssueChange
    handleIssueChange(issueId, 'newFutureAction', '');

    // Check if this specific issue has an activityIssueId
    const issueActivityId = issueActivityIds[issueId];
    if (!issueActivityId) {
      // First time for this specific issue - show confirmation popup
      setPendingFutureAction({ issueId, futureAction: futureActionText });
      setShowFutureActionConfirm(true);
      return;
    }

    // If activityIssueId exists, directly save future action without confirmation popup
    await saveFutureActionDirectly(issueId, futureActionText, issueActivityId);
  };

  const handleFutureActionConfirmYes = async () => {
    if (!pendingFutureAction) return;

    try {
      const moldInfoId = formData?.moldInfoID || formData?.moldMaintenanceData?.moldInfoID;
      if (!moldInfoId) {
        dispatch(showToast({ 
          message: 'Mold Info ID not found', 
          type: 'danger' 
        }));
        return;
      }

      // First, save the issue to get activityIssueId
      const currentIssue = issuesList.find(issue => issue.id === pendingFutureAction.issueId);
      
      // Find the selected category to get CategoryId (same logic as in handleSaveIssues)
      const selectedCategory = categories.find(cat => cat.category === currentIssue?.title);
      const categoryId = selectedCategory ? selectedCategory.categoryId : null;
      
      const issueData = {
        activityIssueId: 0, // New issue
        moldInfoID: moldInfoId,
        maintenanceId: formData?.id || 0,
        title: currentIssue?.title || 'Issue', // Use current issue title
        categoryId: categoryId, // Use mapped categoryId
        futureAction: '',
        comments: currentIssue?.comments || '',
        subActivityIssues: []
      };

      console.log('Saving issue to get activityIssueId:', issueData);
      const issueResponse = await MoldService.saveOrUpdateMoldMaintenanceActivityIssue(issueData);
      
      if (issueResponse && issueResponse.status === 200 && issueResponse.response?.activityIssueId) {
        const activityIssueId = issueResponse.response.activityIssueId;
        console.log('Got activityIssueId:', activityIssueId);
        
        // Store the activityIssueId for this specific issue
        setIssueActivityIds(prev => ({
          ...prev,
          [pendingFutureAction.issueId]: activityIssueId
        }));
        
        // Mark this issue as saved to avoid duplicate API calls
        setSavedIssuesTracker(prev => ({
          ...prev,
          [pendingFutureAction.issueId]: true
        }));
        
        // Now save the future action with the activityIssueId
        await saveFutureActionWithActivityId(moldInfoId, pendingFutureAction.futureAction, 'Open', activityIssueId);
        
        // Update local state
        setIssuesList(prevIssues => prevIssues.map(issue => {
          if (issue.id === pendingFutureAction.issueId) {
            const newAction = {
              action: pendingFutureAction.futureAction,
              status: 'Open',
              notedDate: new Date().toISOString().split('T')[0],
              futureActionId: activityIssueId
            };
            return {
              ...issue,
              futureActionsList: [...(issue.futureActionsList || []), newAction],
              newFutureAction: '' // Clear the input field
            };
          }
          return issue;
        }));

        dispatch(showToast({ 
          message: 'Issue and future action saved successfully', 
          type: 'success' 
        }));

        // Refresh future actions for this specific issue
        console.log(`handleFutureActionConfirmYes: About to call loadFutureActionsForIssue for issue ${pendingFutureAction.issueId} with activityIssueId ${activityIssueId}`);
        await loadFutureActionsForIssue(moldInfoId, pendingFutureAction.issueId, activityIssueId);
      } else {
        throw new Error('Failed to save issue');
      }
    } catch (error) {
      console.error('Error saving issue and future action:', error);
      dispatch(showToast({ 
        message: 'Failed to save issue and future action', 
        type: 'danger' 
      }));
    } finally {
      setShowFutureActionConfirm(false);
      setPendingFutureAction(null);
    }
  };

  const handleFutureActionConfirmNo = () => {
    setShowFutureActionConfirm(false);
    setPendingFutureAction(null);
  };

  const saveFutureActionDirectly = async (issueId, futureAction, activityIssueId) => {
    try {
      const moldInfoId = formData?.moldInfoID || formData?.moldMaintenanceData?.moldInfoID;
      if (!moldInfoId) {
        dispatch(showToast({ 
          message: 'Mold Info ID not found', 
          type: 'danger' 
        }));
        return;
      }

      // Use the provided activityIssueId for this specific issue
      await saveFutureActionWithActivityId(moldInfoId, futureAction, 'Open', activityIssueId);
      
      // Update local state
      setIssuesList(prevIssues => prevIssues.map(issue => {
        if (issue.id === issueId) {
          const newAction = {
            action: futureAction,
            status: 'Open',
            notedDate: new Date().toISOString().split('T')[0],
            futureActionId: activityIssueId
          };
          return {
            ...issue,
            futureActionsList: [...(issue.futureActionsList || []), newAction],
            newFutureAction: '' // Clear the input field
          };
        }
        return issue;
      }));

      // Refresh future actions for this specific issue
      console.log(`saveFutureActionDirectly: About to call loadFutureActionsForIssue for issue ${issueId} with activityIssueId ${activityIssueId}`);
      await loadFutureActionsForIssue(moldInfoId, issueId, activityIssueId);
    } catch (error) {
      console.error('Error saving future action:', error);
    }
  };

  const saveFutureActionWithActivityId = async (moldInfoId, futureAction, status = 'Open', activityIssueId) => {
    try {
      const maintenanceId = formData?.id || 0;
      const payload = {
        MoldInfoID: moldInfoId,
        FutureAction: futureAction,
        Status: status,
        MaintenanceId: maintenanceId,
        ActivityIssueId: activityIssueId
      };
      
      console.log('Saving future action with activityIssueId:', payload);
      const response = await MoldService.saveMoldMaintenanceFutureAction(payload);
      
      if (response && response.status === 200) {
        console.log('Future action saved successfully:', response.response);
        dispatch(showToast({ 
          message: 'Future action saved successfully', 
          type: 'success' 
        }));
        
        // Refresh the future actions data for this specific issue
        const issueId = Object.keys(issueActivityIds).find(id => issueActivityIds[id] === activityIssueId);
        if (issueId) {
          await loadFutureActionsForIssue(moldInfoId, parseInt(issueId));
        }
        return response.response;
      } else {
        throw new Error('Failed to save future action');
      }
    } catch (error) {
      console.error('Error saving future action:', error);
      dispatch(showToast({ 
        message: 'Failed to save future action', 
        type: 'danger' 
      }));
      throw error;
    }
  };

  const handleFutureActionStatusChange = async (issueId, actionIndex, newStatus) => {
    // Find the issue and action to update
    const issue = issuesList.find(issue => issue.id === issueId);
    if (!issue) {
      console.error('Issue not found:', issueId);
      return;
    }

    // Get the action to update from the issue's futureActionsList
    const actionToUpdate = issue.futureActionsList[actionIndex];
    if (!actionToUpdate) {
      console.error('Action not found at index:', actionIndex);
      return;
    }

    // Update local state immediately for better UX
    setIssuesList(issuesList.map(issue => {
      if (issue.id === issueId) {
        const updatedActions = [...(issue.futureActionsList || [])];
        updatedActions[actionIndex] = {
          ...updatedActions[actionIndex],
          status: newStatus
        };
        return {
          ...issue,
          futureActionsList: updatedActions
        };
      }
      return issue;
    }));

    try {
      console.log('Saving status change for future action:', actionToUpdate);
      console.log('New status:', newStatus);
      
      // Prepare payload for the API call
      const activityIssueId = issueActivityIds[issueId] || 0;
      const payload = {
        MoldInfoID: formData?.moldInfoID || formData?.moldInfoId || 1,
        FutureAction: actionToUpdate.futureAction || actionToUpdate.action,
        Status: newStatus,
        MaintenanceId: formData?.id || 0,
        FutureActionId: actionToUpdate.futureActionId || actionToUpdate.id,
        ActivityIssueId: activityIssueId
      };

      console.log('API payload:', payload);

      const response = await MoldService.saveMoldMaintenanceFutureAction(payload);
      console.log('Save API response:', response);

      if (response.status === 200) {
        dispatch(showToast({
          message: 'Status updated successfully!',
          type: 'success'
        }));
        console.log('Status updated successfully');
        
        // Refresh the future actions data after successful update
        const moldInfoId = formData?.moldInfoID || formData?.moldInfoId || 1;
        const maintenanceId = formData?.id || 0;
        const activityIssueId = issueActivityIds[issueId] || 0;
        console.log('Refreshing future actions data with moldInfoId:', moldInfoId, 'maintenanceId:', maintenanceId, 'activityIssueId:', activityIssueId);
        
        try {
          const refreshResponse = await MoldService.getMoldMaintenanceFutureActionsByMaintenanceId(moldInfoId, maintenanceId, activityIssueId);
          console.log('Refresh API response for issue', issueId, ':', refreshResponse);
          
          if (refreshResponse.status === 200) {
            // Update only the specific issue's future actions, not the global state
            const mappedFutureActions = (refreshResponse.response || []).map(action => ({
              ...action,
              action: action.futureAction || action.action,
              notedDate: action.notedDate || action.createdDate
            }));
            
            setIssuesList(prevIssues => {
              const updatedIssues = prevIssues.map(prevIssue => {
                if (prevIssue.id === issueId) {
                  console.log(`Updating future actions for issue ${issueId} from ${prevIssue.futureActionsList?.length || 0} to ${mappedFutureActions.length} actions`);
                  return {
                    ...prevIssue,
                    futureActionsList: mappedFutureActions
                  };
                }
                return prevIssue;
              });
              console.log('Updated issues list after status change:', updatedIssues);
              return updatedIssues;
            });
            
            console.log('Future actions data refreshed successfully for issue', issueId);
          } else {
            console.warn('Failed to refresh future actions data for issue', issueId, ':', refreshResponse);
          }
        } catch (refreshError) {
          console.error('Error refreshing future actions data for issue', issueId, ':', refreshError);
        }
      } else {
        console.error('Failed to update status:', response);
        dispatch(showToast({
          message: 'Failed to update status. Please try again.',
          type: 'danger'
        }));
        
        // Revert the local state change if API failed
        setIssuesList(issuesList.map(issue => {
          if (issue.id === issueId) {
            const updatedActions = [...(issue.futureActionsList || [])];
            updatedActions[actionIndex] = {
              ...updatedActions[actionIndex],
              status: actionToUpdate.status
            };
            return {
              ...issue,
              futureActionsList: updatedActions
            };
          }
          return issue;
        }));
      }
    } catch (error) {
      console.error('Error updating status:', error);
      dispatch(showToast({
        message: 'Error updating status: ' + (error.message || 'Unknown error'),
        type: 'danger'
      }));
      
      // Revert the local state change if API failed
      setIssuesList(issuesList.map(issue => {
        if (issue.id === issueId) {
          const updatedActions = [...(issue.futureActionsList || [])];
          updatedActions[actionIndex] = {
            ...updatedActions[actionIndex],
            status: actionToUpdate.status
          };
          return {
            ...issue,
            futureActionsList: updatedActions
          };
        }
        return issue;
      }));
    }
  };

  const handleRemoveFutureAction = (issueId, actionIndex) => {
    setIssuesList(issuesList.map(issue => {
      if (issue.id === issueId) {
        const updatedActions = [...(issue.futureActionsList || [])];
        updatedActions.splice(actionIndex, 1);
        return {
          ...issue,
          futureActionsList: updatedActions
        };
      }
      return issue;
    }));
  };

  // API functions for future actions - DEPRECATED: Use loadFutureActionsForIssue instead
  const loadFutureActions = async (moldInfoId, activityIssueId = 0) => {
    if (!moldInfoId) return;
    
    try {
      setLoadingFutureActions(true);
      const maintenanceId = formData?.id || 0;
      
      console.log('Loading future actions with moldInfoId:', moldInfoId, 'maintenanceId:', maintenanceId, 'activityIssueId:', activityIssueId);
      
      // Use the new API method that includes maintenance ID and activityIssueId
      const response = await MoldService.getMoldMaintenanceFutureActionsByMaintenanceId(moldInfoId, maintenanceId, activityIssueId);
      
      if (response && response.status === 200) {
        // Don't update global state - this function is deprecated
        console.log('loadFutureActions: API response received but not updating global state (deprecated function)');
      }
    } catch (error) {
      console.error('Error loading future actions:', error);
      dispatch(showToast({ 
        message: 'Failed to load future actions', 
        type: 'danger' 
      }));
    } finally {
      setLoadingFutureActions(false);
    }
  };

  // Load future actions for a specific issue
  const loadFutureActionsForIssue = async (moldInfoId, issueId, activityIssueId = null) => {
    console.log(`loadFutureActionsForIssue called for issue ${issueId} with moldInfoId: ${moldInfoId}, activityIssueId: ${activityIssueId}`);
    
    if (!moldInfoId) {
      console.log('No moldInfoId provided, returning early');
      return;
    }
    
    // Use passed activityIssueId or fall back to state
    const finalActivityIssueId = activityIssueId || issueActivityIds[issueId];
    console.log(`Final activityIssueId for issue ${issueId}:`, finalActivityIssueId);
    
    if (!finalActivityIssueId) {
      console.log(`No activityIssueId found for issue ${issueId}, returning early`);
      return;
    }
    
    try {
      setLoadingFutureActions(true);
      const maintenanceId = formData?.id || 0;
      
      console.log(`Making API call for issue ${issueId}: moldInfoId=${moldInfoId}, maintenanceId=${maintenanceId}, activityIssueId=${finalActivityIssueId}`);
      
      const response = await MoldService.getMoldMaintenanceFutureActionsByMaintenanceId(moldInfoId, maintenanceId, finalActivityIssueId);
      console.log(`API response for issue ${issueId}:`, response);
      
      if (response && response.status === 200) {
        console.log(`Successfully loaded future actions for issue ${issueId}:`, response.response);
        
        // Update the specific issue's future actions
        // Map API data structure to local data structure
        const mappedFutureActions = (response.response || []).map(action => ({
          ...action,
          action: action.futureAction || action.action, // Ensure 'action' property exists
          notedDate: action.notedDate || action.createdDate
        }));
        
        console.log(`Mapped future actions for issue ${issueId}:`, mappedFutureActions);
        
        setIssuesList(prevIssues => {
          const updatedIssues = prevIssues.map(issue => {
            if (issue.id === issueId) {
              console.log(`Updating future actions for issue ${issueId} from ${issue.futureActionsList?.length || 0} to ${mappedFutureActions.length} actions`);
              return {
                ...issue,
                futureActionsList: mappedFutureActions
                // Preserve the current newFutureAction field (don't overwrite it)
              };
            }
            return issue;
          });
          console.log('Updated issues list after loadFutureActionsForIssue:', updatedIssues);
          return updatedIssues;
        });
      } else {
        console.log(`Failed to load future actions for issue ${issueId}:`, response);
      }
    } catch (error) {
      console.error('Error loading future actions for issue:', error);
      dispatch(showToast({ 
        message: 'Failed to load future actions for this issue', 
        type: 'danger' 
      }));
    } finally {
      setLoadingFutureActions(false);
    }
  };

  const saveFutureAction = async (moldInfoId, futureAction, status = 'Open') => {
    try {
      const maintenanceId = formData?.id || 0;
      const payload = {
        MoldInfoID: moldInfoId,
        FutureAction: futureAction,
        Status: status,
        MaintenanceId: maintenanceId
      };
      
      console.log('Saving future action with maintenance ID:', maintenanceId);
      console.log('Saving future action:', payload);
      const response = await MoldService.saveMoldMaintenanceFutureAction(payload);
      
      if (response && response.status === 200) {
        console.log('Future action saved successfully:', response.response);
        dispatch(showToast({ 
          message: 'Future action saved successfully', 
          type: 'success' 
        }));
        
        // Refresh the future actions data
        await loadFutureActions(moldInfoId, 0);
        return response.response;
      } else {
        throw new Error('Failed to save future action');
      }
    } catch (error) {
      console.error('Error saving future action:', error);
      dispatch(showToast({ 
        message: 'Failed to save future action', 
        type: 'danger' 
      }));
      throw error;
    }
  };


  const handleCancelIssues = () => {
    // Close modal without saving and reset maintenance frequency
    setShowIssuesModal(false);
    setIssuesList([{ 
      id: 1, 
      title: '', 
      comments: '', 
      subIssues: [],
      newFutureAction: '',
      futureActionsList: []
    }]);
    // Reset maintenance frequency to empty since we're canceling
    updateFormField('maintenanceFrequency', '');
  };

  // Add Issues modal handler functions
  const handleActionsClick = (issueId = null) => {
    setParentIssueId(issueId);
    setShowAddIssuesModal(true);
    setNewIssue({ title: '', comments: '' });
  };

  const handleNewIssueChange = (field, value) => {
    setNewIssue(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveNewIssue = () => {
    // Prevent double execution
    if (isSavingNewIssueRef.current) {
      return;
    }
    
    // Check if modal is already closed (prevent execution after modal closes)
    if (!showAddIssuesModal) {
      return;
    }
    
    if (newIssue.title.trim() !== '' || newIssue.comments.trim() !== '') {
      isSavingNewIssueRef.current = true; // Set flag to prevent double execution
      const newId = Math.max(...issuesList.map(issue => issue.id), 0) + 1;
      
      // Find the most recent uniqueId from subActionActivityIds that matches this issue
      const recentIds = Object.keys(subActionActivityIds);
      const matchingId = recentIds.find(id => {
        const storedData = subActionActivityIds[id];
        return storedData && storedData.title === newIssue.title && storedData.comments === newIssue.comments;
      });
      
      const uniqueSubActionId = matchingId || `subaction_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Get the stored IDs if they exist
      const storedIds = matchingId ? subActionActivityIds[matchingId] : null;
      
      const newIssueData = { 
        id: newId, 
        title: newIssue.title, 
        comments: newIssue.comments, 
        subIssues: [], 
        actionImages: newIssue.actionImages || [],
        uniqueId: uniqueSubActionId, // Use the same uniqueId as stored in subActionActivityIds
        // Store the IDs directly in the sub-issue object if available
        activityId: storedIds?.activityId,
        subActivityId: storedIds?.subActivityId,
        actionImageId: storedIds?.actionImageId,
        subActionImageId: storedIds?.subActionImageId
      };
      
      if (parentIssueId) {
        // Add as sub-issue
        setIssuesList(prevIssuesList => {
          return prevIssuesList.map(issue => {
            if (issue.id !== parentIssueId) {
              return issue;
            }
            
            // Check for duplicates before adding
            const existingSubIssues = issue.subIssues || [];
            
            // Check if a sub-issue with the same subActivityId already exists
            const duplicateBySubActivityId = newIssueData.subActivityId && existingSubIssues.some(
              sub => sub.subActivityId === newIssueData.subActivityId && sub.subActivityId !== null && sub.subActivityId !== undefined
            );
            
            // Check if a sub-issue with the same title and comments already exists (for new sub-issues without IDs)
            const duplicateByContent = !newIssueData.subActivityId && existingSubIssues.some(
              sub => sub.title === newIssueData.title && sub.comments === newIssueData.comments
            );
            
            if (duplicateBySubActivityId) {
              console.log('Duplicate sub-issue detected by subActivityId:', newIssueData.subActivityId);
              return issue; // Don't add duplicate
            }
            
            if (duplicateByContent) {
              console.log('Duplicate sub-issue detected by content (title and comments)');
              return issue; // Don't add duplicate
            }
            
            // No duplicate found, add the new sub-issue
            return { ...issue, subIssues: [...existingSubIssues, newIssueData] };
          });
        });
      } else {
        // Add as main issue
        setIssuesList(prevIssuesList => [...prevIssuesList, newIssueData]);
      }
    }
    setShowAddIssuesModal(false);
    setNewIssue({ title: '', comments: '', actionImages: [] });
    setParentIssueId(null);
    // Reset editing state when modal is closed
    setEditingSubIssue(null);
    setEditingSubIssueData({ title: '', comments: '' });
    
    // Reset the flag after a short delay to allow state updates to complete
    setTimeout(() => {
      isSavingNewIssueRef.current = false;
    }, 100);
  };

  const handleCancelNewIssue = () => {
    setShowAddIssuesModal(false);
    setNewIssue({ title: '', comments: '', actionImages: [] });
    setParentIssueId(null);
    // Reset editing state when modal is closed
    setEditingSubIssue(null);
    setEditingSubIssueData({ title: '', comments: '' });
    // Reset the flag
    isSavingNewIssueRef.current = false;
  };

  // Sub-issue editing handler functions
  const handleEditSubIssue = (mainIssueId, subIssueId) => {
    const mainIssue = issuesList.find(issue => issue.id === mainIssueId);
    const subIssue = mainIssue?.subIssues?.find(sub => sub.id === subIssueId);
    
    if (subIssue) {
      setEditingSubIssue({ mainIssueId, subIssueId });
      setEditingSubIssueData({ title: subIssue.title, comments: subIssue.comments });
    }
  };

  const handleSaveSubIssueEdit = () => {
    if (editingSubIssue) {
      setIssuesList(issuesList.map(issue => 
        issue.id === editingSubIssue.mainIssueId 
          ? {
              ...issue,
              subIssues: issue.subIssues.map(sub => 
                sub.id === editingSubIssue.subIssueId 
                  ? { ...sub, title: editingSubIssueData.title, comments: editingSubIssueData.comments }
                  : sub
              )
            }
          : issue
      ));
      
      // Reset editing state
      setEditingSubIssue(null);
      setEditingSubIssueData({ title: '', comments: '' });
    }
  };

  const handleCancelSubIssueEdit = () => {
    setEditingSubIssue(null);
    setEditingSubIssueData({ title: '', comments: '' });
  };

  const handleSubIssueEditChange = (field, value) => {
    setEditingSubIssueData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Toggle accordion for sub-issues
  const toggleSubIssuesAccordion = (issueId) => {
    setExpandedSubIssues(prev => ({
      ...prev,
      [issueId]: !prev[issueId]
    }));
  };

  // Handle save issues directly (no confirmation needed since buttons are only enabled when maintenanceId exists)
  const handleSaveIssues = async () => {
    // Validate maintenance type
    if (!selectedMaintenanceTypes || selectedMaintenanceTypes.length === 0) {
      dispatch(showToast({ 
        message: 'Please select at least one maintenance type before saving issues', 
        type: 'danger' 
      }));
      return;
    }
    
    try {
      if (savedIssues) {
        // Update existing issues
        console.log('Updating existing issues:', savedIssues);
        
        for (const issue of issuesList) {
          // Check if this issue has an existing activityIssueId (from Add future action)
          const existingActivityIssueId = issueActivityIds[issue.id];
          
          // Find the selected category to get CategoryId
          const selectedCategory = categories.find(cat => cat.category === issue.title);
          const categoryId = selectedCategory ? selectedCategory.categoryId : null;
          
          // Only use activityIssueId if this specific issue has one stored
          // If it doesn't exist in issueActivityIds, it's a new issue and should use 0
          const activityIssueIdToUse = existingActivityIssueId || 0;
          
          // Deduplicate subIssues before sending to API to prevent duplicate records
          const subIssuesToSave = issue.subIssues || [];
          const seenSubActivityIds = new Set();
          const seenContentKeys = new Set();
          const deduplicatedSubIssues = subIssuesToSave.filter(subIssue => {
            // If sub-issue has a valid subActivityId, deduplicate by ID
            const subActivityId = subIssue.subActivityId || subIssue.subActivityIssueId;
            if (subActivityId && subActivityId !== 0) {
              if (seenSubActivityIds.has(subActivityId)) {
                return false;
              }
              seenSubActivityIds.add(subActivityId);
              return true;
            }
            
            // If no valid subActivityId, deduplicate by title + comments
            const contentKey = `${subIssue.title || ''}_${subIssue.comments || ''}`;
            if (seenContentKeys.has(contentKey)) {
              return false;
            }
            seenContentKeys.add(contentKey);
            return true;
          });
          
          const issueData = {
            activityIssueId: activityIssueIdToUse, // Use existing ID if available for this issue, otherwise 0 for new issue
            moldInfoID: dataToSave.moldInfoID || dataToSave.moldNumber,
            maintenanceId: savedIssues.maintenanceId,
            title: issue.title, // Selected dropdown value
            categoryId: categoryId, // CategoryId from selected dropdown
            futureAction: issue.futureActions || '', // Future Action text field
            comments: issue.comments,
            subActivityIssues: deduplicatedSubIssues.map(subIssue => {
              const subActivityIssueId = subIssue.subActivityId || subIssue.subActivityIssueId || 0;
              console.log('Sub-issue ID mapping:', {
                subIssue,
                subActivityIssueId,
                subIssue_subActivityId: subIssue.subActivityId,
                subIssue_subActivityIssueId: subIssue.subActivityIssueId
              });
              return {
                subActivityIssueId: subActivityIssueId, // Use actual sub-issue ID if available
                title: subIssue.title,
                comments: subIssue.comments
              };
            })
          };
          
          console.log('Updating issue with data:', issueData);
          console.log('Using activityIssueId from previous calls:', activityIssueIdToUse);
          const response = await MoldService.saveOrUpdateMoldMaintenanceActivityIssue(issueData);
          console.log('Update response:', response);
          
          // Store the activityIssueId for this specific issue if we get a new one
          if (response && response.status === 200 && response.response?.activityIssueId) {
            setIssueActivityIds(prev => ({
              ...prev,
              [issue.id]: response.response.activityIssueId
            }));
            
            // Mark this issue as saved
            setSavedIssuesTracker(prev => ({
              ...prev,
              [issue.id]: true
            }));
          }
        }
        
        dispatch(showToast({ 
          message: 'Issues updated successfully!', 
          type: 'success' 
        }));
        
        // Update savedIssues to show green button with eye icon
        setSavedIssues({
          activityIssueId: savedIssues.activityIssueId,
          maintenanceId: savedIssues.maintenanceId
        });
        
        // Clear loadedActivityIssues so it will be refreshed when modal is reopened
        setLoadedActivityIssues(null);
      } else {
        // Create new issues
        console.log('Creating new issues');
        
        for (const issue of issuesList) {
          // Check if this issue has an existing activityIssueId (from Add future action)
          const existingActivityIssueId = issueActivityIds[issue.id];
          
          // Find the selected category to get CategoryId
          const selectedCategory = categories.find(cat => cat.category === issue.title);
          const categoryId = selectedCategory ? selectedCategory.categoryId : null;
          
          // Deduplicate subIssues before sending to API to prevent duplicate records
          const subIssuesToSave = issue.subIssues || [];
          const seenSubActivityIds = new Set();
          const seenContentKeys = new Set();
          const deduplicatedSubIssues = subIssuesToSave.filter(subIssue => {
            // If sub-issue has a valid subActivityId, deduplicate by ID
            const subActivityId = subIssue.subActivityId || subIssue.subActivityIssueId;
            if (subActivityId && subActivityId !== 0) {
              if (seenSubActivityIds.has(subActivityId)) {
                return false;
              }
              seenSubActivityIds.add(subActivityId);
              return true;
            }
            
            // If no valid subActivityId, deduplicate by title + comments
            const contentKey = `${subIssue.title || ''}_${subIssue.comments || ''}`;
            if (seenContentKeys.has(contentKey)) {
              return false;
            }
            seenContentKeys.add(contentKey);
            return true;
          });
          
          const issueData = {
            activityIssueId: existingActivityIssueId || 0, // Use existing ID if available from Add future action, otherwise 0 for new issue
            moldInfoID: dataToSave.moldInfoID || dataToSave.moldNumber,
            maintenanceId: dataToSave.id,
            title: issue.title, // Selected dropdown value
            categoryId: categoryId, // CategoryId from selected dropdown
            futureAction: issue.futureActions || '', // Future Action text field
            comments: issue.comments,
            subActivityIssues: deduplicatedSubIssues.map(subIssue => {
              const subActivityIssueId = subIssue.subActivityId || subIssue.subActivityIssueId || 0;
              console.log('Sub-issue ID mapping:', {
                subIssue,
                subActivityIssueId,
                subIssue_subActivityId: subIssue.subActivityId,
                subIssue_subActivityIssueId: subIssue.subActivityIssueId
              });
              return {
                subActivityIssueId: subActivityIssueId, // Use actual sub-issue ID if available
                title: subIssue.title,
                comments: subIssue.comments
              };
            })
          };
          
          console.log('Creating issue with data:', issueData);
          console.log('Using activityIssueId from previous calls:', existingActivityIssueId || 0);
          const response = await MoldService.saveOrUpdateMoldMaintenanceActivityIssue(issueData);
          console.log('Create response:', response);
          
          // Store the activityIssueId for this specific issue
          if (response && response.status === 200 && response.response?.activityIssueId) {
            setIssueActivityIds(prev => ({
              ...prev,
              [issue.id]: response.response.activityIssueId
            }));
            
            // Mark this issue as saved
            setSavedIssuesTracker(prev => ({
              ...prev,
              [issue.id]: true
            }));
          }
        }
        
        dispatch(showToast({ 
          message: 'Issues saved successfully!', 
          type: 'success' 
        }));
        
        // Update savedIssues to show green button with eye icon
        // Use the first issue's activityIssueId from the response, or keep existing if available
        const firstIssueActivityId = issuesList.length > 0 && issueActivityIds[issuesList[0].id] 
          ? issueActivityIds[issuesList[0].id] 
          : (savedIssues?.activityIssueId || 0);
        
        setSavedIssues({
          activityIssueId: firstIssueActivityId, // Use first issue's activityIssueId from response
          maintenanceId: dataToSave.id
        });
        
        // Clear loadedActivityIssues so it will be refreshed when modal is reopened
        setLoadedActivityIssues(null);
      }
      
      // Close the modal after successful save
      setShowIssuesModal(false);
      
    } catch (error) {
      console.error('Error saving issues:', error);
      dispatch(showToast({ 
        message: 'Error saving issues: ' + (error.message || 'Unknown error'), 
        type: 'danger' 
      }));
    }
  };

  // Confirm save issues
  const confirmSaveIssues = async () => {
    
    try {
      if (savedIssues) {
        // Update existing issues
        console.log('Updating existing issues:', savedIssues);
        
        for (const issue of issuesList) {
          // Check if this issue has an existing activityIssueId (from Add future action)
          const existingActivityIssueId = issueActivityIds[issue.id];
          
          // Find the selected category to get CategoryId
          const selectedCategory = categories.find(cat => cat.category === issue.title);
          const categoryId = selectedCategory ? selectedCategory.categoryId : null;
          
          // Only use activityIssueId if this specific issue has one stored
          // If it doesn't exist in issueActivityIds, it's a new issue and should use 0
          const activityIssueIdToUse = existingActivityIssueId || 0;
          
          const issueData = {
            activityIssueId: activityIssueIdToUse, // Use existing ID if available for this issue, otherwise 0 for new issue
            moldInfoID: dataToSave.moldInfoID || dataToSave.moldNumber,
            maintenanceId: savedIssues.maintenanceId,
            title: issue.title, // Selected dropdown value
            categoryId: categoryId, // CategoryId from selected dropdown
            futureAction: issue.futureActions || '', // Future Action text field
            comments: issue.comments,
            subActivityIssues: issue.subIssues?.map(subIssue => {
              const subActivityIssueId = subIssue.subActivityId || subIssue.subActivityIssueId || 0;
              console.log('Sub-issue ID mapping:', {
                subIssue,
                subActivityIssueId,
                subIssue_subActivityId: subIssue.subActivityId,
                subIssue_subActivityIssueId: subIssue.subActivityIssueId
              });
              return {
                subActivityIssueId: subActivityIssueId, // Use actual sub-issue ID if available
                title: subIssue.title,
                comments: subIssue.comments
              };
            }) || []
          };
          
          console.log('Updating issue data:', issueData);
          console.log('Using activityIssueId from previous calls:', activityIssueIdToUse);
          
          const issueResponse = await MoldService.saveOrUpdateMoldMaintenanceActivityIssue(issueData);
          console.log('Issue updated successfully:', issueResponse);
          
          // Store the activityIssueId for this specific issue if we get a new one
          if (issueResponse && issueResponse.status === 200 && issueResponse.response?.activityIssueId) {
            setIssueActivityIds(prev => ({
              ...prev,
              [issue.id]: issueResponse.response.activityIssueId
            }));
            
            // Mark this issue as saved
            setSavedIssuesTracker(prev => ({
              ...prev,
              [issue.id]: true
            }));
          }
        }
        
        dispatch(showToast({ 
          message: 'Issues updated successfully!', 
          type: 'success' 
        }));
        setShowIssuesModal(false);
      } else {
        // Create new issues
        // First call saveOrUpdateMoldMaintenance API
        const maintenanceData = {
          ...dataToSave,
          maintenanceTypes: selectedMaintenanceTypes
        };
        
        console.log('Saving maintenance data:', maintenanceData);
        
        // Use filterDataForAPI to ensure consistent data structure and include image paths
        const filteredMaintenanceData = filterDataForAPI ? filterDataForAPI(maintenanceData) : maintenanceData;
        console.log('Filtered maintenance data:', filteredMaintenanceData);
        
        const maintenanceResponse = await MoldService.saveUpdateMaintenance(filteredMaintenanceData);
        
        if (maintenanceResponse.status === 200) {
          console.log('Maintenance saved successfully:', maintenanceResponse);
          
          // Get the maintenance ID from response
          const savedMaintenanceId = maintenanceResponse.response?.id || maintenanceId;
          
          // Now save each issue with its sub-issues
          let firstActivityIssueId = null;
          for (const issue of issuesList) {
            // Check if this issue has an existing activityIssueId (from Add future action)
            const existingActivityIssueId = issueActivityIds[issue.id];
            
            // Find the selected category to get CategoryId
            const selectedCategory = categories.find(cat => cat.category === issue.title);
            const categoryId = selectedCategory ? selectedCategory.categoryId : null;
            
            const issueData = {
              activityIssueId: existingActivityIssueId || 0, // Use existing ID if available from Add future action, otherwise 0 for new issue
              moldInfoID: dataToSave.moldInfoID || dataToSave.moldNumber,
              maintenanceId: savedMaintenanceId,
              title: issue.title, // Selected dropdown value
              categoryId: categoryId, // CategoryId from selected dropdown
              futureAction: issue.futureActions || '', // Future Action text field
              comments: issue.comments,
              subActivityIssues: issue.subIssues?.map(subIssue => ({
                subActivityIssueId: subIssue.subActivityId || subIssue.subActivityIssueId || 0, // Use actual sub-issue ID if available
                title: subIssue.title,
                comments: subIssue.comments
              })) || []
            };
            
            console.log('Saving issue data:', issueData);
            console.log('Using activityIssueId from previous calls:', existingActivityIssueId || 0);
            
            const issueResponse = await MoldService.saveOrUpdateMoldMaintenanceActivityIssue(issueData);
            console.log('Issue saved successfully:', issueResponse);
            
            // Store the activityIssueId for this specific issue
            if (issueResponse && issueResponse.status === 200 && issueResponse.response?.activityIssueId) {
              setIssueActivityIds(prev => ({
                ...prev,
                [issue.id]: issueResponse.response.activityIssueId
              }));
              
              // Mark this issue as saved
              setSavedIssuesTracker(prev => ({
                ...prev,
                [issue.id]: true
              }));
            }
            
            // Store the first activityIssueId for the green button functionality
            if (firstActivityIssueId === null && issueResponse.response?.activityIssueId) {
              firstActivityIssueId = issueResponse.response.activityIssueId;
            }
          }
          
          // Store saved issues data for green button functionality
          if (firstActivityIssueId) {
            setSavedIssues({
              activityIssueId: firstActivityIssueId,
              maintenanceId: savedMaintenanceId
            });
          }
          
          dispatch(showToast({ 
            message: 'Issues saved successfully!', 
            type: 'success' 
          }));
          setShowIssuesModal(false);
        } else {
          dispatch(showToast({ 
            message: 'Failed to save maintenance data', 
            type: 'danger' 
          }));
        }
      }
    } catch (error) {
      console.error('Error saving issues:', error);
      dispatch(showToast({ 
        message: 'Error saving issues: ' + (error.message || 'Unknown error'), 
        type: 'danger' 
      }));
    }
  };

  // Handle viewing saved issues
  const handleViewSavedIssues = async () => {
    // Check if we have loaded activityIssues data from API response
    if (loadedActivityIssues && Array.isArray(loadedActivityIssues) && loadedActivityIssues.length > 0) {
      // Map the loaded activityIssues data to the issuesList format
      const loadedIssues = loadedActivityIssues.map((issue, index) => {
        const activityIssueIdValue = issue.activityIssueId || issue.ActivityIssueId || issue.activityId || issue.ActivityId || 0;
        
        // Process and deduplicate subIssues
        const subIssuesRaw = (issue.subActivityIssues || issue.SubActivityIssues || []).map((subIssue, subIndex) => {
          // Use actual IDs from API response to match imageSegments processing
          const actualActivityId = issue.activityIssueId || issue.ActivityIssueId || issue.activityId || issue.ActivityId;
          const actualSubActivityId = subIssue.subActivityIssueId || subIssue.subActivityId || subIssue.SubActivityId || subIssue.SubActivityIssueId || subIssue.id;
          
          // Create uniqueId that matches the format used in imageSegments processing
          const uniqueId = `subissue_${actualActivityId}_${actualSubActivityId}`;
          
          // Store IDs in subActionActivityIds for lookup
          if (actualActivityId || actualSubActivityId) {
            setSubActionActivityIds(prev => ({
              ...prev,
              [uniqueId]: {
                activityId: actualActivityId,
                subActivityId: actualSubActivityId,
                actionImageId: actualActivityId,
                subActionImageId: actualSubActivityId,
                title: subIssue.title || subIssue.Title || '',
                comments: subIssue.comments || subIssue.Comments || ''
              }
            }));
          }
          
          return {
            id: subIndex + 1,
            uniqueId: uniqueId,
            title: subIssue.title || subIssue.Title || '',
            comments: subIssue.comments || subIssue.Comments || '',
            activityId: actualActivityId,
            subActivityId: actualSubActivityId,
            actionImageId: actualActivityId,
            subActionImageId: actualSubActivityId
          };
        });
        
        // Deduplicate subIssues based on subActivityId, or by title+comments if no ID
        const seenSubActivityIds = new Set();
        const seenContentKeys = new Set(); // Track title+comments combinations for sub-issues without IDs
        const deduplicatedSubIssues = subIssuesRaw.filter(subIssue => {
          // If sub-issue has a valid subActivityId, deduplicate by ID
          if (subIssue.subActivityId && subIssue.subActivityId !== 0) {
            if (seenSubActivityIds.has(subIssue.subActivityId)) {
              return false; // Skip duplicate
            }
            seenSubActivityIds.add(subIssue.subActivityId);
            return true; // Keep first occurrence
          }
          
          // If no valid subActivityId, deduplicate by title + comments combination
          const contentKey = `${subIssue.title || ''}_${subIssue.comments || ''}`;
          if (seenContentKeys.has(contentKey)) {
            console.log('Removing duplicate sub-issue by content (title+comments):', contentKey);
            return false; // Skip duplicate
          }
          seenContentKeys.add(contentKey);
          return true; // Keep first occurrence
        });
        
        return {
          id: index + 1,
          title: issue.title || issue.Title || '',
          comments: issue.comments || issue.Comments || '',
          subIssues: deduplicatedSubIssues,
          actionImages: getActionImagesForActivityIssue(activityIssueIdValue),
          activityIssueId: activityIssueIdValue
        };
      });
      
      setIssuesList(loadedIssues);
      setShowIssuesModal(true);
      
      // Load existing future actions when opening in edit mode
      const moldInfoId = formData?.moldInfoID || formData?.moldMaintenanceData?.moldInfoID;
      if (moldInfoId) {
        // Load future actions for all issues from loaded data
        loadedActivityIssues.forEach((issue, index) => {
          const activityIssueId = issue?.activityIssueId || issue?.ActivityIssueId || 0;
        
        if (activityIssueId > 0) {
            // Store the activityIssueId for this issue
          setIssueActivityIds(prev => ({
            ...prev,
              [index + 1]: activityIssueId
            }));
            // Load future actions for this issue - pass activityIssueId directly
            loadFutureActionsForIssue(moldInfoId, index + 1, activityIssueId);
          }
        });
      }
      return;
    }
    
    // Fallback to API call if no loaded data
    if (!savedIssues) {
      return;
    }
    
    try {
      const response = await MoldService.getMoldMaintenanceActivityIssue(
        savedIssues.activityIssueId, 
        savedIssues.maintenanceId
      );
      
      if (response.status === 200 && response.response) {
        // Handle different response structures
        let issuesData = response.response;
        
        // If response is an array, use it directly
        // If response is an object with issues array, extract it
        if (!Array.isArray(issuesData)) {
          if (issuesData.issues && Array.isArray(issuesData.issues)) {
            issuesData = issuesData.issues;
          } else if (issuesData.data && Array.isArray(issuesData.data)) {
            issuesData = issuesData.data;
          } else {
            // If it's a single issue object, wrap it in an array
            issuesData = [issuesData];
          }
        }
        
        // Map the API response to the issuesList format
        const loadedIssues = issuesData.map((issue, index) => {
          // First, process all subIssues and deduplicate based on subActivityIssueId
          const subIssuesRaw = (issue.subActivityIssues || issue.SubActivityIssues || []).map((subIssue, subIndex) => {
            // Use actual IDs from API response instead of generating random ones
            // Try multiple possible field names for the IDs
            const actualActivityId = issue.activityIssueId || issue.ActivityIssueId || issue.activityId || issue.ActivityId; // Use parent issue's activityIssueId for actionImageId
            const actualSubActivityId = subIssue.subActivityIssueId || subIssue.subActivityId || subIssue.SubActivityId || subIssue.SubActivityIssueId || subIssue.id;
            
            // Create a deterministic unique ID based on the actual IDs from API
            // Use the actual activityIssueId instead of the array index to match imageSegments processing
            const uniqueId = `subissue_${actualActivityId || issue.activityIssueId || issue.id || index}_${actualSubActivityId || subIssue.id || subIndex}`;
            
            // Store IDs in subActionActivityIds for lookup by uniqueId
            if (actualActivityId || actualSubActivityId) {
            setSubActionActivityIds(prev => ({
              ...prev,
              [uniqueId]: {
                  activityId: actualActivityId,
                  subActivityId: actualSubActivityId,
                  actionImageId: actualActivityId, // Use actual activityId as actionImageId
                  subActionImageId: actualSubActivityId, // Use actual subActivityId as subActionImageId
                title: subIssue.title || subIssue.Title || '',
                comments: subIssue.comments || subIssue.Comments || ''
              }
            }));
              
            }
            
            return {
              id: subIndex + 1,
              uniqueId: uniqueId,
              title: subIssue.title || subIssue.Title || '',
              comments: subIssue.comments || subIssue.Comments || '',
              // Store actual IDs directly in the sub-issue object (just like title and comments)
              activityId: actualActivityId,
              subActivityId: actualSubActivityId,
              actionImageId: actualActivityId, // Store directly for easy access
              subActionImageId: actualSubActivityId // Store directly for easy access
            };
          });
          
          // Deduplicate subIssues based on subActivityId, or by title+comments if no ID
          const seenSubActivityIds = new Set();
          const seenContentKeys = new Set(); // Track title+comments combinations for sub-issues without IDs
          const deduplicatedSubIssues = subIssuesRaw.filter(subIssue => {
            // If sub-issue has a valid subActivityId, deduplicate by ID
            if (subIssue.subActivityId && subIssue.subActivityId !== 0) {
              if (seenSubActivityIds.has(subIssue.subActivityId)) {
                return false; // Skip duplicate
              }
              seenSubActivityIds.add(subIssue.subActivityId);
              return true; // Keep first occurrence
            }
            
            // If no valid subActivityId, deduplicate by title + comments combination
            const contentKey = `${subIssue.title || ''}_${subIssue.comments || ''}`;
            if (seenContentKeys.has(contentKey)) {
              return false; // Skip duplicate
            }
            seenContentKeys.add(contentKey);
            return true; // Keep first occurrence
          });
          
          return {
            id: index + 1,
            title: issue.title || issue.Title || '',
            comments: issue.comments || issue.Comments || '',
            futureActions: issue.futureAction || issue.FutureAction || '', // Map Future Action
            categoryId: issue.categoryId || issue.CategoryId || null, // Map CategoryId
            subIssues: deduplicatedSubIssues
          };
        });
        
        setIssuesList(loadedIssues);
        setShowIssuesModal(true);
        
        // Load existing future actions when opening in edit mode
        const moldInfoId = formData?.moldInfoID || formData?.moldMaintenanceData?.moldInfoID;
        if (moldInfoId) {
          // Check if we have activityIssueId from API response
          const firstIssue = issuesData[0];
          const activityIssueId = firstIssue?.activityIssueId || firstIssue?.ActivityIssueId || 0;
          
          if (activityIssueId > 0) {
            // Store the activityIssueId for the first issue
            setIssueActivityIds(prev => ({
              ...prev,
              1: activityIssueId
            }));
            
            // Store all issues' activityIssueIds and load future actions for each
            issuesData.forEach((issue, index) => {
              const issueActivityIssueId = issue.activityIssueId || issue.ActivityIssueId || 0;
              
              if (issueActivityIssueId > 0) {
                setIssueActivityIds(prev => ({
                  ...prev,
                  [index + 1]: issueActivityIssueId
                }));
                
                // Load future actions for each issue - pass activityIssueId directly
                loadFutureActionsForIssue(moldInfoId, index + 1, issueActivityIssueId);
              }
            });
          } else {
            loadFutureActions(moldInfoId, 0);
          }
        }
      } else {
        dispatch(showToast({ 
          message: 'Failed to load saved issues', 
          type: 'danger' 
        }));
      }
    } catch (error) {
      console.error('Error loading saved issues:', error);
      dispatch(showToast({ 
        message: 'Error loading saved issues: ' + (error.message || 'Unknown error'), 
        type: 'danger' 
      }));
    }
  };

  // Handler for multiple maintenance types selection
  const handleMaintenanceTypeToggle = (type) => {
    setSelectedMaintenanceTypes(prev => {
      const newSelection = prev.includes(type) 
        ? prev.filter(t => t !== type)  // Remove if already selected
        : [...prev, type];              // Add if not selected
      
      // Update form data with the new selection
      updateFormField('maintenanceTypes', newSelection);
      return newSelection;
    });
  };

  // Ensure maintenanceTypes is always included in formData for image capture modals
  // const getFormDataForModals = () => {
  //   // Get moldInfoID from multiple possible sources
  //   const moldInfoID = formData.moldInfoId || formData.moldInfoID || 
  //                     data?.moldMaintenanceData?.moldInfoID || 
  //                     data?.moldInfoID || 
  //                     moldReceivingData?.moldInfoID ||
  //                     dataToSave?.moldInfoID;
    
  //   // Get toolID from multiple possible sources
  //   const toolID = formData.toolId || formData.toolID || 
  //                  data?.moldMaintenanceData?.toolID || 
  //                  data?.toolID || 
  //                  moldReceivingData?.toolID ||
  //                  dataToSave?.toolID;
    
  //   const modalData = {
  //     ...formData,
  //     maintenanceTypes: selectedMaintenanceTypes && selectedMaintenanceTypes.length > 0 ? selectedMaintenanceTypes : (selectedMaintenanceType ? [selectedMaintenanceType] : []),
  //     // Ensure essential fields are included from the parent data
  //     moldInfoID: moldInfoID,
  //     toolID: toolID,
  //     id: formData.id || data?.moldMaintenanceData?.id || data?.id || dataToSave?.id || 0
  //   };
    
  //   // console.log('getFormDataForModals - selectedMaintenanceTypes:', selectedMaintenanceTypes);
  //   // console.log('getFormDataForModals - modalData.maintenanceTypes:', modalData.maintenanceTypes);
  //   // console.log('getFormDataForModals - modalData.moldInfoID:', modalData.moldInfoID);
  //   // console.log('getFormDataForModals - modalData.toolID:', modalData.toolID);
  //   // console.log('getFormDataForModals - modalData.id:', modalData.id);
  //   // console.log('getFormDataForModals - moldReceivingData:', moldReceivingData);
  //   // console.log('getFormDataForModals - moldReceivingData?.moldInfoID:', moldReceivingData?.moldInfoID);
  //   // console.log('getFormDataForModals - data?.moldMaintenanceData?.moldInfoID:', data?.moldMaintenanceData?.moldInfoID);
  //   // console.log('getFormDataForModals - data?.moldInfoID:', data?.moldInfoID);
  //   // console.log('getFormDataForModals - dataToSave:', dataToSave);
  //   // console.log('getFormDataForModals - dataToSave?.moldInfoID:', dataToSave?.moldInfoID);
    
  //   // If filterDataForAPI function is available, use it to ensure consistent data structure
  //   if (filterDataForAPI && typeof filterDataForAPI === 'function') {
  //     const filteredData = filterDataForAPI(modalData);
  //     // console.log('getFormDataForModals - filteredData:', filteredData);
  //     // console.log('getFormDataForModals - filteredData.MoldInfoID:', filteredData.MoldInfoID);
  //     // console.log('getFormDataForModals - filteredData.ToolID:', filteredData.ToolID);
  //     // console.log('getFormDataForModals - filteredData.ID:', filteredData.ID);
  //     return filteredData;
  //   }
    
  //   return modalData;
  // };
// Ensure maintenanceTypes is always included in formData for image capture modals
const getFormDataForModals = () => {
   // Get moldInfoID from multiple possible sources with better fallback logic
   const moldInfoID = formData.moldInfoId || formData.moldInfoID || 
                     data?.moldMaintenanceData?.moldInfoID || 
                     data?.moldInfoID || 
                     moldReceivingData?.moldInfoID ||
                     dataToSave?.moldInfoID ||
                     dataToSave?.MoldInfoID;
   
   console.log('getFormDataForModals - moldInfoID sources:');
   console.log('  formData.moldInfoId:', formData.moldInfoId);
   console.log('  formData.moldInfoID:', formData.moldInfoID);
   console.log('  data?.moldMaintenanceData?.moldInfoID:', data?.moldMaintenanceData?.moldInfoID);
   console.log('  data?.moldInfoID:', data?.moldInfoID);
   console.log('  moldReceivingData?.moldInfoID:', moldReceivingData?.moldInfoID);
   console.log('  dataToSave?.moldInfoID:', dataToSave?.moldInfoID);
   console.log('  dataToSave?.MoldInfoID:', dataToSave?.MoldInfoID);
   console.log('  Final moldInfoID:', moldInfoID);
  
  // Get toolID from multiple possible sources with better fallback logic
  const toolID = formData.toolId || formData.toolID || 
                 data?.moldMaintenanceData?.toolID || 
                 data?.toolID || 
                 moldReceivingData?.toolID ||
                 dataToSave?.toolID ||
                 dataToSave?.ToolID;
  
  console.log('getFormDataForModals - toolID sources:');
  console.log('  formData.toolId:', formData.toolId);
  console.log('  formData.toolID:', formData.toolID);
  console.log('  data?.moldMaintenanceData?.toolID:', data?.moldMaintenanceData?.toolID);
  console.log('  data?.toolID:', data?.toolID);
  console.log('  moldReceivingData?.toolID:', moldReceivingData?.toolID);
  console.log('  dataToSave?.toolID:', dataToSave?.toolID);
  console.log('  dataToSave?.ToolID:', dataToSave?.ToolID);
  console.log('  Final toolID:', toolID);
  
  // Get the current maintenance ID
  const maintenanceID = formData.id || data?.moldMaintenanceData?.id || data?.id || dataToSave?.id || 0;
  
   const modalData = {
     ...formData, // Include all form data first
     // Ensure maintenance types are properly handled - use actual maintenance type checkboxes, not Weekly/Monthly frequency
     maintenanceType: selectedMaintenanceTypes && selectedMaintenanceTypes.length > 0 
       ? selectedMaintenanceTypes 
       : (formData.maintenanceTypes && formData.maintenanceTypes.length > 0 ? formData.maintenanceTypes : ['Preventive Maintenance']), // Default fallback
     // Also include MaintenanceType field for validation compatibility
     MaintenanceType: selectedMaintenanceTypes && selectedMaintenanceTypes.length > 0 
       ? selectedMaintenanceTypes 
       : (formData.maintenanceTypes && formData.maintenanceTypes.length > 0 ? formData.maintenanceTypes : ['Preventive Maintenance']), // Default fallback
     // Ensure essential fields are included with proper casing for filterDataForAPI
     toolID: toolID, // filterDataForAPI expects lowercase 'toolID'
     toolId: toolID, // Include both casings for compatibility
     moldInfoID: moldInfoID, // filterDataForAPI expects lowercase 'moldInfoID'  
     moldInfoId: moldInfoID, // Include both casings for compatibility
     id: maintenanceID,
     // Include current maintenance date if not already set
     currentMaintenanceDate: formData.currentMaintenanceDate || new Date().toISOString().split('T')[0],
     // Ensure other essential fields are present
     totalCyclesSinceLastMaint: formData.totalCyclesSinceLastMaint || '0',
     maintenanceIntervalCycles: formData.maintenanceIntervalCycles || '0',
   };
  
  console.log('getFormDataForModals - selectedMaintenanceTypes:', selectedMaintenanceTypes);
  console.log('getFormDataForModals - selectedMaintenanceType:', selectedMaintenanceType);
  console.log('getFormDataForModals - modalData.maintenanceTypes:', modalData.maintenanceTypes);
  console.log('getFormDataForModals - modalData.MaintenanceType:', modalData.MaintenanceType);
  console.log('getFormDataForModals - modalData.moldInfoID:', modalData.moldInfoID);
  console.log('getFormDataForModals - modalData.toolID:', modalData.toolID);
  console.log('getFormDataForModals - modalData.id:', modalData.id);
  console.log('getFormDataForModals - formData object:', formData);
  console.log('getFormDataForModals - data object:', data);
  console.log('getFormDataForModals - dataToSave object:', dataToSave);
  
  // If filterDataForAPI function is available, use it to ensure consistent data structure
  if (filterDataForAPI && typeof filterDataForAPI === 'function') {
    console.log('getFormDataForModals - About to call filterDataForAPI with modalData:', modalData);
    const filteredData = filterDataForAPI(modalData);
    console.log('getFormDataForModals - filteredData:', filteredData);
    console.log('getFormDataForModals - filteredData.MoldInfoID:', filteredData.MoldInfoID);
    console.log('getFormDataForModals - filteredData.ToolID:', filteredData.ToolID);
    console.log('getFormDataForModals - filteredData.ID:', filteredData.ID);
    console.log('getFormDataForModals - filteredData.MaintenanceType:', filteredData.MaintenanceType);
    console.log('getFormDataForModals - Returning filteredData:', filteredData);
    return filteredData;
  }
  
  console.log('getFormDataForModals - No filterDataForAPI function, returning modalData:', modalData);
  return modalData;
};
  const parseDate = dateStr => {
    if (!dateStr) return '';
    const parsedDate = parse(dateStr, 'M/d/yyyy hh:mm:ss a', new Date());
    return format(parsedDate, 'yyyy-MM-dd'); // Ensures YYYY-MM-DD format
  };

  const getAdditionalDocuments = () => {
    const processedAdditional = (processedImageSegments?.AdditionalPicPath || []).filter(doc => {
      return !shouldExcludeFromAdditional(doc.record, doc.category);
    });

    if (processedAdditional.length > 0) {
      return processedAdditional;
    }

    if (!Array.isArray(formData?.imageSegments)) {
      return [];
    }

    const additionalLabelNormalized = normalizeRecordName(formData?.additionalLabel || 'Additional Pictures');
    const extraImagesNormalized = normalizeRecordName('Extra Images');
    const defaultAdditionalNormalized = normalizeRecordName('Additional Pictures');

    return formData.imageSegments
      .filter(segment => {
        if (shouldExcludeFromAdditional(segment.record, segment.category)) {
          return false;
        }
        const segmentRecordNormalized = normalizeRecordName(segment.record);
        if (!segment.record || segment.record.trim() === '') {
          return true;
        }
        if (!getFieldNameForRecord(segment.record)) {
          return true;
        }
        if (segmentRecordNormalized === additionalLabelNormalized) {
          return true;
        }
        if (segmentRecordNormalized === defaultAdditionalNormalized) {
          return true;
        }
        if (segmentRecordNormalized === extraImagesNormalized) {
          return true;
        }
        return false;
      })
      .map(segment => ({
        id: segment.id,
        name: `${segment.segment || segment.record || 'Image'}_${segment.id || ''}`,
        path: segment.imageUrl,
        fileName: `${segment.segment || segment.record || 'Image'}_${segment.id || ''}`,
        filePath: segment.imageUrl,
        record: segment.record,
        category: segment.category,
        actionImageId: segment.actionImageId,
        subActionImageId: segment.subActionImageId
      }));
  };

  return (
    <>
    <form className="needs-validation" noValidate style={{ opacity: isPageDisabled ? 0.5 : 1, pointerEvents: isPageDisabled ? 'none' : 'auto' }}>
      <div className="form-row">
        <div className="m-2">
          {/* Maintenance Types - OLD RADIO BUTTONS (COMMENTED OUT) */}
          {/* 
          <h5 className="header-five">Maintenance Types</h5>
          <div className="row m-2">
            <label className="mb-2">
              Select Maintenance Type <span className="text-danger">*</span>
            </label>
            <div className="col-lg-3 col-md-3 col-sm-12">
              <input
                type="radio"
                className="btn-check"
                name="btnradioMaintType"
                id="btnradioPrevnt"
                defaultChecked=""
                checked={formData.maintenanceType === 'Preventive Maintenance'}
                value="Preventive Maintenance"
                onChange={e => {
                  updateFormField('maintenanceType', e.target.value);
                }}
                required
              />
              <label className="btn btn-outline-primary w-100" htmlFor="btnradioPrevnt">
                Preventive Maintenance
              </label>
            </div>

            <div className="col-lg-3 col-md-3 col-sm-12">
              <input
                type="radio"
                className="btn-check"
                name="btnradioMaintType"
                id="btnradioBreak"
                checked={formData.maintenanceType === 'Breakdown Maintenance'}
                value="Breakdown Maintenance"
                onChange={e => {
                  updateFormField('maintenanceType', e.target.value);
                }}
              />
              <label className="btn btn-outline-primary w-100" htmlFor="btnradioBreak">
                Breakdown Maintenance
              </label>
            </div>

            <div className="col-lg-3 col-md-3 col-sm-12">
              <input
                type="radio"
                className="btn-check"
                name="btnradioMaintType"
                id="btnradioModify"
                checked={formData.maintenanceType === 'Mold Modificaion'}
                value="Mold Modificaion"
                onChange={e => {
                  updateFormField('maintenanceType', e.target.value);
                }}
              />
              <label className="btn btn-outline-primary w-100" htmlFor="btnradioModify">
                Mold Modificaion
              </label>
            </div>
          </div>
          */}

          {/* Maintenance Types - NEW CHECKBOX BUTTONS */}
          <h5 className="header-five">Maintenance Types</h5>
          <div className="row m-2">
            <label className="mb-2">
              Select Maintenance Type(s) <span className="text-danger">*</span>
            </label>
            <div className="col-lg-3 col-md-3 col-sm-12">
              <input
                type="checkbox"
                className="btn-check"
                id="btnCheckPreventive"
                checked={selectedMaintenanceTypes.includes('Preventive Maintenance')}
                onChange={() => handleMaintenanceTypeToggle('Preventive Maintenance')}
              />
              <label className="btn btn-outline-primary w-100" htmlFor="btnCheckPreventive">
                Preventive Maintenance
              </label>
            </div>

            <div className="col-lg-3 col-md-3 col-sm-12">
              <input
                type="checkbox"
                className="btn-check"
                id="btnCheckBreakdown"
                checked={selectedMaintenanceTypes.includes('Breakdown Maintenance')}
                onChange={() => handleMaintenanceTypeToggle('Breakdown Maintenance')}
              />
              <label className="btn btn-outline-primary w-100" htmlFor="btnCheckBreakdown">
                Breakdown Maintenance
              </label>
            </div>

            <div className="col-lg-3 col-md-3 col-sm-12">
              <input
                type="checkbox"
                className="btn-check"
                id="btnCheckModification"
                checked={selectedMaintenanceTypes.includes('Mold Modification')}
                onChange={() => handleMaintenanceTypeToggle('Mold Modification')}
              />
              <label className="btn btn-outline-primary w-100" htmlFor="btnCheckModification">
                Mold Modification
              </label>
            </div>

            <div className="col-lg-3 col-md-3 col-sm-12">
              <input
                type="checkbox"
                className="btn-check"
                id="btnCheckPredictive"
                checked={selectedMaintenanceTypes.includes('Predictive Maintenance')}
                onChange={() => handleMaintenanceTypeToggle('Predictive Maintenance')}
              />
              <label className="btn btn-outline-primary w-100" htmlFor="btnCheckPredictive">
                Predictive Maintenance
              </label>
            </div>
          </div>

          {/* Mold Information*/}
          <div className="mt-4">
            <h5 className="header-five">Mold Information</h5>
          </div>
          <div className="row m-2">
            <div className="col-lg-3 col-md-3 col-sm-12">
              <div className="input-blocks ">
                <label>
                  ToolID <span className="text-danger">*</span>
                </label>
                <input
                  placeholder=""
                  type="text"
                  value={moldReceivingData.toolID}
                  disabled={true}
                  className="form-control"
                />
              </div>
            </div>
            <div className="col-lg-3 col-md-3 col-sm-12">
              <div className="input-blocks">
                <label>
                  Mold Number <span className="text-danger">*</span>
                </label>

                <input
                  placeholder=""
                  type="text"
                  value={moldReceivingData.moldNumber}
                  disabled={true}
                  className="form-control"
                />
              </div>
            </div>

            <div className="col-lg-3 col-md- col-sm-12">
              <div className="input-blocks">
                <label>
                  Mold Description
                </label>
                <input
                  type="text"
                  value={moldReceivingData.moldDescription}
                  disabled={true}
                  className="form-control"
                />
              </div>
            </div>

            <div className="col-lg-3 col-md-3 col-sm-12">
              <div className="input-blocks">
                <label>
                  Part
                </label>
                <div className="d-flex align-items-start gap-2">
                  <div className="flex-grow-1">
                    <input
                      type="text"
                      className="form-control"
                      value={
                        (formData.parts || []).length > 0
                          ? `${(formData.parts || []).length} part${(formData.parts || []).length > 1 ? 's' : ''} selected`
                          : 'No parts selected'
                      }
                      readOnly
                      disabled
                      style={{ cursor: 'not-allowed', backgroundColor: '#e9ecef' }}
                    />
                  </div>
                  <Button
                    variant="outline-secondary"
                    className="d-flex align-items-center justify-content-center mt-1"
                    title="View all parts"
                    type="button"
                    onClick={() => setShowPartsModal(true)}
                    disabled={!(formData.parts || []).length}
                  >
                    <Eye size={16} />
                  </Button>
                </div>
                <div className="invalid-feedback">Please provide a valid Part ID</div>
              </div>
            </div>
          </div>

          <div className="row m-2">
            <div className="col-lg-3 col-md-3 col-sm-12">
              <div className="input-blocks">
                <label>
                  Material Type
                </label>
                <input
                  type="text"
                  value={formData.materialType}
                  disabled={true}
                  className="form-control"
                  placeholder=""
                />
              </div>
            </div>

            <div className="col-lg-3 col-md-3 col-sm-12">
              <div className="input-blocks">
                <label>
                  Cavity
                </label>
                <input
                  type="text"
                  value={formData.cavity}
                  disabled={true}
                  className="form-control"
                  placeholder=""
                />
              </div>
            </div>
          </div>

          {/*Usage Information*/}
          <h5 className="header-five">Usage Information</h5>
          <div className="row m-2">
            <div className="col-lg-3 col-md-3 col-sm-12">
              <div className="input-blocks">
                <label>Total Cycles Since Last Maintenance</label>
                <input
                  type="number"
                  value={formData.totalCyclesSinceLastMaint}
                  onChange={e => updateFormField('totalCyclesSinceLastMaint', e.target.value)}
                  className="form-control"
                  placeholder=""
                  disabled={isDisabled}
                />
              </div>
            </div>

            {/* Total Cycles to Date field hidden as requested */}
            <div className="col-lg-3 col-md-3 col-sm-12">
              <div className="input-blocks">
                <label>
                  Last Maintenance Date <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  value={formData.lastMaintenanceDate}
                  onChange={e => updateFormField('lastMaintenanceDate', e.target.value)}
                  className="form-control"
                  placeholder=""
                  disabled={true}
                  required
                />
              </div>
            </div>
          </div>

          {/* Maintenance Interval*/}
          <h5 className="header-five">Maintenance Interval</h5>
          <div className="row m-2">
            <div className="col-lg-3 col-md-3 col-sm-12">
              <div className="input-blocks">
                <label>Maintenance Interval (Cycles)</label>
                <input
                  type="number"
                  value={formData.maintenanceIntervalCycles}
                  onChange={e => updateFormField('maintenanceIntervalCycles', e.target.value)}
                  className="form-control"
                  disabled={isDisabled}
                />
              </div>
            </div>

            <div className="col-lg-3 col-md-3 col-sm-12">
              <div className="input-blocks">
                <label>Current Maintenance Date</label>
                <input
                  type="date"
                  value={formData.currentMaintenanceDate}
                  onChange={e => updateFormField('currentMaintenanceDate', e.target.value)}
                  className="form-control"
                  disabled={isDisabled}
                />
              </div>
            </div>

            <div className="col-lg-3 col-md-3 col-sm-12">
              <div className="input-blocks">
                <label>Current Maintenance Hours</label>
                <input
                  type="number"
                  value={formData.currentMaintenanceHours}
                  onChange={e => {
                    const value = e.target.value;
                    // Limit to very safe database range to prevent overflow
                    if (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= 10000)) {
                      updateFormField('currentMaintenanceHours', value);
                    }
                  }}
                  className="form-control"
                  placeholder="Enter current hours (max 10,000)"
                  max="10000"
                  disabled={isDisabled}
                />
              </div>
            </div>
            <div className="col-lg-3 col-md-3 col-sm-12">
              <div className="input-blocks">
                <label>Total Maintenance Hours</label>
                <input
                  type="number"
                  value={formData.totalMaintenanceHours}
                  onChange={e => updateFormField('totalMaintenanceHours', e.target.value)}
                  className="form-control"
                  disabled={true}
                  readOnly
                />
              </div>
            </div>
          </div>

          {/*Maintenance Activities Performed*/}
          <h5 className="header-five">Maintenance Activities Performed</h5>
          
          {/* Toggle Button Container */}
          <div className="row m-2">
            <div className="col-lg-12 col-md-12 col-sm-12">
              <div className="input-blocks">
                <label>Maintenance Frequency</label>
                <div className="btn-group d-flex flex-wrap gap-3" role="group" aria-label="Maintenance frequency toggle" style={{ maxWidth: '100%' }}>
                  {/* Weekly Maintenance Button */}
                  {savedMaintenanceRecords.weekly ? (
                    <button
                      type="button"
                      className="btn btn-outline-success btn-sm d-flex align-items-center gap-2"
                      onClick={() => handleViewSavedMaintenance('Weekly')}
                      style={{ fontSize: '0.875rem', padding: '0.375rem 0.75rem', whiteSpace: 'nowrap' }}
                      title="View saved Weekly Maintenance record"
                    >
                      <FaEye />
                      Weekly Maintenance Records
                    </button>
                  ) : (
                    <>
                <input
                    type="radio"
                    className="btn-check"
                    name="maintenanceFrequency"
                    id="btnWeekly"
                    value="Weekly"
                    checked={formData.maintenanceFrequency === 'Weekly'}
                    onChange={e => {
                      handleMaintenanceTypeClick('Weekly');
                    }}
                    disabled={isDisabled}
                  />
                  <label className="btn btn-outline-primary btn-sm" htmlFor="btnWeekly" style={{ fontSize: '0.875rem', padding: '0.375rem 0.75rem', whiteSpace: 'nowrap' }}>
                    Weekly Maintenance Records
                  </label>
                    </>
                  )}
                  
                  {/* Monthly Maintenance Button */}
                  {savedMaintenanceRecords.monthly ? (
                    <button
                      type="button"
                      className="btn btn-outline-success btn-sm d-flex align-items-center gap-2"
                      onClick={() => handleViewSavedMaintenance('Monthly')}
                      style={{ fontSize: '0.875rem', padding: '0.375rem 0.75rem', whiteSpace: 'nowrap' }}
                      title="View saved Monthly Maintenance record"
                    >
                      <FaEye />
                      Monthly Maintenance Records
                    </button>
                  ) : (
                    <>
                  <input
                    type="radio"
                    className="btn-check"
                    name="maintenanceFrequency"
                    id="btnMonthly"
                    value="Monthly"
                    checked={formData.maintenanceFrequency === 'Monthly'}
                    onChange={e => {
                      handleMaintenanceTypeClick('Monthly');
                    }}
                    disabled={isDisabled}
                  />
                  <label className="btn btn-outline-primary btn-sm" htmlFor="btnMonthly" style={{ fontSize: '0.875rem', padding: '0.375rem 0.75rem', whiteSpace: 'nowrap' }}>
                    Monthly Maintenance Records
                  </label>
                    </>
                  )}
                  
                  <input
                    type="radio"
                    className="btn-check"
                    name="maintenanceFrequency"
                    id="btnIssues"
                    value="Issues"
                    checked={formData.maintenanceFrequency === 'Issues'}
                    onChange={e => {
                      if (savedIssues) {
                        handleViewSavedIssues();
                      } else {
                        handleIssuesFoundClick();
                      }
                    }}
                    disabled={isDisabled && !savedIssues} // Allow clicking if there are saved issues (read-only view)
                  />
                  <label 
                    className={`btn btn-sm ${savedIssues ? 'btn-outline-success' : 'btn-outline-warning'}`} 
                    htmlFor={isDisabled && savedIssues ? undefined : 'btnIssues'} // Don't use htmlFor when disabled to allow custom click
                    onClick={e => {
                      // If disabled but has saved issues, allow viewing in read-only mode
                      if (isDisabled && savedIssues) {
                        e.preventDefault();
                        handleViewSavedIssues();
                      }
                    }}
                    style={{ 
                      fontSize: '0.875rem', 
                      padding: '0.375rem 0.75rem', 
                      whiteSpace: 'nowrap',
                      cursor: isDisabled && savedIssues ? 'pointer' : 'default'
                    }}
                  >
                    {savedIssues ? (
                      <>
                        <FaEye className="me-1" />
                        Other Maintenance Records
                      </>
                    ) : (
                      'Other Maintenance Records'
                    )}
                  </label>
                 
                </div>
              </div>
            </div>
          </div>
          {/* <div
            className="row m-2 d-flex"
            style={{ backgroundColor: '#f0f0f0', borderRadius: '8px', padding: '16px' }}
          >
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="row m-2 d-flex">
                <div className="col-lg-6 col-md-6 col-sm-12">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={formData.cleaning}
                      onChange={e => updateFormField('cleaning', e.target.checked)}
                      id="btnCleaning"
                      disabled={isDisabled || !maintenanceButtonStates.cleaning}
                    />

                    <label className={`form-check-label ${!maintenanceButtonStates.cleaning ? 'text-muted' : ''}`} htmlFor="btnCleaning">
                      Cleaning
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={formData.spray}
                      onChange={e => updateFormField('spray', e.target.checked)}
                      id="btnSpray"
                      disabled={isDisabled || !maintenanceButtonStates.spray}
                    />
                    <label className={`form-check-label ${!maintenanceButtonStates.spray ? 'text-muted' : ''}`} htmlFor="btnSpray">
                      Spray
                    </label>
                  </div>
                </div>

                <div className="col-lg-6 col-md-6 col-sm-12">
                  <div className="input-blocks">
                    <label>Comments 1</label>
                    <input
                      type="text"
                      value={formData.comments1}
                      onChange={e => updateFormField('comments1', e.target.value)}
                      className="form-control"
                      disabled={isDisabled}
                    />
                  </div>
                </div>
              </div>

              <div className="row m-2 d-flex">
                <div className="col-lg-6 col-md-6 col-sm-12">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={formData.inspection}
                      onChange={e => updateFormField('inspection', e.target.checked)}
                      id="btnInspec"
                      disabled={isDisabled || !maintenanceButtonStates.inspection}
                    />
                    <label className={`form-check-label ${!maintenanceButtonStates.inspection ? 'text-muted' : ''}`} htmlFor="btnInspec">
                      Inspection
                    </label>
                  </div>
                </div>

                <div className="col-lg-6 col-md-6 col-sm-12">
                  <div className="input-blocks">
                    <label>Comments 2</label>
                    <input
                      type="text"
                      value={formData.comments2}
                      onChange={e => updateFormField('comments2', e.target.value)}
                      className="form-control"
                      disabled={isDisabled}
                    />
                  </div>
                </div>
              </div>

              <div className="row m-2 d-flex">
                <div className="col-lg-6 col-md-6 col-sm-12">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={formData.lubrication}
                      onChange={e => updateFormField('lubrication', e.target.checked)}
                      id="btnLubri"
                      disabled={isDisabled || !maintenanceButtonStates.lubrication}
                    />
                    <label className={`form-check-label ${!maintenanceButtonStates.lubrication ? 'text-muted' : ''}`} htmlFor="btnLubri">
                      Lubrication
                    </label>
                  </div>
                </div>

                <div className="col-lg-6 col-md-6 col-sm-12">
                  <div className="input-blocks">
                    <label>Comments 3</label>
                    <input
                      type="text"
                      value={formData.comments3}
                      onChange={e => updateFormField('comments3', e.target.value)}
                      className="form-control"
                      disabled={isDisabled}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="row m-2 d-flex">
                <div className="col-lg-6 col-md-6 col-sm-12">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={formData.componentReplacement}
                      onChange={e => updateFormField('componentReplacement', e.target.checked)}
                      id="btnReplcmnt"
                      disabled={isDisabled || !maintenanceButtonStates.componentReplacement}
                    />
                    <label className={`form-check-label ${!maintenanceButtonStates.componentReplacement ? 'text-muted' : ''}`} htmlFor="btnReplcmnt">
                      Component Replacement
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={formData.componentsReplaced}
                      onChange={e => updateFormField('componentsReplaced', e.target.checked)}
                      id="btnReplcd"
                      disabled={isDisabled || !maintenanceButtonStates.componentsReplaced}
                    />
                    <label className={`form-check-label ${!maintenanceButtonStates.componentsReplaced ? 'text-muted' : ''}`} htmlFor="btnReplcd">
                      Components Replaced
                    </label>
                  </div>
                </div>

                <div className="col-lg-6 col-md-6 col-sm-12">
                  <div className="input-blocks">
                    <label>Comments 4</label>
                    <input
                      type="text"
                      value={formData.comments4}
                      onChange={e => updateFormField('comments4', e.target.value)}
                      className="form-control"
                      disabled={isDisabled}
                    />
                  </div>
                </div>
              </div>

              <div className="row m-2 d-flex">
                <div className="col-lg-6 col-md-6 col-sm-12">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={formData.repair}
                      onChange={e => updateFormField('repair', e.target.checked)}
                      id="btnRepair"
                      disabled={isDisabled || !maintenanceButtonStates.repair}
                    />
                    <label className={`form-check-label ${!maintenanceButtonStates.repair ? 'text-muted' : ''}`} htmlFor="btnRepair">
                      Repair
                    </label>
                  </div>
                </div>

                <div className="col-lg-6 col-md-6 col-sm-12">
                  <div className="input-blocks">
                    <label>Details of Repair</label>
                    <input
                      type="text"
                      value={formData.detailsofRepair}
                      onChange={e => updateFormField('detailsofRepair', e.target.value)}
                      className="form-control"
                      disabled={isDisabled}
                    />
                  </div>
                </div>
              </div>

              <div className="row m-2 d-flex">
                <div className="col-lg-6 col-md-6 col-sm-12">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={formData.functionalTest}
                      onChange={e => updateFormField('functionalTest', e.target.checked)}
                      id="btnFnctnl"
                      disabled={isDisabled || !maintenanceButtonStates.functionalTest}
                    />
                    <label className={`form-check-label ${!maintenanceButtonStates.functionalTest ? 'text-muted' : ''}`} htmlFor="btnFnctnl">
                      Functional Test
                    </label>
                  </div>
                </div>

                <div className="col-lg-6 col-md-6 col-sm-12">
                  <div className="input-blocks">
                    <label>Comments 5</label>
                    <input
                      type="text"
                      value={formData.comments5}
                      onChange={e => updateFormField('comments5', e.target.value)}
                      className="form-control"
                      disabled={isDisabled}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div> */}

          {/* Documentation*/}
          <h5 className="header-five">Documentation</h5>
          <div className="row m-2">
            <div className="col-lg-3 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Maintenance Report Document</label>
                <SimpleFileUpload
                  id={MOLD_MAINTENANCE.MAINTENANCE_REPORT_DOCUMENT}
                  onChange={handleFileUpload}
                  value={getDocumentPath(formData.maintenance_Report_Document)?.fileName}
                  filePath={getDocumentPath(formData.maintenance_Report_Document)?.fileUrl}
                  isDisabled={isDisabled}
                  onViewClick={(filePath, fileName) => {
                    setDocumentViewerUrl(filePath);
                    setDocumentViewerName(fileName);
                    setShowDocumentViewer(true);
                  }}
                />
              </div>
            </div>

            <div className="col-lg-3 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Inspection Checklist Document</label>
                <SimpleFileUpload
                  id={MOLD_MAINTENANCE.INSPECTION_CHECKLIST_DOCUMENT}
                  onChange={handleFileUpload}
                  value={getDocumentPath(formData.inspection_Checklist_Document)?.fileName}
                  filePath={getDocumentPath(formData.inspection_Checklist_Document)?.fileUrl}
                  isDisabled={isDisabled}
                  onViewClick={(filePath, fileName) => {
                    setDocumentViewerUrl(filePath);
                    setDocumentViewerName(fileName);
                    setShowDocumentViewer(true);
                  }}
                />
              </div>
            </div>

            <div className="col-lg-3 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Functional Test Results Document</label>
                <SimpleFileUpload
                  id={MOLD_MAINTENANCE.FUNCTIONAL_TEST_RESULTS_DOCUMENT}
                  onChange={handleFileUpload}
                  value={getDocumentPath(formData.functionalTest_Results_Document)?.fileName}
                  filePath={getDocumentPath(formData.functionalTest_Results_Document)?.fileUrl}
                  isDisabled={isDisabled}
                  onViewClick={(filePath, fileName) => {
                    setDocumentViewerUrl(filePath);
                    setDocumentViewerName(fileName);
                    setShowDocumentViewer(true);
                  }}
                />
              </div>
            </div>
          </div>

  {/* Picture Uploads*/}
  <h5 className="header-five">Maintenance Records</h5>
          <div className="row m-2">
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Mold Picture Before Maintenance</label>
                <SegmentWebcamFileUpload
                  id={MOLD_MAINTENANCE.MOLD_PICTURE_BEFORE_MAINTENANCE}
                  toolID={formData?.toolID}
                  moldInfoID={formData?.moldInfoID}
                  buttonLabel="Mold Picture Before Maintenance"
                  maintenanceId={formData?.id || 0}
                  maintenanceData={getFormDataForModals()}
                  onChange={handleFileUpload}
                  documents={convertDocumentsForWebCamFileUpload('MoldPicture_Before_Maintenance')}
                  value={getDocumentPath(formData.moldPicture_Before_Maintenance)?.fileName}
                  filePath={getDocumentPath(formData.moldPicture_Before_Maintenance)?.fileUrl}
                  isDisabled={isDisabled}
                  onImageSegmentUploaded={handleImageSegmentUploaded}
                  onMaintenanceSaved={handleMaintenanceSaved}
                  showManageImages={hasImagesForField('MoldPicture_Before_Maintenance')}
                />
              </div>
            </div>
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Mold Picture After Maintenance</label>
                <SegmentWebcamFileUpload
                  id={MOLD_MAINTENANCE.MOLD_PICTURE_AFTER_MAINTENANCE}
                  toolID={formData?.moldMaintenanceData?.toolID}
                  moldInfoID={formData?.moldInfoID}
                  buttonLabel="Mold Picture After Maintenance"
                  maintenanceId={formData?.id || 0}
                  maintenanceData={getFormDataForModals()}
                  onChange={handleFileUpload}
                  documents={convertDocumentsForWebCamFileUpload('MoldPicture_After_Maintenance')}
                  value={getDocumentPath(formData.moldPicture_After_Maintenance)?.fileName}
                  filePath={getDocumentPath(formData.moldPicture_After_Maintenance)?.fileUrl}
                  isDisabled={isDisabled}
                  onImageSegmentUploaded={handleImageSegmentUploaded}
                  onMaintenanceSaved={handleMaintenanceSaved}
                  showManageImages={hasImagesForField('MoldPicture_After_Maintenance')}
                />
              </div>
            </div>
          </div>
          <div className="row m-2">
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Preventive Maintenance Pictures</label>
                <SegmentWebcamFileUpload
                  id={MOLD_MAINTENANCE.ADDITIONAL_PICTURE_ONE}
                  toolID={formData?.moldMaintenanceData?.toolID}
                  moldInfoID={formData?.moldInfoID}
                  buttonLabel="Preventive Maintenance Pictures"
                  maintenanceId={formData?.id || 0}
                  maintenanceData={getFormDataForModals()}
                  onChange={handleFileUpload}
                  documents={convertDocumentsForWebCamFileUpload('AdditionalPicPath1')}
                  value={getDocumentPath(formData.additionalPicPath1)?.fileName}
                  filePath={getDocumentPath(formData.additionalPicPath1)?.fileUrl}
                  isDisabled={isDisabled}
                  onImageSegmentUploaded={handleImageSegmentUploaded}
                  onMaintenanceSaved={handleMaintenanceSaved}
                  showManageImages={hasImagesForField('AdditionalPicPath1')}
                  context="maintenance"
                />
              </div>
            </div>
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Breakdown Maintenance Pictures</label>
                <SegmentWebcamFileUpload
                  id={MOLD_MAINTENANCE.ADDITIONAL_PICTURE_TWO}
                  toolID={formData?.moldMaintenanceData?.toolID}
                  moldInfoID={formData?.moldInfoID}
                  buttonLabel="Breakdown Maintenance Pictures"
                  maintenanceId={formData?.id || 0}
                  maintenanceData={getFormDataForModals()}
                  onChange={handleFileUpload}
                  documents={convertDocumentsForWebCamFileUpload('AdditionalPicPath2')}
                  value={getDocumentPath(formData.additionalPicPath2)?.fileName}
                  filePath={getDocumentPath(formData.additionalPicPath2)?.fileUrl}
                  isDisabled={isDisabled}
                  onImageSegmentUploaded={handleImageSegmentUploaded}
                  onMaintenanceSaved={handleMaintenanceSaved}
                  showManageImages={hasImagesForField('AdditionalPicPath2')}
                />
              </div>
            </div>
          </div>
          <div className="row m-2">
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Repair Pictures</label>
                <SegmentWebcamFileUpload
                  id={MOLD_MAINTENANCE.ADDITIONAL_PICTURE_THREE}
                  toolID={formData?.moldMaintenanceData?.toolID}
                  moldInfoID={formData?.moldInfoID}
                  buttonLabel="Repair Pictures"
                  maintenanceId={formData?.id || 0}
                  maintenanceData={getFormDataForModals()}
                  onChange={handleFileUpload}
                  documents={convertDocumentsForWebCamFileUpload('AdditionalPicPath3')}
                  value={getDocumentPath(formData.additionalPicPath3)?.fileName}
                  filePath={getDocumentPath(formData.additionalPicPath3)?.fileUrl}
                  isDisabled={isDisabled}
                  onImageSegmentUploaded={handleImageSegmentUploaded}
                  onMaintenanceSaved={handleMaintenanceSaved}
                  showManageImages={hasImagesForField('AdditionalPicPath3')}
                  context="maintenance"
                />
              </div>
            </div>
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Mold Modification Pictures</label>
                <SegmentWebcamFileUpload
                  id={MOLD_MAINTENANCE.ADDITIONAL_PICTURE_THREE}
                  toolID={formData?.moldMaintenanceData?.toolID}
                  moldInfoID={formData?.moldInfoID}
                  buttonLabel="Mold Modification Pictures"
                  maintenanceId={formData?.id || 0}
                  maintenanceData={getFormDataForModals()}
                  onChange={handleFileUpload}
                  documents={convertDocumentsForWebCamFileUpload('AdditionalPicPath4')}
                  value={getDocumentPath(formData.additionalPicPath4)?.fileName}
                  filePath={getDocumentPath(formData.additionalPicPath4)?.fileUrl}
                  isDisabled={isDisabled}
                  onImageSegmentUploaded={handleImageSegmentUploaded}
                  onMaintenanceSaved={handleMaintenanceSaved}
                  showManageImages={hasImagesForField('AdditionalPicPath4')}
                  context="maintenance"
                />
              </div>
            </div>
          </div>
          <div className="row m-2">
            {/*<div className="col-lg-6 col-md-6 col-sm-12">
               <div className="input-blocks">
                <label>Additional Pictures</label>
                <SegmentWebcamFileUpload
                  id={MOLD_MAINTENANCE.ADDITIONAL_PICTURES}
                  onChange={handleFileUpload}
                  documents={convertDocumentsForWebCamFileUpload('AdditionalPicPath3')}
                  value={getDocumentPath(formData.additionalPicPath3)?.fileName}
                  filePath={getDocumentPath(formData.additionalPicPath3)?.fileUrl}
                  isDisabled={isDisabled}
                />
              </div> */}

            {/* <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <div className="d-flex align-items-center">
                  {formData.additionalLabelEditing ? (
                    <input
                      type="text"
                      className="form-control"
                      value={formData.additionalLabel || 'Additional Pictures'}
                      onChange={e => updateFormField('additionalLabel', e.target.value)}
                      onBlur={() => updateFormField('additionalLabelEditing', false)}
                      autoFocus
                    />
                  ) : (
                    <>
                      <label className="mb-0">
                        {formData.additionalLabel || 'Additional Pictures'}
                      </label>
                      {!isDisabled && (
                        <FaPencilAlt
                          size={14}
                          color="black" 
                          style={{ marginLeft: '6px', cursor: 'pointer' }} 
                          onClick={() => updateFormField('additionalLabelEditing', true)}
                        />
                      )}
                    </>
                  )}
                </div>
                <div className="mt-2">
                  <SegmentWebcamFileUpload
                    id={MOLD_MAINTENANCE.ADDITIONAL_PICTURE_ONE}
                    onChange={handleFileUpload}
                    documents={convertDocumentsForWebCamFileUpload('AdditionalPicPath')}
                    value={getDocumentPath(formData.additionalPicPath)?.fileName}
                    filePath={getDocumentPath(formData.additionalPicPath)?.fileUrl}
                    isDisabled={isDisabled}
                  />
                </div>
              </div>
            </div> */}

            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                {/* <div className="d-flex align-items-center">
                  {formData.additionalLabelEditing ? (
                    <input
                      type="text"
                      className="form-control"
                      value={formData.additionalLabel || 'Additional Pictures'}
                      onChange={e => updateFormField('additionalLabel', e.target.value)}
                      onBlur={() => updateFormField('additionalLabelEditing', false)}
                      autoFocus
                    />
                  ) : (
                    <>
                      <label className="mb-0">
                        {formData.additionalLabel || 'Additional Pictures'}
                      </label>
                      {!isDisabled && (
                        <FaPencilAlt
                          size={12}
                          color="black"
                          style={{ marginLeft: '4px', cursor: 'pointer' }}
                          onClick={() => updateFormField('additionalLabelEditing', true)}
                        />
                      )}
                    </>
                  )}
                </div> */}

                <EditableLabel
                  key={formData.additionalLabel || 'Additional Pictures'} // Force re-render when label changes
                  defaultText={formData.additionalLabel || 'Additional Pictures'}
                  onChange={val => updateFormField('additionalLabel', val)}
                  isDisabled={isDisabled}
                />

                <div className="mt-2">
                  <SegmentWebcamFileUpload
                    id={MOLD_MAINTENANCE.ADDITIONAL_PICTURES}
                    toolID={formData?.toolID}
                    moldInfoID={formData?.moldInfoID}
                    buttonLabel={formData.additionalLabel || 'Additional Pictures'}
                    maintenanceId={formData?.id || 0}
                    maintenanceData={getFormDataForModals()}
                    onChange={handleFileUpload}
                    documents={convertDocumentsForWebCamFileUpload('AdditionalPicPath')}
                    value={getDocumentPath(formData.additionalPicPath)?.fileName}
                    filePath={getDocumentPath(formData.additionalPicPath)?.fileUrl}
                    isDisabled={isDisabled}
                    onImageSegmentUploaded={handleImageSegmentUploaded}
                    onMaintenanceSaved={handleMaintenanceSaved}
                    showManageImages={hasImagesForField('AdditionalPicPath')}
                    context="maintenance"
                  />
                </div>
              </div>
            </div>
          </div>
          {/* Comments/Notes*/}
          <h5 className="header-five">Comments/Notes</h5>
          <div className="row m-2">
            <div className="col-lg-12 col-md-12 col-sm-12">
              <div className="input-blocks">
                <label>Additional Comments</label>
                <TextEditor
                  data={formData.additionalComments}
                  onChange={val => updateFormField('additionalComments', val)}
                  disabled={isDisabled}
                />
              </div>
            </div>
          </div>

          {/* Approval*/}
          <h5 className="header-five">Approval</h5>
          <div className="row m-2">
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Maintenance Performed By</label>
                <div className="d-flex">
                  <input
                    type="text"
                    value={formData.maintenance_PerformedBy}
                    readOnly={!isDisabled}
                    className={`form-control${!isDisabled ? ' bg-light' : ''}`}
                    placeholder="Name"
                    disabled={isDisabled}
                  />
                  {formData.maintenance_PerformedBy && formData.maintenance_PerformedBy.trim() !== '' && (
                    <input
                      type="datetime-local"
                      step={1}
                      value={formData.maintenance_PerformedBy_Date || ''}
                      onChange={e => updateFormField('maintenance_PerformedBy_Date', e.target.value)}
                      className="form-control ms-4"
                      disabled={isDisabled}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Quality Control Approved By</label>
                <div className="d-flex">
                  <input
                    type="text"
                    value={formData.quality_Control_ApprovedBy}
                    onChange={e => updateFormField('quality_Control_ApprovedBy', e.target.value)}
                    className="form-control"
                    placeholder="Name"
                    disabled={isDisabled}
                  />
                  {formData.quality_Control_ApprovedBy && formData.quality_Control_ApprovedBy.trim() !== '' && (
                    <input
                      type="datetime-local"
                      step={1}
                      value={formData.quality_Control_ApprovedBy_Date || ''}
                      onChange={e =>
                        updateFormField('quality_Control_ApprovedBy_Date', e.target.value)
                      }
                      className="form-control ms-4"
                      disabled={isDisabled}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row m-2">
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Engineering Approved By</label>
                <div className="d-flex">
                  <input
                    type="text"
                    value={formData.engineering_ApprovedBy}
                    onChange={e => updateFormField('engineering_ApprovedBy', e.target.value)}
                    className="form-control"
                    placeholder="Name"
                    disabled={isDisabled}
                  />
                  {formData.engineering_ApprovedBy && formData.engineering_ApprovedBy.trim() !== '' && (
                    <input
                      type="datetime-local"
                      step={1}
                      value={formData.engineering_ApprovedBy_Date || ''}
                      onChange={e => updateFormField('engineering_ApprovedBy_Date', e.target.value)}
                      className="form-control ms-4"
                      disabled={isDisabled}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Submitted By</label>
                <div className="d-flex">
                <input
                  type="text"
                  value={formData.submittedBy}
                  readOnly={!isDisabled}
                  className={`form-control${!isDisabled ? ' bg-light' : ''}`}
                  placeholder="Name"
                  disabled={isDisabled}
                />
                  {formData.submittedBy && formData.submittedBy.trim() !== '' && (
                    <input
                      type="datetime-local"
                      step={1}
                      value={formData.submittedByDate || ''}
                      onChange={e => updateFormField('submittedByDate', e.target.value)}
                      className="form-control ms-4"
                      disabled={isDisabled}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Close Maintenance Record Button - Only show when viewing saved record */}
        {isViewingSavedRecord && (
          <div className="row mt-3">
            <div className="col-12 text-center">
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleCloseMaintenanceRecord}
                disabled={isPageDisabled}
              >
                Close Maintenance Record
              </button>
            </div>
          </div>
        )}
    </form>

    {/* Maintenance Checklist Modal */}
    {showMaintenanceModal && (
      <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {isViewingSavedRecord ? 'Edit' : 'Create'} {selectedMaintenanceType} Maintenance Checklist
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCancelChecklist}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              <div className="row">
                <div className="col-12">
                  {maintenanceChecklists[selectedMaintenanceType]?.map((item, index) => {
                    const itemKey = `${selectedMaintenanceType}_${index}`;
                    return (
                      <div key={itemKey} className="mb-3 p-3 border rounded">
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1 me-3">
                            <p className="mb-2 fw-semibold">
                              {index + 1}. {item}
                            </p>
                          </div>
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className={`btn btn-sm ${checklistResponses[itemKey] === 'Yes' ? 'btn-success' : 'btn-outline-success'}`}
                              onClick={() => handleChecklistResponse(itemKey, 'Yes')}
                              style={{ minWidth: '60px' }}
                            >
                              Yes
                            </button>
                            <button
                              type="button"
                              className={`btn btn-sm ${checklistResponses[itemKey] === 'No' ? 'btn-danger' : 'btn-outline-danger'}`}
                              onClick={() => handleChecklistResponse(itemKey, 'No')}
                              style={{ minWidth: '60px' }}
                            >
                              No
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancelChecklist}
              >
                {isViewingSavedRecord ? 'Close' : 'Cancel'}
              </button>
              {isViewingSavedRecord ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveChecklist}
                >
                  Update
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveChecklist}
                >
                  Save
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Issues Found Modal */}
    {showIssuesModal && (
      <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {savedIssues ? 'Edit Other Maintenance Records' : 'Add Other Maintenance Records'}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCancelIssues}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {/* Show notification if maintenance ID is zero */}
              {(formData?.id === 0 || !formData?.id) && (
                <div className="alert alert-warning d-flex align-items-center mb-3" role="alert">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <div>
                    <strong>Please save the maintenance before entering the Maintenance Records.</strong>
                    <br />
                    <small>Save the current maintenance record first to enable all features.</small>
                  </div>
                </div>
              )}
              <div className="row">
                <div className="col-12">
                  {(() => {
                    const isMaintenanceSaved = formData?.id && formData.id > 0;
                    return issuesList.map((issue, index) => (
                    <div key={issue.id} className="mb-4 p-3 border rounded">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">Issue #{index + 1}</h6>
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-primary btn-sm fw-bold"
                            onClick={() => handleActionsClick(issue.id)}
                            disabled={!isMaintenanceSaved || !issue.title || issue.title.trim() === ''}
                            style={{ 
                              boxShadow: (isMaintenanceSaved && issue.title && issue.title.trim() !== '') ? '0 2px 4px rgba(0,123,255,0.3)' : 'none',
                              border: (isMaintenanceSaved && issue.title && issue.title.trim() !== '') ? '2px solid #007bff' : '2px solid #ccc',
                              opacity: (isMaintenanceSaved && issue.title && issue.title.trim() !== '') ? 1 : 0.6
                            }}
                          >
                            Actions
                          </button>
                          <SegmentWebcamFileUpload
                            key={`future_actions_${issue.id}_${issue.activityIssueId || 'new'}`}
                            id={`future_actions_${issue.id}`}
                            toolID={formData?.moldMaintenanceData?.toolID}
                            moldInfoID={formData?.moldInfoID}
                            buttonLabel="Capture Image"
                            maintenanceId={formData?.id || 0}
                            maintenanceData={(() => {
                              console.log(`Form: Calling getFormDataForModals() for Mold Picture After Maintenance`);
                              const result = getFormDataForModals();
                              console.log(`Form: getFormDataForModals() returned for Mold Picture After Maintenance:`, result);
                              return result;
                            })()}
                            onChange={(images) => {
                              console.log('Actions view - onChange called with images:', images);
                              handleIssueActionImages(issue.id, images);
                            }}
                            documents={(() => {
                              console.log('Actions view - passing documents for issue', issue.id, ':', issue.actionImages || []);
                              return issue.actionImages || [];
                            })()}
                            value={issue.actionImages?.[0]?.fileName}
                            filePath={issue.actionImages?.[0]?.filePath}
                            isDisabled={
                              isDisabled ||
                              !isMaintenanceSaved ||
                              !issue.title ||
                              issue.title.trim() === ''
                            }
                            onImageSegmentUploaded={handleImageSegmentUploaded}
                            onMaintenanceSaved={handleMaintenanceSaved}
                            onActivityIssueSaved={(activityIssueId) => handleActivityIssueSaved(activityIssueId, issue.id)}
                            showManageImages={issue.actionImages && issue.actionImages.length > 0}
                            context="other-maintenance-issue"
                            issueData={(() => {
                              // Ensure we pass the latest activityIssueId from both the issue object and issueActivityIds state
                              const latestActivityIssueId = issue.activityIssueId || issueActivityIds[issue.id] || 0;
                              console.log('Form: Rendering SegmentWebcamFileUpload for issue:', issue.id, 'with activityIssueId:', latestActivityIssueId);
                              console.log('Form: issue.activityIssueId:', issue.activityIssueId);
                              console.log('Form: issueActivityIds[issue.id]:', issueActivityIds[issue.id]);
                              console.log('Form: Full issue object:', JSON.stringify(issue, null, 2));
                              return {
                                ...issue,
                                activityIssueId: latestActivityIssueId  // Explicitly set the latest activityIssueId
                              };
                            })()}
                            categories={categories}
                          />
                        </div>
                      </div>
                      
                      <div className="row">
                        <div className="col-12 mb-3">
                          <label className="form-label fw-semibold">Category</label>
                          <select
                            className="form-control"
                            value={issue.title}
                            onChange={(e) => handleIssueChange(issue.id, 'title', e.target.value)}
                            disabled={categoriesLoading}
                          >
                            <option value="">
                              {categoriesLoading ? 'Loading categories...' : 'Select Category'}
                            </option>
                            {categories.map(category => (
                              <option key={category.categoryId} value={category.category}>
                                {category.category}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="col-12">
                          <label className="form-label fw-semibold">Comments</label>
                          <textarea
                            className="form-control"
                            rows="3"
                            placeholder="Enter detailed comments about the issue"
                            value={issue.comments}
                            onChange={(e) => handleIssueChange(issue.id, 'comments', e.target.value)}
                          />
                        </div>
                        
                        <div className="col-12 mt-2">
                          <label className="form-label fw-semibold">Future Actions</label>
                          <div className="d-flex gap-2 mb-3">
                            <input
                              type="text"
                            className="form-control"
                              placeholder="Enter future action"
                              value={issue.newFutureAction || ''}
                              onChange={(e) => handleIssueChange(issue.id, 'newFutureAction', e.target.value)}
                              disabled={!isMaintenanceSaved}
                              style={{ opacity: isMaintenanceSaved ? 1 : 0.6 }}
                            />
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => handleAddFutureAction(issue.id)}
                              disabled={!isMaintenanceSaved}
                              style={{ opacity: isMaintenanceSaved ? 1 : 0.6 }}
                            >
                              Add
                            </button>
                      </div>
                      
                          {/* Future Actions Table */}
                          <div className="table-responsive">
                            <table className="table table-hover mb-0" style={{ borderCollapse: 'separate', borderSpacing: '0' }}>
                              <thead>
                                <tr style={{ backgroundColor: '#f8f9fa' }}>
                                  <th className="fw-bold text-dark py-3 px-4" style={{ 
                                    borderBottom: '2px solid #dee2e6',
                                    fontSize: '13px',
                                    letterSpacing: '0.5px'
                                  }}>
                                    <i className="fas fa-tasks me-2 text-primary"></i>
                                    Future Actions
                                  </th>
                                  <th className="fw-bold text-dark py-3 px-4" style={{ 
                                    borderBottom: '2px solid #dee2e6',
                                    fontSize: '13px',
                                    letterSpacing: '0.5px'
                                  }}>
                                    <i className="fas fa-flag me-2 text-primary"></i>
                                    Status
                                  </th>
                                  <th className="fw-bold text-dark py-3 px-4" style={{ 
                                    borderBottom: '2px solid #dee2e6',
                                    fontSize: '13px',
                                    letterSpacing: '0.5px'
                                  }}>
                                    <i className="fas fa-calendar me-2 text-primary"></i>
                                    Noted Date
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {loadingFutureActions ? (
                                  <tr>
                                    <td colSpan="3" className="text-center py-4" style={{ backgroundColor: '#f8f9fa' }}>
                                      <div className="d-flex align-items-center justify-content-center">
                                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                                          <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <span className="text-muted">Loading future actions...</span>
                                      </div>
                                    </td>
                                  </tr>
                                ) : issue.futureActionsList && issue.futureActionsList.length > 0 ? (
                                  issue.futureActionsList.map((action, actionIndex) => {
                                    console.log('Rendering future action for issue', issue.id, ':', action, 'at index:', actionIndex);
                                    return (
                                      <tr 
                                        key={`issue-${issue.id}-action-${action.futureActionId || actionIndex}`}
                                        style={{ 
                                          borderBottom: '1px solid #e9ecef',
                                          transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = 'transparent';
                                        }}
                                      >
                                        <td className="py-3 px-4" style={{ borderRight: '1px solid #e9ecef' }}>
                                          <div className="d-flex align-items-center">
                                            <div className="me-3">
                                              <i className="fas fa-circle text-primary" style={{ fontSize: '6px' }}></i>
                                            </div>
                                            <span className="fw-medium text-dark" style={{ fontSize: '13px' }}>
                                              {action.futureAction}
                                            </span>
                                          </div>
                                        </td>
                                        <td className="py-3 px-4" style={{ borderRight: '1px solid #e9ecef' }}>
                                          <select
                                            className="form-select form-select-sm"
                                            value={action.status || 'Open'}
                                            onChange={(e) => {
                                              console.log('Status change triggered for API action:', action, 'new status:', e.target.value);
                                              handleFutureActionStatusChange(issue.id, actionIndex, e.target.value);
                                            }}
                                            style={{ 
                                              minWidth: '120px',
                                              border: '2px solid transparent',
                                              borderRadius: '6px',
                                              fontSize: '12px',
                                              fontWeight: '500',
                                              backgroundColor: action.status === 'Open' ? '#fff3cd' : 
                                                             action.status === 'Fixed' ? '#d1edff' : '#f8d7da',
                                              transition: 'all 0.3s ease',
                                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                            }}
                                            onFocus={(e) => {
                                              e.target.style.borderColor = '#007bff';
                                              e.target.style.boxShadow = '0 0 0 0.2rem rgba(0,123,255,0.25)';
                                            }}
                                            onBlur={(e) => {
                                              e.target.style.borderColor = 'transparent';
                                              e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                            }}
                                          >
                                            <option value="Open">🟡 Open</option>
                                            <option value="Fixed">🟢 Fixed</option>
                                            <option value="On Hold">🔴 On Hold</option>
                                          </select>
                                        </td>
                                        <td className="py-3 px-4">
                                          <div className="d-flex align-items-center">
                                            <i className="fas fa-calendar-alt me-2 text-muted" style={{ fontSize: '10px' }}></i>
                                            <span className="text-muted fw-medium" style={{ fontSize: '12px' }}>
                                              {action.notedDate ? new Date(action.notedDate).toISOString().split('T')[0] : 'N/A'}
                                            </span>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })
                                ) : issue.futureActionsList && issue.futureActionsList.length > 0 ? (
                                  issue.futureActionsList.map((action, actionIndex) => {
                                    console.log('Rendering local future action:', action, 'at index:', actionIndex);
                                    return (
                                      <tr 
                                        key={`local-${actionIndex}`}
                                        style={{ 
                                          borderBottom: '1px solid #e9ecef',
                                          transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = 'transparent';
                                        }}
                                      >
                                        <td className="py-3 px-4" style={{ borderRight: '1px solid #e9ecef' }}>
                                          <div className="d-flex align-items-center">
                                            <div className="me-3">
                                              <i className="fas fa-circle text-primary" style={{ fontSize: '6px' }}></i>
                                            </div>
                                            <span className="fw-medium text-dark" style={{ fontSize: '13px' }}>
                                              {action.action || action.futureAction}
                                            </span>
                                          </div>
                                        </td>
                                        <td className="py-3 px-4" style={{ borderRight: '1px solid #e9ecef' }}>
                                          <select
                                            className="form-select form-select-sm"
                                            value={action.status || 'Open'}
                                            onChange={(e) => {
                                              console.log('Status change triggered for local action:', action, 'new status:', e.target.value);
                                              handleFutureActionStatusChange(issue.id, actionIndex, e.target.value);
                                            }}
                                            style={{ 
                                              minWidth: '120px',
                                              border: '2px solid transparent',
                                              borderRadius: '6px',
                                              fontSize: '12px',
                                              fontWeight: '500',
                                              backgroundColor: action.status === 'Open' ? '#fff3cd' : 
                                                             action.status === 'Fixed' ? '#d1edff' : '#f8d7da',
                                              transition: 'all 0.3s ease',
                                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                            }}
                                            onFocus={(e) => {
                                              e.target.style.borderColor = '#007bff';
                                              e.target.style.boxShadow = '0 0 0 0.2rem rgba(0,123,255,0.25)';
                                            }}
                                            onBlur={(e) => {
                                              e.target.style.borderColor = 'transparent';
                                              e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                            }}
                                          >
                                            <option value="Open">🟡 Open</option>
                                            <option value="Fixed">🟢 Fixed</option>
                                            <option value="On Hold">🔴 On Hold</option>
                                          </select>
                                        </td>
                                        <td className="py-3 px-4">
                                          <div className="d-flex align-items-center">
                                            <i className="fas fa-calendar-alt me-2 text-muted" style={{ fontSize: '10px' }}></i>
                                            <span className="text-muted fw-medium" style={{ fontSize: '12px' }}>
                                              {action.notedDate || 'N/A'}
                                            </span>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })
                                ) : (
                                  <tr>
                                    <td colSpan="3" className="text-center py-4" style={{ backgroundColor: '#f8f9fa' }}>
                                      <div className="d-flex flex-column align-items-center">
                                        <i className="fas fa-inbox text-muted mb-2" style={{ fontSize: '2rem' }}></i>
                                        <h6 className="text-muted mb-1">No Future Actions</h6>
                                        <p className="text-muted mb-0" style={{ fontSize: '12px' }}>
                                          No future actions have been recorded yet.
                                        </p>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                      
                      {/* Actions display with accordion */}
                      {issue.subIssues && issue.subIssues.length > 0 && (
                        <div className="mt-3">
                          <div 
                            className="d-flex justify-content-between align-items-center mb-2 p-2 border rounded bg-light"
                            onClick={() => toggleSubIssuesAccordion(issue.id)}
                            style={{ 
                              cursor: 'pointer',
                              transition: 'background-color 0.2s ease',
                              userSelect: 'none'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#e9ecef'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                          >
                            <h6 className="text-muted mb-0 fw-semibold">
                              Actions ({issue.subIssues.length})
                            </h6>
                            <span 
                              className="badge bg-primary"
                              style={{ 
                                fontSize: '0.75rem',
                                transition: 'transform 0.2s ease'
                              }}
                            >
                              {expandedSubIssues[issue.id] ? '▼' : '▶'}
                            </span>
                          </div>
                          
                          {expandedSubIssues[issue.id] && (
                            <div 
                              className="sub-issues-container"
                              style={{
                                maxHeight: '400px',
                                overflowY: 'auto',
                                border: '1px solid #dee2e6',
                                borderTop: 'none',
                                borderTopLeftRadius: '0',
                                borderTopRightRadius: '0',
                                borderBottomLeftRadius: '0.375rem',
                                borderBottomRightRadius: '0.375rem',
                                backgroundColor: '#f8f9fa'
                              }}
                            >
                              {issue.subIssues.map((subIssue, subIndex) => {
                            const isEditing = editingSubIssue && 
                                            editingSubIssue.mainIssueId === issue.id && 
                                            editingSubIssue.subIssueId === subIssue.id;
                            
                            return (
                              <div key={subIssue.uniqueId || subIssue.id} className="mb-3 p-3 bg-light border rounded">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <div>
                                    <h6 className="mb-1 text-primary">Action #{subIndex + 1}</h6>
                                    {isEditing ? (
                                      <input
                                        type="text"
                                        className="form-control form-control-sm mb-2"
                                        placeholder="Enter action title"
                                        value={editingSubIssueData.title}
                                        onChange={(e) => handleSubIssueEditChange('title', e.target.value)}
                                      />
                                    ) : (
                                      <h6 className="mb-0 fw-bold">{subIssue.title || 'Untitled'}</h6>
                                    )}
                                  </div>
                                  <div className="d-flex gap-1">
                                    {isEditing ? (
                                      <>
                                        <button
                                          type="button"
                                          className="btn btn-success btn-sm"
                                          onClick={handleSaveSubIssueEdit}
                                        >
                                          Save
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-secondary btn-sm"
                                          onClick={handleCancelSubIssueEdit}
                                        >
                                          Cancel
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          type="button"
                                          className="btn btn-outline-primary btn-sm"
                                          onClick={() => handleEditSubIssue(issue.id, subIssue.id)}
                                        >
                                          Edit
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                                {isEditing ? (
                                  <div className="mt-2">
                                    <label className="form-label fw-semibold">Comments:</label>
                                    <textarea
                                      className="form-control form-control-sm"
                                      rows="3"
                                      placeholder="Enter sub-issue comments"
                                      value={editingSubIssueData.comments}
                                      onChange={(e) => handleSubIssueEditChange('comments', e.target.value)}
                                    />
                                  </div>
                                ) : (
                                  subIssue.comments && (
                                    <div className="mt-2">
                                      <small className="text-muted fw-semibold">Comments:</small>
                                      <p className="mb-0 mt-1 text-dark">{subIssue.comments}</p>
                                    </div>
                                  )
                                )}
                                
                                {/* Action Images section for sub-issues */}
                                {hasImageSegmentsForRecord("Action Images") && (
                                  <div className="mt-3">
                                    <label className="form-label fw-semibold">Action Images</label>
                                    {(() => {
                                    })()}
                                    <SegmentWebcamFileUpload
                                      id={`subissue_action_images_${subIssue.uniqueId || subIssue.id}`}
                                      toolID={formData?.moldMaintenanceData?.toolID}
                                      moldInfoID={formData?.moldInfoID}
                                      buttonLabel="Action Images"
                                      maintenanceId={formData?.id || 0}
                                      maintenanceData={getFormDataForModals()}
                                      onChange={handleFileUpload}
                                      documents={(() => {
                                        // For Actions section, we need to filter SubActionImages by subActionImageId
                                        const subActionImages = processedImageSegments.SubActionImages || [];
                                        const matchingImages = subActionImages.filter(img => 
                                          img.subActionImageId === (subIssue.subActionImageId || subIssue.subActivityId)
                                        );
                                        console.log('Filtering SubActionImages for sub-issue:', {
                                          subIssue,
                                          subActionImages,
                                          matchingImages,
                                          subActionImageId: subIssue.subActionImageId,
                                          subActivityId: subIssue.subActivityId
                                        });
                                        return matchingImages;
                                      })()}
                                      value=""
                                      filePath=""
                                      isDisabled={isDisabled}
                                      onImageSegmentUploaded={handleImageSegmentUploaded}
                                      onMaintenanceSaved={handleMaintenanceSaved}
                                      showManageImages={hasImageSegmentsForRecord("Action Images")}
                                      context="maintenance"
                                      savedMaintenanceActivityData={(() => {
                                        console.log('=== RENDERING ACTIONS SECTION ===');
                                        console.log('subIssue:', subIssue);
                                        console.log('subIssue.uniqueId:', subIssue.uniqueId);
                                        console.log('subIssue.id:', subIssue.id);
                                        console.log('Current issuesList:', issuesList);
                                        console.log('Current subActionActivityIds:', subActionActivityIds);
                                        const result = getSubActionActivityIds(subIssue.uniqueId || subIssue.id);
                                        console.log('getSubActionActivityIds result:', result);
                                        return result;
                                      })()}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ));
                  })()}
                  
                  <div className="text-center mt-3">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={handleAddIssue}
                    >
                      + Add Issue
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancelIssues}
              >
                {savedIssues ? 'Close' : 'Cancel'}
              </button>
              {savedIssues ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveIssues}
                >
                  Update
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveIssues}
                >
                  Save
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    )}


    {/* Add Issues Modal */}
    {showAddIssuesModal && (
      <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Add Actions</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleCancelNewIssue}
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label fw-semibold">Actions</label>
                <select
                  className="form-control"
                  value={newIssue.title}
                  onChange={(e) => handleNewIssueChange('title', e.target.value)}
                >
                  <option value="">Select Actions</option>
                  <option value="Repair">Repair</option>
                  <option value="Replace">Replace</option>
                  <option value="No Action Taken">No Action Taken</option>
                </select>
              </div>
              
              <div className="mb-3">
                <label className="form-label fw-semibold">Comments</label>
                <textarea
                  className="form-control"
                  rows="4"
                  placeholder="Enter detailed comments about the issue"
                  value={newIssue.comments}
                  onChange={(e) => handleNewIssueChange('comments', e.target.value)}
                />
              </div>
              
              <div className="mb-3">
                <label className="form-label fw-semibold">Action Images</label>
                <SegmentWebcamFileUpload
                  id={`new_action_${Date.now()}`}
                  toolID={formData?.moldMaintenanceData?.toolID}
                  moldInfoID={formData?.moldInfoID}
                  buttonLabel="Action Images"
                  maintenanceId={formData?.id || 0}
                  maintenanceData={getFormDataForModals()}
                  onChange={(images) => {
                    setNewIssue(prev => {
                      const updated = { ...prev, actionImages: images };
                      return updated;
                    });
                  }}
                  documents={newIssue.actionImages || []}
                  value={newIssue.actionImages?.[0]?.fileName}
                  filePath={newIssue.actionImages?.[0]?.filePath}
                  isDisabled={isDisabled || !newIssue.title || newIssue.title.trim() === ''}
                  onImageSegmentUploaded={handleImageSegmentUploaded}
                  onMaintenanceSaved={handleMaintenanceSaved}
                  showManageImages={newIssue.actionImages && newIssue.actionImages.length > 0}
                  context="maintenance"
                  onCaptureImageClick={handleCaptureImageClick}
                  shouldOpenCaptureModal={shouldOpenCaptureModal}
                  onCaptureModalOpened={() => setShouldOpenCaptureModal(false)}
                  savedMaintenanceActivityData={getLatestActivityIds()}
                />
              </div>
              
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={handleSaveNewIssue}
              >
                Add Actions
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancelNewIssue}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveNewIssue}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Bootstrap Confirmation Dialog */}
    <Modal show={showConfirmDialog} onHide={handleConfirmNo} centered size="sm">
      <Modal.Header closeButton>
        <Modal.Title>Confirm Save</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>Do you want to save this maintenance?</p>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-end">
        <Button variant="primary" onClick={handleConfirmYes}>
          Yes
        </Button>
        <Button variant="secondary" onClick={handleConfirmNo}>
          No
        </Button>
      </Modal.Footer>
    </Modal>

    {/* Save Images Confirmation Dialog */}
    <Modal show={showSaveImagesConfirm} onHide={handleSaveImagesConfirmNo} centered size="sm">
      <Modal.Header closeButton>
        <Modal.Title>Save Images</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        To upload the images, you need to save the current issue first. Do you want to continue?
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-end">
        <Button variant="primary" onClick={handleSaveImagesConfirmYes}>
          Yes
        </Button>
        <Button variant="secondary" onClick={handleSaveImagesConfirmNo}>
          No
        </Button>
      </Modal.Footer>
    </Modal>

    {/* Future Action Confirmation Dialog */}
    <Modal show={showFutureActionConfirm} onHide={handleFutureActionConfirmNo} centered size="sm">
      <Modal.Header closeButton>
        <Modal.Title>Confirm Save</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>Do you want to save this Issue?</p>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-end">
        <Button variant="primary" onClick={handleFutureActionConfirmYes}>
          Yes
        </Button>
        <Button variant="secondary" onClick={handleFutureActionConfirmNo}>
          No
        </Button>
      </Modal.Footer>
    </Modal>

    {/* Parts View Modal */}
    <Modal show={showPartsModal} onHide={() => setShowPartsModal(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>Selected Parts</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {(formData.parts || []).length === 0 ? (
          <p className="mb-0 text-muted">No parts selected.</p>
        ) : (
          <ul className="list-group list-group-flush">
            {(formData.parts || []).map((part, index) => (
              <li
                key={`${part.value ?? part.partId ?? part.partNumber ?? index}`}
                className="list-group-item"
              >
                <span className="fw-medium">{part.partNumber || part.label || 'N/A'}</span>
              </li>
            ))}
          </ul>
        )}
      </Modal.Body>
    </Modal>

    {/* Document Viewer Modal */}
    <DocumentViewerModal
      show={showDocumentViewer}
      onHide={() => setShowDocumentViewer(false)}
      documentUrl={documentViewerUrl}
      documentName={documentViewerName}
    />

    </>
  );
};

export default MoldMaintenanceAddEditForm;
